// API client for connecting to the backend server

import {
  getSuperadminMode,
  SUPERADMIN_HEADER,
  SUPERADMIN_HEADER_ON,
} from '@/auth/superadminMode'
import { requireActiveWorkspaceId } from './workspace'

// Default to a same-origin relative path so any deployment (laptop
// dev via Vite proxy, dev environment via Caddy reverse-proxy)
// works with zero env config. VITE_API_URL is an override for
// unusual setups (e.g. running the SPA against a remote backend).
export const API_BASE = import.meta.env.VITE_API_URL || '/api'

/** Wrapper around fetch that always sends the Better Auth session
 * cookie. Backend is on a different origin in dev (`:3002` vs the
 * frontend's `:8090`), so cookies don't ride along on a bare `fetch`
 * unless we set `credentials: 'include'`. Pre-Phase-5a this didn't
 * matter (no routes were session-gated); post-cutover every API
 * endpoint requires the cookie. CORS on the server already allows
 * credentials from the frontend origin (see index.ts cors() config).
 *
 * Use this for every internal API call. Cross-origin third-party
 * calls (none today) should call `fetch` directly to avoid leaking
 * cookies.
 *
 * `RequestInit` is narrowed to `Omit<…, 'credentials'>` so callers
 * can't silently downgrade to `same-origin`/`omit` and produce the
 * 401-redirect-loop that bit Phase 5b's first wiring. The override
 * also goes AFTER the spread for runtime correctness — even if the
 * type is bypassed via `as unknown`, the included credentials win.
 *
 * Superadmin mode (#429 Wave 2): when the in-tab toggle is ON,
 * attach `x-superadmin-mode: on`. The backend's `requireSuperadmin`
 * gate (admin-tier routes only) fires when both this header AND the
 * persisted `isSuperadmin` flag are present. Caller-supplied
 * headers win — if a route explicitly sets `x-superadmin-mode`
 * (e.g. `off` to opt out for one request), we don't double-stamp.
 * Headers carried by a `Request` instance passed as `input` are
 * preserved too (otherwise the caller's opt-out on the Request
 * would be silently dropped). */
export function apiFetch(
  input: RequestInfo,
  init: Omit<RequestInit, 'credentials'> = {},
): Promise<Response> {
  // Start from headers on the Request instance (if any), then layer
  // init.headers on top — same precedence the underlying fetch()
  // uses. Without this, a caller who built a Request with custom
  // headers would lose them through this wrapper.
  const headers = new Headers(input instanceof Request ? input.headers : undefined)
  if (init.headers) {
    new Headers(init.headers).forEach((value, key) => headers.set(key, value))
  }
  if (getSuperadminMode() && !headers.has(SUPERADMIN_HEADER)) {
    headers.set(SUPERADMIN_HEADER, SUPERADMIN_HEADER_ON)
  }
  return fetch(input, { ...init, headers, credentials: 'include' })
}
// ── CI/CD-protected DELETE: client helpers (#644) ──────────────────────
//
// When an entity has `ciProtected: true`, the backend requires both a
// `?force=true` query string AND an `X-OttoTester-Confirm-Delete: <name>`
// header on DELETE. The UI collects the typed-name confirmation and
// passes it through these helpers. See ADR 0015 § referential integrity.

export interface CiProtectedDeleteOpts {
  /** Pass `true` to send `?force=true`. Must be set together with
   * `confirmName` to satisfy the backend's dual-gate. */
  force?: boolean
  /** The entity's `name` field, as typed by the user in the confirm
   * dialog. Sent in `X-OttoTester-Confirm-Delete`. */
  confirmName?: string
}

function ciProtectedQuery(opts?: CiProtectedDeleteOpts): string {
  return opts?.force ? '?force=true' : ''
}

function ciProtectedHeaders(opts?: CiProtectedDeleteOpts): Record<string, string> {
  return opts?.confirmName ? { 'X-OttoTester-Confirm-Delete': opts.confirmName } : {}
}

/** Surfaces the backend's 409 `ci_protected` response so the UI can
 * distinguish "you need to type the name to confirm" from generic
 * errors. The dialog catches this, switches to name-typed mode, and
 * retries the DELETE with the headers populated. */
export class CiProtectedError extends Error {
  constructor(readonly entityName: string) {
    super(
      `This entity is marked as a CI/CD entry point. Confirm by typing "${entityName}".`,
    )
    this.name = 'CiProtectedError'
  }

  static async fromResponse(res: Response): Promise<CiProtectedError> {
    const body = (await res.json().catch(() => ({}))) as { entityName?: string }
    return new CiProtectedError(typeof body.entityName === 'string' ? body.entityName : '')
  }
}

/** A permanent delete of a suggestion that tests were built from was
 * refused (409, `code: 'candidate-in-use'`). The delete dialog catches
 * this and offers Archive instead, which keeps the tests linked. */
export class CandidateInUseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CandidateInUseError'
  }
}

/** Surfaces the billing gate's 402 (#1253 / #1435) so a blocked run can
 * show the gate's plain message and deep-link the billing page. The gate
 * (`assertBillingWritable`) serializes `{ error, code, upgradeHint,
 * billingUrl }` — `billingUrl` spread from the PlanError `detail`. `code`
 * is `billing_locked` (no active plan → paused) or `billing_read_only`
 * (past-due past the grace window). A plan-capacity 402 is NOT this — it
 * carries a different `code`, so callers fall through to a generic error. */
export class BillingLockedError extends Error {
  constructor(
    message: string,
    readonly code: 'billing_locked' | 'billing_read_only',
    readonly billingUrl: string,
  ) {
    super(message)
    this.name = 'BillingLockedError'
  }
}

/** Throw a `BillingLockedError` when a response body is a billing-lock 402.
 * Call it after reading the error body (so the body is read once) and
 * before a generic throw. No-op for any other status/code. */
function throwIfBillingLocked(
  status: number,
  body: { error?: string; code?: string; billingUrl?: string },
): void {
  if (status !== 402) return
  if (body.code !== 'billing_locked' && body.code !== 'billing_read_only') return
  throw new BillingLockedError(
    body.error || 'Your workspace is paused for billing.',
    body.code,
    typeof body.billingUrl === 'string' ? body.billingUrl : '/account/billing',
  )
}

// Base URL for the server (without /api suffix) — used for artifact
// URLs. When API_BASE is relative ('/api'), the server is the page's
// origin; otherwise strip the trailing /api off the absolute URL.
const SERVER_BASE = API_BASE.startsWith('/')
  ? window.location.origin
  : API_BASE.replace(/\/api\/?$/, '')

// Default to same-origin WebSocket with the scheme matching the page
// (wss on HTTPS, ws on HTTP). Hardcoding ws:// breaks on HTTPS deploys
// because the browser blocks it as mixed content.
const WS_BASE =
  import.meta.env.VITE_WS_URL ||
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`

/** Workspace-scoped URL prefix. Reads the active workspaceId set by
 * the AppContextProvider during boot (after `/api/workspaces/me`
 * resolves). Throws if called before boot completes — surfaces a clear
 * error rather than letting the literal string "null" reach the
 * server. Every workspace-scoped API helper uses this; flat helpers
 * (system fixtures, workspaces CRUD, run artifacts, run-control)
 * keep using `${API_BASE}` directly. */
function ws(): string {
  return `${API_BASE}/workspaces/${requireActiveWorkspaceId()}`
}

/** Public workspace-scoped URL builder for callers OUTSIDE the apiFetch
 * client — e.g. file-download buttons that use a raw `fetch` (to stream a
 * blob) instead of `apiFetch`. The applications/testCases/etc. routers mount
 * under `/api/workspaces/:workspaceId/...`, so a bare `/api/applications/...`
 * matches no route and 404s (#2055 follow-up). Mirrors `ws()`; throws the
 * same clear pre-boot error if the active workspace isn't set yet. */
export function workspaceApiUrl(subpath: string): string {
  const base = ws()
  return subpath.startsWith('/') ? `${base}${subpath}` : `${base}/${subpath}`
}

// Convert a server-relative artifact URL (e.g. /api/runs/.../artifacts/...) to a full URL
export function artifactUrl(path: string): string {
  if (!path) return ''
  if (path.startsWith('http')) return path
  return `${SERVER_BASE}${path}`
}

// Get URL for HTML report. Hard-coded to a relative `/api/...` path
// instead of `${API_BASE}` so the URL is always same-origin: the only
// caller (RunReport.tsx) iframes this and needs `contentDocument`
// access for theme sync, CSS injection, and click interception, which
// only works same-origin. Production (no VITE_API_URL) and Vite dev
// (via the `server.proxy` block in `vite.config.ts`) are already
// same-origin; ignoring the VITE_API_URL override here guards against
// a cross-origin value silently breaking the wrap.
export function reportUrl(runId: string): string {
  return `/api/runs/${runId}/report/index.html`
}

export interface TestRunRequest {
  id?: string
  applicationId: string
  targetUrl: string
  variant: {
    id: string
    name: string
    baseUrl: string
    status: string
    pathPrefix?: string
    browser?: {
      timezone?: string
      locale?: string
      viewportWidth?: number
      viewportHeight?: number
    }
  }
  objectName: string
  fields: Array<{
    id: string
    name: string
    type: string
    required: boolean
    options?: string[]
  }>
  relativePath: string
  selectorHints?: {
    listSelector?: string
    createButton?: string
    formContainer?: string
    searchInput?: string
  }
  aiInstructions?: string
  additionalInstructions?: string
  maxTestCases?: number
  testCaseIds?: string[]
  phases?: ('plan' | 'generate' | 'execute' | 'heal')[]
  captureTrace?: 'off' | 'on-failure' | 'always'
  /** Playwright screenshot capture (ottotester#814). Mirrors captureTrace. */
  captureScreenshot?: 'off' | 'on-failure' | 'always'
  resumeMode?: boolean
  resumePhases?: ('plan' | 'generate' | 'execute' | 'heal')[]
  logLevel?: 'info' | 'debug' | 'trace'
  testDataMode?: 'off' | 'single-row' | 'all-rows'
  healOnFailure?: boolean
  createIssueOnFailure?: boolean
  /** Feature Status (#687) — biases healer aggressiveness. Missing => 'established'. */
  stabilityExpectation?: 'established' | 'volatile'
  /** Per-assertion polling timeout in ms (#1076). Mirrors the
   * backend `TestRunRequest.assertionTimeout`. Sourced from the
   * active template's `assertionTimeout`; the orchestrator threads
   * it into the generated `playwright.config.ts` as the
   * `expect: { timeout: <ms> }` block. Missing => generator omits
   * the block and Playwright's built-in 5000ms default applies. */
  assertionTimeout?: number
}

export interface BrowserConfig {
  type: 'chromium' | 'firefox' | 'webkit'
  headless: boolean
  /** Optional override; backend defaults to 1920 when omitted. */
  viewportWidth?: number
  /** Optional override; backend defaults to 1080 when omitted. */
  viewportHeight?: number
  timeout: number
  slowMotion: boolean
}

export type TestRunPhase = 'planning' | 'generating' | 'executing' | 'processing' | 'preparing' | 'healing' | 'verifying' | 'healed'

/** The one test a run is working on right now. Mirrors the server's
 * `CurrentTest`; `position` counts within the current step's own list. */
export interface CurrentTest {
  name: string
  position: number
  total: number
  activity: 'writing' | 'running' | 'fixing' | 'checking'
}

export interface TestRunProgress {
  runId: string
  status: 'running' | 'completed' | 'failed' | 'cancelled'
  phase?: TestRunPhase
  currentTest?: CurrentTest
  progress: number
  results: Array<{
    id: string
    runId: string
    category: string
    name: string
    status: 'pending' | 'running' | 'passed' | 'failed' | 'skipped'
    steps: Array<{
      id: string
      name: string
      status: string
      duration?: number
      error?: string
    }>
    duration: number
    error?: string
    screenshot?: string
  }>
  logs: Array<{
    timestamp: string
    level: 'info' | 'warn' | 'error' | 'debug'
    message: string
    details?: string
    agent?: 'planner' | 'generator' | 'executor' | 'healer' | 'extractor' | 'auditor' | 'rag' | 'system'
  }>
  summary?: {
    total: number
    passed: number
    failed: number
    skipped: number
  }
  /** #1580 — which phases this run ran, and how many tests it planned /
   * generated. Drive the adaptive run summary: a run that didn't `execute`
   * shows Planned/Generated instead of all-zero execution tiles, on both the
   * live run view and the results page. Absent on runs created before #1580. */
  ranPhases?: Array<'plan' | 'generate' | 'execute' | 'heal'>
  plannedCount?: number
  generatedCount?: number
  /** #1589 — plain-language reason a run failed to RUN its tests (load/compile/
   * runner error), distinct from "ran with failures". Present only on a failed
   * run. */
  errorReason?: string
}

// Check if backend is available
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await apiFetch(`${API_BASE}/health`)
    return response.ok
  } catch {
    return false
  }
}

// Start a test run
export async function startTestRun(
  request: TestRunRequest,
  browserConfig?: Partial<BrowserConfig>
): Promise<{ runId: string }> {
  const response = await apiFetch(`${API_BASE}/runs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request, browserConfig }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throwIfBillingLocked(response.status, error)
    throw new Error(error.message || error.error || 'Failed to start test run')
  }

  return response.json()
}

/** Trigger a run from the TestRunner Start page using the templateId-only
 * body (#1114). Replaces the old inline `apiStartTestRun(request, browserConfig)`
 * call in TestRunner.startRun() — the template now owns all execution settings
 * so the frontend no longer builds a TestRunRequest locally.
 *
 * Uses `/api/runs` (the in-app route), not `/api/v1/runs` (the CI/CD route) —
 * both accept the same body shape after #1114.
 *
 * Accepts the same per-trigger optional fields as the v1 route:
 * callbackUrl (not used by the UI today), buildTag, buildMetadata. */
export async function triggerRun(
  templateId: string,
  opts?: {
    callbackUrl?: string
    buildTag?: string
    buildMetadata?: Record<string, unknown>
    /** Candidates to seed the planner with, for THIS run only (#2158
     * slice 1). The saved template is never modified, so accepting a few
     * suggestions on the Proposed tab cannot change what a scheduled run
     * does. The server drops any id that does not belong to the run's
     * application. */
    pickedCandidateIds?: string[]
    /** Settings for THIS run only, laid over the saved ones (#2175 slice
     * 2). What the Run button in a run-settings dialog sends: the operator
     * changed something, the run uses it, and the saved settings stay as
     * they were. Only accepted for a template started by hand. */
    settingsOverride?: Record<string, unknown>
  },
): Promise<{ runId: string }> {
  const response = await apiFetch(`${API_BASE}/runs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ templateId, ...opts }),
  })

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as {
      error?: string
      message?: string
      code?: string
      billingUrl?: string
    }
    throwIfBillingLocked(response.status, errorData)
    throw new Error(errorData.error || errorData.message || 'Failed to start test run')
  }

  return response.json()
}

// Get active run IDs
export async function getActiveRuns(): Promise<string[]> {
  try {
    const response = await apiFetch(`${API_BASE}/runs`)
    if (!response.ok) return []
    const data = await response.json()
    return data.runs ?? []
  } catch {
    return []
  }
}

// Get test run status
export async function getTestRunStatus(runId: string): Promise<TestRunProgress | null> {
  const response = await apiFetch(`${API_BASE}/runs/${runId}`)

  if (response.status === 404) {
    return null
  }

  if (!response.ok) {
    throw new Error('Failed to get test run status')
  }

  return response.json()
}

// Cancel a test run
export async function cancelTestRun(runId: string): Promise<void> {
  const response = await apiFetch(`${API_BASE}/runs/${runId}/cancel`, {
    method: 'POST',
  })

  if (!response.ok) {
    throw new Error('Failed to cancel test run')
  }
}

// WebSocket connection for real-time updates
export function createWebSocketConnection(
  runId: string,
  onProgress: (progress: TestRunProgress) => void,
  onError?: (error: Error) => void,
  onClose?: () => void
): WebSocket {
  const ws = new WebSocket(`${WS_BASE}?runId=${runId}`)

  ws.onmessage = (event) => {
    try {
      const message = JSON.parse(event.data)
      if (message.type === 'progress') {
        onProgress(message.data)
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error)
    }
  }

  ws.onerror = (event) => {
    console.error('WebSocket error:', event)
    onError?.(new Error('WebSocket connection error'))
  }

  ws.onclose = () => {
    onClose?.()
  }

  return ws
}

// Send cancel command via WebSocket
export function sendCancelCommand(ws: WebSocket, runId: string): void {
  if (ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: 'cancel', runId }))
  }
}

// ============================================
// Entity API (MongoDB-backed)
// ============================================

import type {
  Application,
  CreateRunTemplateInput,
  UpdateRunTemplateInput,
  TestSuite,
  TestRun,
  Variant,
  TestPlan,
  PlanTestCase,
  TrendsResponse,
  FlakyTestsResponse,
  CandidateCoverageTrendResponse,
  IssueCoverageDimension,
  IssueCoverageTrendResponse,
  TriageResponse,
  TriageBucketKey,
  RegressionDeltaResponse,
  RegressionDeltaNotApplicable,
  FailureSummaryResponse,
  RunTypeFilter,
  ComparisonResponse,
  ReferenceDocMeta,
  DocumentKind,
  CandidateTest,
  CandidateScriptedStatusResponse,
  VariantCoverage,
  CandidateTestIntent,
  CandidateTestTag,
  CandidateTestStep,
  TestCasePriority,
  AppGitHubBinding,
  AppSlackBinding,
  AppSlackEventSubscription,
  AppIssueSourceBinding,
  RunTemplatePool,
} from '@/types'

// Helper to convert MongoDB _id to id
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeId<T>(doc: any): T {
  if (!doc) return doc
  const { _id, ...rest } = doc
  return { ...rest, id: _id || doc.id } as T
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeIds<T>(docs: any[]): T[] {
  return docs.map((doc) => normalizeId<T>(doc))
}

// ============================================
// Applications API
// ============================================

export const applicationsAPI = {
  async getAll(): Promise<Application[]> {
    const response = await apiFetch(`${ws()}/applications`)
    if (!response.ok) throw new Error('Failed to fetch applications')
    const data = await response.json()
    return normalizeIds<Application>(data)
  },

  async getById(id: string): Promise<Application | null> {
    const response = await apiFetch(`${ws()}/applications/${id}`)
    if (response.status === 404) return null
    if (!response.ok) throw new Error('Failed to fetch application')
    const data = await response.json()
    return normalizeId<Application>(data)
  },

  async create(app: Omit<Application, 'id' | 'createdAt' | 'updatedAt'>): Promise<Application> {
    const response = await apiFetch(`${ws()}/applications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(app),
    })
    if (!response.ok) throw new Error('Failed to create application')
    const data = await response.json()
    return normalizeId<Application>(data)
  },

  async update(id: string, app: Partial<Application>): Promise<Application> {
    const response = await apiFetch(`${ws()}/applications/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(app),
    })
    if (!response.ok) throw new Error('Failed to update application')
    const data = await response.json()
    return normalizeId<Application>(data)
  },

  async delete(id: string): Promise<void> {
    const response = await apiFetch(`${ws()}/applications/${id}`, {
      method: 'DELETE',
    })
    if (!response.ok) throw new Error('Failed to delete application')
  },

  /**
   * Upload a reference document to an Application (Slice 2 of #857,
   * ie #860). Server extracts text from the file (.txt / .md / .docx
   * supported), stores it inline on the Application doc, and returns
   * the new doc's metadata (NOT the extracted text — clients already
   * have the file content locally if they need it, and the payload
   * can be hundreds of KB).
   *
   * Return type is `ReferenceDocMeta` (alias for
   * `Omit<ReferenceDoc, 'text'>`) — keeps the upload response shape
   * tied to the canonical `ReferenceDoc` so adding a field to the
   * full shape (e.g. an audit flag in v2) doesn't drift between
   * GET and POST responses.
   *
   * Throws `Error` with the server's `message` on 4xx (per-file cap,
   * total-text cap, unsupported format, empty extraction) so the UI
   * can surface the reason to the user.
   */
  async uploadReferenceDoc(appId: string, file: File): Promise<ReferenceDocMeta> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiFetch(`${ws()}/applications/${appId}/reference-docs`, {
      method: 'POST',
      body: formData,
      // Don't set Content-Type — the browser fills in
      // `multipart/form-data; boundary=...` automatically.
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Upload failed' }))
      throw new Error(err.message || err.error || 'Upload failed')
    }
    return response.json()
  },

  async deleteReferenceDoc(appId: string, docId: string): Promise<void> {
    const response = await apiFetch(`${ws()}/applications/${appId}/reference-docs/${docId}`, {
      method: 'DELETE',
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Delete failed' }))
      throw new Error(err.message || err.error || 'Delete failed')
    }
  },

  /**
   * Add a reference doc by link (#1806). The server reads the linked
   * page or file once, saves its text just like an upload, and kicks
   * off the candidate proposer. Returns the new doc's metadata.
   *
   * Throws `Error` with the server's plain-language `message` on 4xx:
   * a bad/blocked/unreadable link, an oversize fetch, or — when the
   * same link is already saved — a 409 whose message points at Refresh.
   */
  async addReferenceDocLink(appId: string, url: string): Promise<ReferenceDocMeta> {
    const response = await apiFetch(`${ws()}/applications/${appId}/reference-docs/link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Could not add link' }))
      throw new Error(err.message || err.error || 'Could not add link')
    }
    return response.json()
  },

  /**
   * Refresh a link doc from its saved link (#1806). Re-reads the page
   * and updates the saved copy + date only — no candidate suggestions
   * are regenerated and no AI credits are spent. Returns
   * `{ suggestionsStale }`: true when the re-read changed the content
   * (the row then offers a one-click Re-propose via `reproposeForDoc`).
   * A failed re-read leaves the previous saved copy intact.
   */
  async refreshReferenceDocLink(
    appId: string,
    docId: string,
  ): Promise<{ suggestionsStale: boolean }> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/reference-docs/${docId}/refresh`,
      { method: 'POST' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Refresh failed' }))
      throw new Error(err.message || err.error || 'Refresh failed')
    }
    return response.json()
  },

  // ── Candidate tests (Slice 3A–3C of #860) ──────────────────
  // CRUD + re-propose surface backing the Candidates tab on the
  // Application detail page. All routes are app-scoped under
  // `/applications/:id/...` per the backend's permission gate.

  async listCandidates(
    appId: string,
    opts: { includeInactive?: boolean } = {},
  ): Promise<CandidateTest[]> {
    const params = new URLSearchParams()
    if (opts.includeInactive) params.set('includeInactive', 'true')
    const qs = params.toString() ? `?${params}` : ''
    const response = await apiFetch(`${ws()}/applications/${appId}/candidates${qs}`)
    if (!response.ok) throw new Error('Failed to load tests')
    return response.json()
  },

  /** Variant-scoped status of every active suggestion: the per-row
   * status (proposed / planned / scripted / ready), the counts, Test
   * Coverage, and the tests that count toward no suggestion. All from
   * the one coverage calculation the dashboard reads too. Drives the
   * Proposed tab's badge, bar, row badges and summary line, and the
   * Active tab's "Show them" filter. */
  async scriptedStatus(
    appId: string,
    variantId: string,
  ): Promise<CandidateScriptedStatusResponse> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/candidates/scripted-status?variantId=${encodeURIComponent(variantId)}`,
    )
    if (!response.ok) throw new Error(`Failed to load scripted-status (${response.status})`)
    return response.json()
  },

  /** Every coverage number for one variant: requirement and defect
   * coverage with their gap rows, and the proposed-test totals. The one
   * coverage read; the dashboard and the Proposed tab both use it. */
  async coverage(appId: string, variantId: string): Promise<VariantCoverage> {
    const params = new URLSearchParams({ variantId })
    const response = await apiFetch(`${ws()}/applications/${appId}/coverage?${params}`)
    if (!response.ok) throw new Error(`Failed to load coverage (${response.status})`)
    return response.json()
  },

  async createCandidate(
    appId: string,
    body: {
      name: string
      description: string
      intent?: CandidateTestIntent | null
      preconditions?: string[] | null
      steps?: CandidateTestStep[] | null
      tags?: CandidateTestTag[]
    },
  ): Promise<CandidateTest> {
    const response = await apiFetch(`${ws()}/applications/${appId}/candidates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Create failed' }))
      throw new Error(err.message || err.error || 'Create failed')
    }
    return response.json()
  },

  async updateCandidate(
    appId: string,
    candidateId: string,
    patch: {
      name?: string
      description?: string
      intent?: CandidateTestIntent | null
      preconditions?: string[] | null
      steps?: CandidateTestStep[] | null
      tags?: CandidateTestTag[]
      priority?: TestCasePriority | null
      /** Restore an archived suggestion. Only `true`; archiving is
       * `deleteCandidate`. */
      active?: true
    },
  ): Promise<CandidateTest> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/candidates/${candidateId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Update failed' }))
      throw new Error(err.message || err.error || 'Update failed')
    }
    return response.json()
  },

  /** "Add as a suggestion": turn a test that counts toward no
   * suggestion into one, and link that test to it. `outcome` says
   * whether a new suggestion was made, an active one with exactly the
   * same text was reused, or an archived one was (the test then stays
   * uncounted until it is restored). A refusal (409) throws with the
   * server's plain message. */
  async addSuggestionFromTest(
    appId: string,
    testCaseId: string,
  ): Promise<{
    outcome: 'created' | 'existing' | 'archived'
    candidate: { _id: string; name: string; active: boolean }
  }> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/candidates/from-test/${encodeURIComponent(testCaseId)}`,
      { method: 'POST' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: "Couldn't add the proposed test" }))
      throw new Error(err.error || err.message || "Couldn't add the proposed test")
    }
    return response.json()
  },

  /** Soft-delete by default (sets `active: false`; row stays in
   * the collection for audit + future restore). Pass `{ hard: true }`
   * for permanent removal — admin / cleanup paths. */
  async deleteCandidate(
    appId: string,
    candidateId: string,
    opts: { hard?: boolean } = {},
  ): Promise<void> {
    const qs = opts.hard ? '?hard=true' : ''
    const response = await apiFetch(
      `${ws()}/applications/${appId}/candidates/${candidateId}${qs}`,
      { method: 'DELETE' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Delete failed' }))
      if (response.status === 409 && err.code === 'candidate-in-use') {
        throw new CandidateInUseError(err.error)
      }
      throw new Error(err.message || err.error || 'Delete failed')
    }
  },

  /** Permanently delete every suggestion of the app, archived included.
   * Suggestions a test was built from are kept; `kept` says how many. */
  async deleteAllCandidates(appId: string): Promise<{ deleted: number; kept: number }> {
    const response = await apiFetch(`${ws()}/applications/${appId}/candidates`, { method: 'DELETE' })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Delete failed' }))
      throw new Error(err.message || err.error || 'Delete failed')
    }
    return response.json()
  },

  /** Confirm what a document's read found: the others are unticked and
   * their proposed tests archived, and for Requirements and Defects the
   * kept parts count. `kind`, when sent, saves what the document is in
   * the same request. `partsContentHash` is the version of the list being
   * reviewed; the server refuses (409) if it changed, or while a read is
   * running. */
  async confirmReferenceDocParts(
    appId: string,
    docId: string,
    keptPartIds: string[],
    partsContentHash: string,
    kind?: DocumentKind,
  ): Promise<{ kept: number; excluded: number; archived: number }> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/reference-docs/${docId}/confirm-parts`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keptPartIds, partsContentHash, ...(kind ? { kind } : {}) }),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: "Couldn't confirm this list. Try again." }))
      throw new Error(err.message || err.error || "Couldn't confirm this list. Try again.")
    }
    return response.json()
  },

  /** Re-propose for a single reference doc. Hard-deletes the doc's
   * existing proposed candidates (where `userEdited === false`)
   * then enqueues the proposer outbox row. User-edited candidates,
   * and candidates a test was built from, persist through re-propose. Returns 202 immediately — actual
   * extraction runs async via the dispatcher.
   *
   * `continue: true` is "Read the rest": carry on a read that stopped
   * part-way, deleting nothing and keeping the tests already saved. */
  async reproposeForDoc(
    appId: string,
    docId: string,
    opts: { continue?: boolean } = {},
  ): Promise<{ deletedExisting: number }> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/reference-docs/${docId}/re-propose`,
      opts.continue
        ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ continue: true }) }
        : { method: 'POST' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: "Couldn't suggest tests. Try again." }))
      throw new Error(err.message || err.error || "Couldn't suggest tests. Try again.")
    }
    return response.json()
  },

  /** Set or clear the kinds of test one reference doc suggests (#2102
   * slice 2). Pass an array to override, or `null` to clear back to the
   * default (Positive). The caller re-fetches the app afterward to pick up
   * the new kinds and any raised stale nudge. */
  async setReferenceDocKinds(
    appId: string,
    docId: string,
    intents: CandidateTestIntent[] | null,
  ): Promise<void> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/reference-docs/${docId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposerIntents: intents }),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to update kinds' }))
      throw new Error(err.message || err.error || 'Failed to update kinds')
    }
  },

  /** Save what one reference doc counts as: Requirements, Defects or
   * Reference. Saved as the user's choice even when it matches the
   * current suggestion, which is how a suggestion is confirmed. Does not
   * re-read the document. The caller re-fetches the app afterward. */
  async setReferenceDocCountsAs(
    appId: string,
    docId: string,
    kind: DocumentKind,
  ): Promise<void> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/reference-docs/${docId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind }),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to save what the document counts as' }))
      throw new Error(err.message || err.error || 'Failed to save what the document counts as')
    }
  },

  /** Re-propose across every reference doc in the app. Wipes the
   * non-user-edited proposed rows that came from documents (not imported
   * issues, and not ones a test was built from), then enqueues the
   * proposer for each ref doc currently uploaded. */
  async reproposeAll(
    appId: string,
  ): Promise<{ deletedExisting: number; docsEnqueued: number }> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/candidates/re-propose-all`,
      { method: 'POST' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: "Couldn't suggest tests. Try again." }))
      throw new Error(err.message || err.error || "Couldn't suggest tests. Try again.")
    }
    return response.json()
  },

  /** Fetch the per-app integrations bindings + the workspace's
   * available repos (GitHub) and channels (Slack) + the per-variant
   * oversight breakdown (#1154). The frontend renders the picker
   * options directly from this response; no second round-trip is
   * needed. Each provider's source carries its own `connected` flag
   * (so the picker can render the disabled empty state when the
   * workspace hasn't installed that provider) plus an optional
   * `error` string (so the UI can surface transient provider blips
   * without breaking the other provider's form). */
  async getIntegrations(appId: string): Promise<AppIntegrationsResponse> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/integrations`,
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({
        error: 'Failed to load integrations',
      }))
      throw new Error(err.message || err.error || 'Failed to load integrations')
    }
    return response.json()
  },

  /** Partial update of the app's GitHub and/or Slack bindings.
   * `github` and `slack` are independently optional — pass only the
   * provider's block you're saving so the other one isn't blown away.
   * Pass `null` to clear a binding entirely. */
  async updateIntegrations(
    appId: string,
    patch: {
      github?: AppGitHubBinding | null
      slack?: AppSlackBinding | null
      issueSource?: AppIssueSourceBinding | null
    },
  ): Promise<{
    bindings: {
      github: AppGitHubBinding | null
      slack: AppSlackBinding | null
      issueSource: AppIssueSourceBinding | null
    }
    /** Present when the saved issueSource carried a scope filter (#1867):
     * the current approximate match count, and a warning when it's 0. */
    filterMatchCount?: number
    filterWarning?: string
  }> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/integrations`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({
        error: 'Failed to save integration',
      }))
      throw new Error(err.message || err.error || 'Failed to save integration')
    }
    return response.json()
  },

  // ── issueSource discovery + read-only browse (#1558 Slice 1b) ──────────

  /** Projects accessible to a connection — the multi-select picker source. */
  /** The repositories a failed test can open its issue in: the ones the
   * workspace's GitHub Issues connection can see. Null when the workspace
   * has no GitHub Issues connection. */
  async getFailedTestRepositoryOptions(appId: string): Promise<{ connectionId: string; repos: IssueSourceProject[] } | null> {
    // The app's integrations view lists the workspace connections to anyone
    // who can edit the app; the workspace connection list is for admins.
    const connection = (await applicationsAPI.getIntegrations(appId)).issueTracker.connections.find((c) => c.provider === 'github')
    if (!connection) return null
    return { connectionId: connection.id, repos: await applicationsAPI.getIssueSourceProjects(appId, connection.id) }
  },

  async getIssueSourceProjects(appId: string, connectionId: string): Promise<IssueSourceProject[]> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/issue-source/projects?connectionId=${encodeURIComponent(connectionId)}`,
    )
    if (!response.ok) throw new Error('Failed to load projects')
    return response.json()
  },

  /** The UNION of issue types across the given projects (the type-mapping
   * source — a type in ANY selected project is offered). */
  async getIssueSourceIssueTypes(
    appId: string,
    connectionId: string,
    projectKeys: string[],
  ): Promise<IssueSourceIssueType[]> {
    const qs = new URLSearchParams({ connectionId, projectKeys: projectKeys.join(',') })
    const response = await apiFetch(`${ws()}/applications/${appId}/issue-source/issue-types?${qs}`)
    if (!response.ok) throw new Error('Failed to load issue types')
    return response.json()
  },

  /** Content fields available on the connection — the "which fields to
   * import" picker source. A tracker whose fields come from its issues (body
   * headings) gives the union across the selected projects; with none
   * selected, only its base fields. */
  async getIssueSourceFields(appId: string, connectionId: string, projectKeys: string[] = []): Promise<IssueSourceField[]> {
    const qs = new URLSearchParams({ connectionId })
    if (projectKeys.length > 0) qs.set('projectKeys', projectKeys.join(','))
    const response = await apiFetch(`${ws()}/applications/${appId}/issue-source/fields?${qs}`)
    if (!response.ok) throw new Error('Failed to load fields')
    return response.json()
  },

  /** Fix versions across the selected projects — the scope filter's
   * fix-version picker source (#1867). Deduped by name server-side. */
  async getIssueSourceVersions(
    appId: string,
    connectionId: string,
    projectKeys: string[],
  ): Promise<IssueSourceVersion[]> {
    const qs = new URLSearchParams({ connectionId, projectKeys: projectKeys.join(',') })
    const response = await apiFetch(`${ws()}/applications/${appId}/issue-source/versions?${qs}`)
    if (!response.ok) throw new Error('Failed to load versions')
    return response.json()
  },

  /** Issue priorities on the connection — the scope filter's priority picker
   * source (#1867). Instance-global. */
  async getIssueSourcePriorities(appId: string, connectionId: string): Promise<IssueSourcePriority[]> {
    const response = await apiFetch(
      `${ws()}/applications/${appId}/issue-source/priorities?connectionId=${encodeURIComponent(connectionId)}`,
    )
    if (!response.ok) throw new Error('Failed to load priorities')
    return response.json()
  },

  /** Label suggestions matching a query — the labels filter's type-ahead
   * (#1867 PR2b). `projectKeys` are the selected projects, saved or not: a
   * tracker whose labels belong to a project searches those only. */
  async suggestIssueSourceLabels(appId: string, connectionId: string, query: string, projectKeys: string[] = []): Promise<string[]> {
    const qs = new URLSearchParams({ connectionId, query })
    if (projectKeys.length > 0) qs.set('projectKeys', projectKeys.join(','))
    const response = await apiFetch(`${ws()}/applications/${appId}/issue-source/labels?${qs}`)
    if (!response.ok) throw new Error('Failed to load labels')
    return response.json()
  },

  /** Users matching a query — the assignee/reporter filter's people search
   * (#1867 PR2b). Returns accountId + displayName. */
  async searchIssueSourceUsers(appId: string, connectionId: string, query: string, projectKeys: string[] = []): Promise<IssueSourceUser[]> {
    const qs = new URLSearchParams({ connectionId, query })
    if (projectKeys.length > 0) qs.set('projectKeys', projectKeys.join(','))
    const response = await apiFetch(`${ws()}/applications/${appId}/issue-source/users?${qs}`)
    if (!response.ok) throw new Error('Failed to search users')
    return response.json()
  },

  /** Whether the label or version names in a saved filter still exist in
   * the selected projects: `found` in any, `missing` from every one, or
   * `unknown` when a project couldn't be read. Changes nothing. */
  async checkIssueSourceFilterValues(
    appId: string,
    input: { connectionId: string; projectKeys: string[]; field: 'labels' | 'versions'; names: string[] },
  ): Promise<Record<string, 'found' | 'missing' | 'unknown'>> {
    const response = await apiFetch(`${ws()}/applications/${appId}/issue-source/filter-values`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!response.ok) throw new Error('Failed to check the filter values')
    return response.json()
  },

  /** Live preview of a candidate scope filter (#1867) — the approximate match
   * count BEFORE save. `count` is `null` when the provider can't count.
   * Throws with the tracker's message on an invalid/permission-bad filter
   * (the caller surfaces it as the block-Save error). */
  async previewIssueSourceFilter(
    appId: string,
    input: { connectionId: string; projectKeys: string[]; issueTypeIds: string[]; scopeFilter: string },
  ): Promise<{ count: number | null }> {
    const response = await apiFetch(`${ws()}/applications/${appId}/issue-source/filter-preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to check the filter' }))
      throw new Error(err.message || err.error || 'Failed to check the filter')
    }
    return response.json()
  },

  /** Read-only issue browser — enforces the app's bound projects + mapped
   * types server-side. Optional `projectKey` filter must be within the
   * bound set. */
  async browseIssueSourceIssues(
    appId: string,
    opts: { projectKey?: string; cursor?: string; limit?: number } = {},
  ): Promise<IssueSourceIssuePage> {
    const qs = new URLSearchParams()
    if (opts.projectKey) qs.set('projectKey', opts.projectKey)
    if (opts.cursor) qs.set('cursor', opts.cursor)
    if (opts.limit) qs.set('limit', String(opts.limit))
    const suffix = qs.toString() ? `?${qs}` : ''
    const response = await apiFetch(`${ws()}/applications/${appId}/issue-source/issues${suffix}`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to load issues' }))
      throw new Error(err.error || 'Failed to load issues')
    }
    return response.json()
  },

  /** Import selected issues → enqueue one ingest job per issue and return
   * IMMEDIATELY (per-issue progress reads from `getIssueSourceImportStatus`).
   * Only issues in the app's bound projects are accepted (enforced
   * server-side). */
  async importIssueSourceIssues(
    appId: string,
    issues: ImportIssueRequest[],
  ): Promise<{ results: IssueImportResult[] }> {
    const response = await apiFetch(`${ws()}/applications/${appId}/issue-source/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ issues }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to start the import' }))
      throw new Error(err.error || 'Failed to start the import')
    }
    return response.json()
  },

  /** Poll per-issue import status by external id (comma-joined server-side). */
  async getIssueSourceImportStatus(appId: string, externalIds: string[]): Promise<IssueImportStatus[]> {
    if (externalIds.length === 0) return []
    const qs = new URLSearchParams({ externalIds: externalIds.join(',') })
    const response = await apiFetch(`${ws()}/applications/${appId}/issue-source/import-status?${qs}`)
    if (!response.ok) throw new Error('Failed to read import status')
    return response.json()
  },
}

/** Picker option for the GitHub repository combobox on the per-app
 * Integrations tab (#1154). `repoIdentifier` is the canonical
 * `"owner/repo"` slug stored on the app + matched by the webhook
 * receiver; the other fields are convenience properties for the
 * picker's avatar / View-on-GitHub link. */
export interface AppIntegrationsGitHubRepo {
  repoIdentifier: string
  owner: string
  name: string
  htmlUrl: string
}

/** Picker option for the Slack channel combobox on the per-app
 * Integrations tab (#1154). The wire shape exposes `id`, `name`,
 * `isPrivate` — same projection the per-workspace Slack channel-
 * routing UI already consumes. */
export interface AppIntegrationsSlackChannel {
  id: string
  name: string
  isPrivate: boolean
}

/** Per-variant oversight row — surfaces in the "Variants with trigger
 * rules" / "Variants with channel overrides" lists. `count` is 0 for
 * variants the customer hasn't configured yet; the UI renders those
 * as muted rows with a "Configure →" CTA. */
export interface AppIntegrationsOversightRow {
  variantId: string
  variantName: string
  count: number
}

/** Combined response from `GET /applications/:id/integrations`. The
 * frontend reads the picker options + bindings + oversight off this
 * single shape — one network round-trip on tab mount, no further
 * provider-detail fetches. */
export interface AppIntegrationsResponse {
  bindings: {
    github: AppGitHubBinding | null
    slack: AppSlackBinding | null
    issueSource: AppIssueSourceBinding | null
  }
  github:
    | { connected: false }
    | { connected: true; repos: AppIntegrationsGitHubRepo[]; error?: string }
  slack:
    | { connected: false }
    | {
        connected: true
        installId: string
        teamName: string
        channels: AppIntegrationsSlackChannel[]
        error?: string
      }
  /** The workspace's inbound issue-tracker connections an app can bind to
   * (redacted — never a token). Empty when none is connected on the
   * workspace (#1558 Slice 1b). `error` is set when the list could not be
   * read, so that empty list does not mean nothing is connected. */
  issueTracker: { connections: AppIssueTrackerConnection[]; error?: string }
  oversight: {
    github: AppIntegrationsOversightRow[]
    slack: AppIntegrationsOversightRow[]
  }
}

// ── issueSource discovery + browse shapes (#1558 Slice 1b) ────────────────

/** A workspace issue-tracker connection an app can bind to (redacted). */
export interface AppIssueTrackerConnection {
  id: string
  provider: IssueTrackerProvider
  providerConfig?: Record<string, unknown>
  hasSecret: boolean
  /** How it signs in, for a tracker that offers a choice. Read through
   * `signInOf`: absent means the tracker's default. */
  signIn?: IssueTrackerSignIn
  lastValidatedAt?: string
}

/** A project the connection groups issues by. */
export interface IssueSourceProject {
  id: string
  key: string
  name: string
}

/** An issue type (the classifier the requirement/defect mapping targets). */
export interface IssueSourceIssueType {
  id: string
  name: string
  /** A label's colour as six hex digits, when the tracker has one. */
  color?: string
}

/** A content field the operator can feed the Proposer. */
export interface IssueSourceField {
  id: string
  name: string
}

/** A fix version — the scope filter's fix-version value list (#1867 PR2). */
export interface IssueSourceVersion {
  id: string
  name: string
  released?: boolean
  archived?: boolean
}

/** An issue priority — the scope filter's priority value list (#1867 PR2). */
/** A user for the assignee/reporter people picker (#1867 PR2b). */
export interface IssueSourceUser {
  accountId: string
  displayName: string
}

export interface IssueSourcePriority {
  id: string
  name: string
}

/** A normalized issue in the read-only browser (a subset of the backend's
 * `ExternalIssue` — the fields the browser renders + the identity the
 * import action needs). */
export interface IssueSourceIssue {
  externalId: string
  displayKey: string
  projectId: string
  projectKey: string
  type: { id: string; name: string }
  /** The issue's labels, by id with the name for display. */
  labels?: Array<{ id: string; name: string }>
  /** How the binding sorts this issue: the shared server rule's answer. */
  sortedAs?: 'requirement' | 'defect' | 'conflict'
  status: string
  summary: string
  revisionToken: string
  updatedAt: string
  /** Closed as not to be built: shown, but left out of coverage. */
  notPlanned?: boolean
  /** Finds the issue again after a rename; sent back with an import. */
  readHandle?: string
  /** The project's readable name (a repository's `owner/repo`). */
  projectName?: string
}

/** One issue's identity in an import request — everything the ingest needs
 * without a server-side re-fetch at enqueue. */
export interface ImportIssueRequest {
  externalId: string
  displayKey: string
  projectId: string
  projectKey: string
  issueType: string
  /** The issue's label ids as browsed. */
  issueLabelIds?: string[]
  /** Finds the issue after a rename, when the tracker gave one. */
  readHandle?: string
  revisionToken: string
}

/** Per-issue outcome of firing an import (immediate — before ingestion). */
export type IssueImportResultKind = 'enqueued' | 'already-queued' | 'rejected' | 'enqueue-failed'
export interface IssueImportResult {
  externalId: string
  result: IssueImportResultKind
  reason?: string
}

/** Per-issue ingest status (the frontend polls this after firing). `sent` =
 * imported, `dead` = terminal failure (see `failureReason`). */
export type IssueImportStatusKind = 'pending' | 'inFlight' | 'sent' | 'dead'
export interface IssueImportStatus {
  externalId: string
  status: IssueImportStatusKind
  failureReason?: string
  attempts: number
  updatedAt: string
  /** Attachments the import couldn't use (#1843 Slice 2b); `reason` is the
   * backend's internal code, mapped to plain language for display. Absent
   * when nothing was skipped. */
  attachmentSkips?: Array<{ filename: string; reason: string }>
  /** For a finished job: whether it imported the issue, or skipped it
   * because of how it is labeled. */
  importResult?: 'imported' | 'skipped'
  /** Why a skipped issue was left out, in plain words from the server. */
  skipReason?: string
  /** Mapped body sections the issue didn't have, when that left nothing to
   * suggest tests from. */
  emptySectionFieldIds?: string[]
}

/** A page of browsed issues + an opaque cursor for the next page. */
export interface IssueSourceIssuePage {
  issues: IssueSourceIssue[]
  nextCursor?: string
}

// ============================================
// Variants API
// ============================================

export const variantsAPI = {
  async getByApp(applicationId: string): Promise<Variant[]> {
    const response = await apiFetch(`${ws()}/variants?applicationId=${applicationId}`)
    if (!response.ok) throw new Error('Failed to fetch variants')
    const data = await response.json()
    return normalizeIds<Variant>(data)
  },

  /** List every variant in the workspace (active only by default).
   * Used by the Slack routing matrix's bulk picker which needs every
   * (app, variant) pair to render the customer's options. */
  async getAll(): Promise<Variant[]> {
    const response = await apiFetch(`${ws()}/variants`)
    if (!response.ok) throw new Error('Failed to fetch variants')
    const data = await response.json()
    return normalizeIds<Variant>(data)
  },

  async getById(id: string): Promise<Variant | null> {
    const response = await apiFetch(`${ws()}/variants/${id}`)
    if (response.status === 404) return null
    if (!response.ok) throw new Error('Failed to fetch variant')
    const data = await response.json()
    return normalizeId<Variant>(data)
  },

  async create(variant: Omit<Variant, 'id' | 'createdAt' | 'updatedAt'>): Promise<Variant> {
    const response = await apiFetch(`${ws()}/variants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(variant),
    })
    if (!response.ok) throw new Error('Failed to create variant')
    const data = await response.json()
    return normalizeId<Variant>(data)
  },

  async update(id: string, variant: Partial<Variant>): Promise<Variant> {
    const response = await apiFetch(`${ws()}/variants/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(variant),
    })
    if (!response.ok) throw new Error('Failed to update variant')
    const data = await response.json()
    return normalizeId<Variant>(data)
  },

  async delete(id: string, opts?: CiProtectedDeleteOpts): Promise<void> {
    const response = await apiFetch(`${ws()}/variants/${id}${ciProtectedQuery(opts)}`, {
      method: 'DELETE',
      headers: ciProtectedHeaders(opts),
    })
    if (response.status === 409) {
      throw await CiProtectedError.fromResponse(response)
    }
    if (!response.ok) throw new Error('Failed to delete variant')
  },

  async restore(id: string): Promise<Variant> {
    const response = await apiFetch(`${ws()}/variants/${id}/restore`, { method: 'POST' })
    if (!response.ok) throw new Error('Failed to restore variant')
    const data = await response.json()
    return normalizeId<Variant>(data)
  },

  /** Clear a discovered sign-in URL from the variant's scan.authCandidates
   * (operator dismissing a wrong/noisy detection). `url` is the FULL
   * stored value (not the display form) so the server's $pull matches. */
  async clearAuthCandidate(id: string, url: string): Promise<Variant> {
    const response = await apiFetch(`${ws()}/variants/${id}/auth-candidates?url=${encodeURIComponent(url)}`, {
      method: 'DELETE',
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to clear sign-in address' }))
      throw new Error(err.error || 'Failed to clear sign-in address')
    }
    const data = await response.json()
    return normalizeId<Variant>(data)
  },

  async scan(url: string): Promise<import('@/types').ScanResult> {
    const response = await apiFetch(`${ws()}/variants/scan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Scan failed' }))
      throw new Error(err.error || 'Scan failed')
    }
    return response.json()
  },

  async rescan(id: string): Promise<import('@/types').ScanResult> {
    const response = await apiFetch(`${ws()}/variants/${id}/rescan`, { method: 'POST' })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Rescan failed' }))
      throw new Error(err.error || 'Rescan failed')
    }
    return response.json()
  },

  // Note: `getResolvedAuthBinding` + `putAuthBinding` were removed in
  // #876 Slice 1 along with the variant-level auth-binding surface.
  // Per-credential bindings are now read/written via testDataAPI's
  // credential save path (`TestData.credentials[i].loginFixtureBinding`).

  /** Fetch the resolved Notifications view for a variant's Automation
   * page (#1156, Slice 4 of meta #1152). The backend uses the same
   * resolver the runtime dispatch consults, so the inheritance hint
   * and the overrides list reflect EXACTLY what would fire — no
   * client-side parallel resolution that could drift.
   *
   * Returns `null` when the variant doesn't exist (the page renders
   * the missing-id branch); never throws on 404. */
  async getNotificationOverridesResolved(
    variantId: string,
  ): Promise<VariantNotificationOverridesResolved | null> {
    const response = await apiFetch(
      `${ws()}/variants/${variantId}/notification-overrides/resolved`,
    )
    if (response.status === 404) return null
    if (!response.ok) {
      const err = await response.json().catch(() => ({
        error: 'Failed to load notification overrides',
      }))
      throw new Error(err.message || err.error || 'Failed to load notification overrides')
    }
    return response.json()
  },

  /** Replace the variant's notification overrides list. Whole-list
   * semantics — pass the desired final state, the backend persists
   * exactly that. Empty array clears every override; absent rows
   * are removed. Returns the post-save resolved view so the caller
   * doesn't need a second round-trip. */
  async updateNotificationOverrides(
    variantId: string,
    overrides: Array<{ event: AppSlackEventSubscription; channelId: string }>,
  ): Promise<VariantNotificationOverridesResolved> {
    const response = await apiFetch(
      `${ws()}/variants/${variantId}/notification-overrides`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides }),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({
        error: 'Failed to save notification overrides',
      }))
      throw new Error(err.message || err.error || 'Failed to save notification overrides')
    }
    return response.json()
  },
}

/** Resolved view of a variant's Slack notification routing (#1156).
 * Wire shape from `GET .../notification-overrides/resolved` and
 * `PATCH .../notification-overrides`. Feeds the Notifications
 * section's inheritance hint AND its overrides table — the same
 * shape backs both the read and the post-save refresh. */
export interface VariantNotificationOverridesResolved {
  /** True when the parent App has SOMETHING set on its `slack`
   * binding (channel and/or events). Used by the frontend to decide
   * whether to render the empty-state branch with the "Set up the
   * Slack default on the app's Integrations tab" pointer vs the
   * normal Notifications panel. */
  appHasSlackBinding: boolean
  /** App-level default — channelId + the set of events it
   * subscribes to. `null` when the app's binding is partial (a
   * channel without any subscribed events, or vice versa) — the
   * inheritance hint shows the empty-state branch in that case. */
  appDefault: {
    channelId: string
    events: AppSlackEventSubscription[]
  } | null
  /** Per-event overrides on this variant. Each row replaces the app
   * default for its specific event; an override for an event the app
   * default doesn't subscribe to ALSO fires (the variant opts INTO
   * that event). */
  overrides: Array<{
    event: AppSlackEventSubscription
    channelId: string
  }>
  /** The parent app's id — useful so the empty-state branch can
   * deep-link the operator to the App Integrations tab where the
   * Slack default lives. `null` when the variant has no app FK
   * (shouldn't happen for live variants — defensive shape). */
  applicationId: string | null
}

// ============================================
// Test Plans API
// ============================================

export const testPlansAPI = {
  async getByApp(applicationId: string): Promise<TestPlan[]> {
    const response = await apiFetch(`${ws()}/test-plans?applicationId=${applicationId}`)
    if (!response.ok) throw new Error('Failed to fetch test plans')
    const data = await response.json()
    return normalizeIds<TestPlan>(data)
  },

  async get(applicationId: string, variantId: string): Promise<TestPlan | null> {
    const response = await apiFetch(`${ws()}/test-plans/${applicationId}/${variantId}`)
    if (response.status === 404) return null
    if (!response.ok) throw new Error('Failed to fetch test plan')
    const data = await response.json()
    return normalizeId<TestPlan>(data)
  },

  async getTestCases(applicationId: string, variantId: string): Promise<PlanTestCase[]> {
    const response = await apiFetch(`${ws()}/test-plans/${applicationId}/${variantId}/test-cases`)
    if (!response.ok) throw new Error('Failed to fetch plan test cases')
    const data = await response.json()
    return normalizeIds<PlanTestCase>(data)
  },

  async skipTestCase(applicationId: string, variantId: string, testCaseId: string): Promise<void> {
    const response = await apiFetch(`${ws()}/test-plans/${applicationId}/${variantId}/skip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ testCaseId }),
    })
    if (!response.ok) throw new Error('Failed to skip test case')
  },

  async clear(applicationId: string, variantId: string, runId: string): Promise<void> {
    const response = await apiFetch(`${ws()}/test-plans/${applicationId}/${variantId}/clear`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ runId }),
    })
    if (!response.ok) throw new Error('Failed to clear test plan')
  },

  async deleteTestCase(applicationId: string, variantId: string, testCaseId: string): Promise<void> {
    const response = await apiFetch(`${ws()}/test-plans/${applicationId}/${variantId}/test-case/${testCaseId}`, {
      method: 'DELETE',
    })
    if (!response.ok) throw new Error('Failed to delete test case')
  },

  async updateTestCase(applicationId: string, variantId: string, testCaseId: string, updates: Partial<{ categories: string[]; priority: string; intent: string; role: string; type: 'test' | 'fixture'; dependencies: string[] }>): Promise<void> {
    const response = await apiFetch(`${ws()}/test-plans/${applicationId}/${variantId}/test-case/${testCaseId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
    if (!response.ok) throw new Error('Failed to update test case')
  },

  async delete(applicationId: string, variantId: string): Promise<void> {
    const response = await apiFetch(`${ws()}/test-plans/${applicationId}/${variantId}`, {
      method: 'DELETE',
    })
    if (!response.ok) throw new Error('Failed to delete test plan')
  },
}

// ============================================
// Test Cases API
// ============================================

/** Mirrors the server's `CommitPhase` union (historyRead.ts). Keep
 * the strings in sync; the filter chips + phase-pill colors are
 * keyed on these values. */
export type CommitPhase =
  | 'planner'
  | 'generator'
  | 'executor'
  | 'healer'
  | 'system'
  | 'manual-anon'
  | 'manual'

export interface TestCaseHistoryEntry {
  sha: string
  fullSha: string
  message: string
  body: string
  /** ISO 8601 commit date. */
  date: string
  authorName: string
  authorEmail: string
  phase: CommitPhase
}

export interface TestCaseRevision {
  sha: string
  scriptCode: string | null
  yamlContent: string | null
}

/** Response shape from `testCasesAPI.update` and `testCasesAPI.restore`
 * after #596 retired the merged endpoint. The server returns the
 * canonical TestCase row plus the script's `scriptCode` separately,
 * rather than a flattened "merged" view. Frontend callers re-derive
 * the merged shape via `mergeTestCases` (see types/index.ts). */
export interface TestCaseUpdateResponse {
  testCase: import('@/types').PlanTestCase
  scriptCode: string | null
}

/** Body shape for PUT /api/test-cases/:id. After #596 the legacy
 * `steps` / `expectedResult` fields are gone — `mcpSteps` is the
 * canonical step representation. */
export interface TestCaseUpdateBody {
  name?: string
  description?: string
  mcpSteps?: import('@/types').McpStep[]
  scriptCode?: string
}

export const testCasesAPI = {
  /** Update a test case + its associated TestScript in one atomic
   * commit on the per-app repo's main branch. Body shape:
   * `{name?, description?, mcpSteps?, scriptCode?}` — unspecified
   * fields are left alone. When `commit` is supplied, the server
   * attributes the git commit to the signed-in user. */
  async update(
    id: string,
    patch: TestCaseUpdateBody,
    commit?: { message: string; details?: string },
  ): Promise<TestCaseUpdateResponse> {
    const body: Record<string, unknown> = { ...patch }
    if (commit) {
      body.commitMessage = commit.message
      if (commit.details) body.commitDetails = commit.details
    }
    const response = await apiFetch(`${ws()}/test-cases/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as { error?: string }
      throw new Error(err.error || 'Failed to update test case')
    }
    return response.json()
  },

  /** Re-check a sign-in-blocked test right now (#1978). The server
   * recomposes the sign-in inputs and runs the one preflight, then clears
   * the block if it passes or refreshes the reason if it still can't. Only
   * valid for a sign-in block — the server refuses anything else. */
  async retry(id: string): Promise<{ testCase: PlanTestCase | null; canRetrySignInBlock: boolean }> {
    const response = await apiFetch(`${ws()}/test-cases/${id}/retry`, { method: 'POST' })
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as { error?: string }
      throw new Error(err.error || 'Failed to re-check sign-in')
    }
    const data = await response.json() as { testCase: unknown; canRetrySignInBlock: boolean }
    // Normalize `_id` → `id` on the returned row so a caller reading
    // `result.testCase.id` gets a value (the server sends `_id`).
    return {
      testCase: data.testCase ? normalizeId<PlanTestCase>(data.testCase) : null,
      canRetrySignInBlock: data.canRetrySignInBlock,
    }
  },

  /** Per-test-case version history from the per-app git repo. Newest
   * commit first. Empty array when the repo doesn't exist yet (app
   * created but no run materialized). */
  async getHistory(id: string): Promise<TestCaseHistoryEntry[]> {
    const response = await apiFetch(`${ws()}/test-cases/${id}/history`)
    if (!response.ok) throw new Error('Failed to fetch test case history')
    return response.json()
  },

  /** Canonical yaml + spec contents at a specific commit. Accepts
   * short or full sha; the response carries the full sha back so the
   * caller can re-key against it. 404 → null. */
  async getRevision(id: string, sha: string): Promise<TestCaseRevision | null> {
    const response = await apiFetch(`${ws()}/test-cases/${id}/revisions/${sha}`)
    if (response.status === 404) return null
    if (!response.ok) throw new Error('Failed to fetch revision')
    return response.json()
  },

  /** Restore the script content from a previous revision. The English
   * plan (.yaml) is left untouched by design; if it has drifted from
   * current the UI surfaces that in the restore dialog. `commit` is
   * required — restore must carry a forensic message. */
  async restore(
    id: string,
    sha: string,
    commit: { message: string; details?: string },
  ): Promise<TestCaseUpdateResponse> {
    const body: Record<string, unknown> = { commitMessage: commit.message }
    if (commit.details) body.commitDetails = commit.details
    const response = await apiFetch(`${ws()}/test-cases/${id}/restore/${sha}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as { error?: string }
      throw new Error(err.error || 'Failed to restore revision')
    }
    // Same _id → id normalization as `update`. Copilot review on PR #604.
    const raw = await response.json() as { testCase: unknown; scriptCode: string | null }
    return {
      testCase: normalizeId<import('@/types').PlanTestCase>(raw.testCase),
      scriptCode: raw.scriptCode,
    }
  },

  /** Bulk-delete every TestCase + cascaded TestScript for an
   * application. Used by the "Delete all tests" affordance on the
   * Tests page. The server cascades suite-membership pointers + the
   * per-app canonical files on the way out. */
  async deleteAllByApp(applicationId: string): Promise<void> {
    const response = await apiFetch(`${ws()}/test-cases?applicationId=${applicationId}`, {
      method: 'DELETE',
    })
    if (!response.ok) throw new Error('Failed to delete test cases')
  },
}

// ============================================
// Test Scripts API — raw TestScript CRUD
// ============================================
//
// The canonical client for script content + health after #596 retired
// the merged `/api/test-cases` GET endpoint. Pair with `testPlansAPI`
// for plan-side English content + identity, and merge client-side
// via `mergeTestCases` when a flat per-test view is needed.

export interface TestScriptSummary {
  _id: string
  applicationId: string
  variantId: string
  testCaseId: string
  name: string
  scriptCode: string
  hash: string
  createdByRunId: string
  createdAt: string
  updatedAt: string
  healthStatus?: 'unknown' | 'passing' | 'failing' | 'blocked'
  lastPassedAt?: string
  lastFailedAt?: string
  lastExecutedAt?: string
  /** How many times a fix has been attempted on this script, successful
   * or not (#2194). Shown under Run and fix so an operator can see that
   * the last two attempts failed before starting a third. */
  healAttempts?: number
  /** How many of those attempts worked and were saved. */
  healSuccesses?: number
}

/** One attempt to fix a script, as the Fix history panel shows it. */
export interface FixTry {
  runId: string
  /** ISO 8601. */
  healedAt: string
  /** The test's error that started the attempt. */
  failureReason: string
  /** The healer's own explanation of what it changed. */
  changesDescription: string
  /** True when the fixed script passed and was saved. */
  wasSuccessful: boolean
}

export const testScriptsAPI = {
  async list(opts: { applicationId?: string; variantId?: string } = {}): Promise<TestScriptSummary[]> {
    const params = new URLSearchParams()
    if (opts.applicationId) params.set('applicationId', opts.applicationId)
    if (opts.variantId) params.set('variantId', opts.variantId)
    const qs = params.toString()
    const response = await apiFetch(`${ws()}/test-scripts${qs ? `?${qs}` : ''}`)
    if (!response.ok) throw new Error('Failed to fetch test scripts')
    return response.json()
  },

  /** Every attempt to fix one script, newest first. */
  async getFixes(scriptId: string): Promise<FixTry[]> {
    const response = await apiFetch(`${ws()}/test-scripts/${encodeURIComponent(scriptId)}/fixes`)
    if (!response.ok) throw new Error('Failed to load the fix history')
    return response.json()
  },
}

// ============================================
// Test Suites API
// ============================================

export const testSuitesAPI = {
  async getAll(): Promise<TestSuite[]> {
    const response = await apiFetch(`${ws()}/test-suites`)
    if (!response.ok) throw new Error('Failed to fetch test suites')
    const data = await response.json()
    return normalizeIds<TestSuite>(data)
  },

  /** Scoped to a single variant. Suites carry a variantId; without
   * filtering you'd see cross-variant suites under whichever variant is
   * selected. Variant id is now required. */
  async getByAppAndVariant(applicationId: string, variantId: string): Promise<TestSuite[]> {
    const response = await apiFetch(`${ws()}/test-suites?applicationId=${applicationId}&variantId=${variantId}`)
    if (!response.ok) throw new Error('Failed to fetch test suites')
    const data = await response.json()
    return normalizeIds<TestSuite>(data)
  },

  async getById(id: string): Promise<TestSuite | null> {
    const response = await apiFetch(`${ws()}/test-suites/${id}`)
    if (response.status === 404) return null
    if (!response.ok) throw new Error('Failed to fetch test suite')
    const data = await response.json()
    return normalizeId<TestSuite>(data)
  },

  async create(suite: Omit<TestSuite, 'id' | 'createdAt' | 'updatedAt'>): Promise<TestSuite> {
    const response = await apiFetch(`${ws()}/test-suites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(suite),
    })
    if (!response.ok) throw new Error('Failed to create test suite')
    const data = await response.json()
    return normalizeId<TestSuite>(data)
  },

  async update(id: string, suite: Partial<TestSuite>): Promise<TestSuite> {
    const response = await apiFetch(`${ws()}/test-suites/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(suite),
    })
    if (!response.ok) throw new Error('Failed to update test suite')
    const data = await response.json()
    return normalizeId<TestSuite>(data)
  },

  async delete(id: string, opts?: CiProtectedDeleteOpts): Promise<void> {
    const response = await apiFetch(`${ws()}/test-suites/${id}${ciProtectedQuery(opts)}`, {
      method: 'DELETE',
      headers: ciProtectedHeaders(opts),
    })
    if (response.status === 409) {
      throw await CiProtectedError.fromResponse(response)
    }
    if (!response.ok) throw new Error('Failed to delete test suite')
  },

  async restore(id: string): Promise<TestSuite> {
    const response = await apiFetch(`${ws()}/test-suites/${id}/restore`, { method: 'POST' })
    if (!response.ok) throw new Error('Failed to restore test suite')
    const data = await response.json()
    return normalizeId<TestSuite>(data)
  },
}

// ============================================
// CI/CD Integrations API (#642 Phase A2)
// ============================================

/** Provider enum mirrors the backend's `CI_INTEGRATION_PROVIDERS`.
 * Kept as a string-literal union locally so the UI can render the
 * provider select without a round-trip on every render. */
export type CiIntegrationProvider = 'webhook' | 'github' | 'gitlab' | 'azureDevops'

/** Redacted view of a CI integration — what GET endpoints return.
 * The HMAC secret is NEVER included; `secretSet` is a boolean
 * indicating whether one is stored. */
export interface CiIntegration {
  id: string
  workspaceId: string
  provider: CiIntegrationProvider
  callbackUrl?: string
  secretSet: boolean
  preferences?: { fireStartedEvent?: boolean }
  providerConfig?: Record<string, unknown>
  lastUsedAt?: string
  createdAt: string
  updatedAt: string
}

/** Returned by POST create (when secret is server-generated) and POST
 * rotate-secret. The plaintext is visible ONCE — the UI surfaces it
 * in a reveal-once banner and then drops it from state when the user
 * dismisses. Mirrors the AccountTokens fresh-token shape. */
export interface CiIntegrationWithSecret extends CiIntegration {
  secret: string
  secretRevealedOnce: true
}

export interface CreateCiIntegrationInput {
  provider: CiIntegrationProvider
  callbackUrl?: string
  /** Omit to have the server auto-generate (response includes the
   * plaintext once). Supply to use a customer-chosen value (response
   * is redacted since the caller already knows the plaintext). */
  secret?: string
  preferences?: { fireStartedEvent?: boolean }
  providerConfig?: Record<string, unknown>
}

export const ciIntegrationsAPI = {
  async list(): Promise<CiIntegration[]> {
    const response = await apiFetch(`${ws()}/ci-integrations`)
    if (!response.ok) throw new Error('Failed to list CI integrations')
    return response.json()
  },

  /** Returns either a `CiIntegrationWithSecret` (server-generated
   * secret path, plaintext included) or a plain `CiIntegration`
   * (caller-supplied secret path, redacted). The discriminator is
   * `secretRevealedOnce`. */
  async create(input: CreateCiIntegrationInput): Promise<CiIntegration | CiIntegrationWithSecret> {
    const response = await apiFetch(`${ws()}/ci-integrations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to create CI integration')
    }
    return response.json()
  },

  async update(id: string, patch: Partial<CreateCiIntegrationInput>): Promise<CiIntegration> {
    const response = await apiFetch(`${ws()}/ci-integrations/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to update CI integration')
    }
    return response.json()
  },

  /** Disconnects GitHub from the workspace: the issue connections signed in
   * through the App, then the App install record. Safe to repeat, so a
   * disconnect that failed partway finishes on the next try. A connection
   * signed in with a personal token is kept. */
  async disconnectGitHub(): Promise<void> {
    const response = await apiFetch(`${ws()}/ci-integrations/github`, { method: 'DELETE' })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to disconnect GitHub')
    }
  },

  /** Rotates the HMAC secret. Returns the new plaintext ONCE — call
   * sites surface in a reveal-once banner. After this call returns
   * the old secret is invalidated; the customer must update their
   * receiver to verify against the new value. */
  async rotateSecret(id: string): Promise<CiIntegrationWithSecret> {
    const response = await apiFetch(`${ws()}/ci-integrations/${id}/rotate-secret`, { method: 'POST' })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to rotate CI integration secret')
    }
    return response.json()
  },

  async delete(id: string): Promise<void> {
    const response = await apiFetch(`${ws()}/ci-integrations/${id}`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Failed to delete CI integration')
  },
}

// ============================================
// Issue-tracker connections API (#1558 Slice 1a)
// ============================================

/** Which inbound tracker a connection is for, e.g. `'jira'`. A string, not
 * a fixed list: the frontend never names a tracker, it reads the tracker's
 * descriptor (below) for its name, logo, words and form fields. */
export type IssueTrackerProvider = string

/** One credential field the connect form renders. `secret` fields are
 * write-only + masked; the value is never returned by any GET. */
export interface IssueTrackerCredentialField {
  key: string
  label: string
  secret?: boolean
}

/** A provider's self-description — drives the connect UI so it doesn't
 * branch on provider name. */
export interface IssueTrackerProviderDescriptor {
  provider: string
  /** The tracker's name as people say it, e.g. "Jira". */
  displayName: string
  /** What a connection to the tracker is called, when that differs from its
   * name ("GitHub Issues"). Read through `connectionNameOf`. */
  connectionName?: string
  /** Where the sign-in comes from when it isn't typed in, e.g. the
   * workspace's GitHub App install. A tracker with this and typed-in fields
   * offers both, the install first. */
  credentialSource?: string
  /** Content fields whose ids start with this are the issue's own body
   * sections, named by their headings. */
  strictFieldPrefix?: string
  /** Which logo `TrackerLogo` draws; unknown keys get a plain mark. */
  logoKey: string
  /** What the tracker calls a group of issues, singular and lowercase. */
  projectNoun: string
  /** What the tracker calls a kind of issue, singular and lowercase. */
  issueTypeNoun: string
  /** The scope filter's language; picks the builder's translator. Absent ⇒
   * a plain text box. */
  filterLanguage?: string
  capabilities: { inboundIssues: boolean }
  classificationScheme: 'issueType' | 'label'
  credentialSchema: IssueTrackerCredentialField[]
  pagination: 'cursor'
}

/** The ways a tracker that offers a choice signs in: through the workspace's
 * app install, or with a token typed in on the card. */
export type IssueTrackerSignIn = 'app' | 'token'

/** Redacted view of a connection — what GET endpoints return. The secret
 * credential (Jira: API token) is NEVER included; `hasSecret` indicates
 * one is stored. */
export interface IssueTrackerConnection {
  id: string
  provider: IssueTrackerProvider
  providerConfig?: Record<string, unknown>
  hasSecret: boolean
  /** How it signs in, for a tracker that offers a choice. Read through
   * `signInOf`: absent means the tracker's default. */
  signIn?: IssueTrackerSignIn
  /** When stage-1 validation last succeeded — shown as "last checked". */
  lastValidatedAt?: string
  createdAt: string
  updatedAt: string
}

/** Typed stage-1 validation result. Distinguishes bad credentials from an
 * unreachable host so the UI shows a specific message, never a bare
 * "connection failed". */
export type IssueTrackerValidationResult =
  | { ok: true }
  | {
      ok: false
      reason: 'invalid_credentials' | 'unreachable' | 'network' | 'not_installed' | 'missing_permission'
      message: string
    }

/** Connect/update outcome. On a validation failure the connection is NOT
 * saved and the typed reason comes back for the UI to render. */
export type IssueTrackerConnectResult =
  | { ok: true; connection: IssueTrackerConnection }
  | { ok: false; validation: Extract<IssueTrackerValidationResult, { ok: false }> }

export const issueTrackersAPI = {
  /** The workspace's connections (redacted). */
  async list(): Promise<IssueTrackerConnection[]> {
    const response = await apiFetch(`${ws()}/issue-trackers`)
    if (!response.ok) throw new Error('Failed to load issue-tracker connections')
    return response.json()
  },

  /** The inbound providers a user can connect + their credential schema. */
  async listProviders(): Promise<IssueTrackerProviderDescriptor[]> {
    const response = await apiFetch(`${ws()}/issue-trackers/providers`)
    if (!response.ok) throw new Error('Failed to load issue-tracker providers')
    return response.json()
  },

  /** Connect a tracker: validates the credentials, then persists on
   * success. A `422` (validation failure — not saved) resolves to
   * `{ ok: false, validation }` rather than throwing, so the caller can
   * show the typed reason inline. */
  async connect(input: {
    provider: string
    /** For a tracker that offers a choice; absent means its default. */
    signIn?: IssueTrackerSignIn
    credentials: Record<string, string>
  }): Promise<IssueTrackerConnectResult> {
    const response = await apiFetch(`${ws()}/issue-trackers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (response.status === 422) {
      return { ok: false, validation: await response.json() }
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to connect the issue tracker')
    }
    return { ok: true, connection: await response.json() }
  },

  /** Re-check a stored connection (stage-1). Always returns the typed
   * result when the connection exists. */
  async validate(id: string): Promise<IssueTrackerValidationResult> {
    const response = await apiFetch(`${ws()}/issue-trackers/${id}/validate`, { method: 'POST' })
    if (!response.ok) throw new Error('Failed to check the connection')
    return response.json()
  },

  /** Update a connection's credentials (rotate the token / change base
   * URL). Re-validates the merged credentials; a `422` resolves to
   * `{ ok: false, validation }` and nothing is saved. */
  async updateCredentials(
    id: string,
    credentials: Record<string, string>,
  ): Promise<IssueTrackerConnectResult> {
    const response = await apiFetch(`${ws()}/issue-trackers/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credentials }),
    })
    if (response.status === 422) {
      return { ok: false, validation: await response.json() }
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to update the connection')
    }
    return { ok: true, connection: await response.json() }
  },

  /** Move a token connection onto the workspace's app install. The server
   * checks the install first; a `422` resolves to `{ ok: false, validation }`
   * and nothing changes. On success the stored token is deleted. */
  async switchToApp(id: string): Promise<IssueTrackerConnectResult> {
    const response = await apiFetch(`${ws()}/issue-trackers/${id}/sign-in`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ signIn: 'app' }),
    })
    if (response.status === 422) {
      return { ok: false, validation: await response.json() }
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to switch to the app install')
    }
    return { ok: true, connection: await response.json() }
  },

  async delete(id: string): Promise<void> {
    const response = await apiFetch(`${ws()}/issue-trackers/${id}`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Failed to disconnect the issue tracker')
  },

  /** The projects a stored connection can read, for the connected card's
   * "Can read issues in N repositories" line. */
  async listProjects(id: string): Promise<IssueSourceProject[]> {
    const response = await apiFetch(`${ws()}/issue-trackers/${id}/projects`)
    if (!response.ok) throw new Error('Failed to load projects')
    return response.json()
  },
}

// ============================================
// GitHub integration details API (#1091 / #1092)
// ============================================

/** Normalized account shape returned by the installation-details
 * endpoint. Field names mirror the server's `GitHubAccount` type;
 * no Octokit namespace leaks to the frontend. */
export interface GitHubInstallationAccount {
  login: string
  avatarUrl: string
  type: 'User' | 'Organization'
}

/** Normalized repository shape. */
export interface GitHubInstallationRepo {
  owner: string
  name: string
  ownerAvatarUrl: string
  htmlUrl: string
}

/** Full shape returned by `GET /workspaces/:id/github/installations/:id/details`. */
export interface GitHubInstallationDetails {
  account: GitHubInstallationAccount
  repos: GitHubInstallationRepo[]
  /** The install's permissions, e.g. `{ issues: 'read' }`. Cached on the
   * server for five minutes, so an approval shows up within that. */
  permissions?: Record<string, string>
}

/** GitHub confirmed the App install is gone (the details call answered 410),
 * as opposed to GitHub being unreachable. */
export class InstallationGoneError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InstallationGoneError'
  }
}

export const githubInstallationAPI = {
  /** Fetch the normalized account + repo list for a GitHub App installation.
   * Workspace-scoped. Throws if the installation is not owned by this
   * workspace (403) or if GitHub is unreachable (502). */
  async getDetails(
    workspaceId: string,
    installationId: number,
  ): Promise<GitHubInstallationDetails> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${workspaceId}/github/installations/${installationId}/details`,
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({
        error: `Unable to fetch installation details (${response.status})`,
      }))
      const message = typeof body.error === 'string'
        ? body.error
        : `Unable to fetch installation details (${response.status})`
      // 410: GitHub confirmed the App was uninstalled. Any other failure
      // means GitHub couldn't be asked, which says nothing either way.
      throw response.status === 410 ? new InstallationGoneError(message) : new Error(message)
    }
    return response.json()
  },

  /** Return the URL for the re-authorize endpoint (workspace-scoped).
   * The caller redirects the browser to this URL; the backend 302s to
   * GitHub's configure-repos page for the specific installation so the
   * operator can adjust which repositories OttoTester can access.
   *
   * Returns a URL string rather than doing the redirect so callers can
   * set a loading state before handing off to the browser. */
  reauthorizeUrl(workspaceId: string, installationId: number): string {
    return `${API_BASE}/workspaces/${workspaceId}/github/installations/${installationId}/reauthorize`
  },
}

// ============================================
// Test Runs API
// ============================================

export const testRunsAPI = {
  async getAll(page = 1, limit = 20): Promise<{ runs: TestRun[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
    const response = await apiFetch(`${ws()}/test-runs?page=${page}&limit=${limit}`)
    if (!response.ok) throw new Error('Failed to fetch test runs')
    const data = await response.json()
    return {
      runs: normalizeIds<TestRun>(data.runs),
      pagination: data.pagination,
    }
  },

  async getById(id: string): Promise<TestRun | null> {
    const response = await apiFetch(`${ws()}/test-runs/${id}`)
    if (response.status === 404) return null
    if (!response.ok) throw new Error('Failed to fetch test run')
    const data = await response.json()
    return normalizeId<TestRun>(data)
  },

  async getLogs(id: string, opts?: { offset?: number; limit?: number; level?: 'info' | 'warn' | 'error' | 'debug' }): Promise<{
    logs: Array<{ timestamp: string; level: 'info' | 'warn' | 'error' | 'debug'; message: string; details?: string; agent?: 'planner' | 'generator' | 'executor' | 'healer' | 'extractor' | 'auditor' | 'rag' | 'system' }>
    total: number
    offset: number
    limit: number
  }> {
    const params = new URLSearchParams()
    if (opts?.offset !== undefined) params.set('offset', String(opts.offset))
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit))
    if (opts?.level) params.set('level', opts.level)
    const qs = params.toString() ? `?${params.toString()}` : ''
    const response = await apiFetch(`${ws()}/test-runs/${id}/logs${qs}`)
    if (!response.ok) throw new Error('Failed to fetch test run logs')
    return response.json()
  },

  async create(run: TestRun): Promise<TestRun> {
    // Send id as _id so MongoDB uses our UUID instead of generating an ObjectId
    const { id, ...rest } = run
    const response = await apiFetch(`${ws()}/test-runs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ _id: id, ...rest }),
    })
    if (!response.ok) throw new Error('Failed to create test run')
    const data = await response.json()
    return normalizeId<TestRun>(data)
  },

  async update(id: string, run: Partial<TestRun>): Promise<TestRun> {
    const response = await apiFetch(`${ws()}/test-runs/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(run),
    })
    if (!response.ok) throw new Error('Failed to update test run')
    const data = await response.json()
    return normalizeId<TestRun>(data)
  },

  /** Replace a run's tag map — backs the post-hoc tag editor (#747).
   * Surfaces the server's `error` message (e.g. an invalid-tag-key
   * rejection) so the editor can show it verbatim instead of a
   * generic failure. */
  async updateTags(id: string, tags: Record<string, string>): Promise<TestRun> {
    const response = await apiFetch(`${ws()}/test-runs/${id}/tags`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tags }),
    })
    if (!response.ok) {
      const body = await response.json().catch(() => null)
      throw new Error(body?.error || `Failed to update run tags (${response.status})`)
    }
    const data = await response.json()
    return normalizeId<TestRun>(data)
  },

  async delete(id: string): Promise<void> {
    const response = await apiFetch(`${ws()}/test-runs/${id}`, {
      method: 'DELETE',
    })
    if (!response.ok) throw new Error('Failed to delete test run')
  },

  async getByApp(appId: string): Promise<TestRun[]> {
    const response = await apiFetch(`${ws()}/test-runs/by-app/${appId}`)
    if (!response.ok) throw new Error('Failed to fetch test runs')
    const data = await response.json()
    return normalizeIds<TestRun>(data)
  },
}

// ============================================
// Hints API
// ============================================

export interface Hint {
  id: string
  variantId: string
  source: string
  category: string
  element?: string
  selector?: string
  pattern?: string
  antipattern?: string
  sourceRunId: string
  sourceTestName?: string
  confirmationCount: number
  lastConfirmedAt: string
  failureCount: number
  injectedCount: number
  adoptedCount: number
  effectiveCount: number
  /** Beta-smoothed effective/adopted efficacy, in (0,1). Computed
   * server-side — matches the Analytics Hint Efficacy report. */
  efficacyRate: number
  /** Rated/unrated efficacy classification, server-computed. */
  bucket: 'effective' | 'deadWeight' | 'unrated'
  /** Combined adopted/injected, or null when never injected. Kept for
   * back-compat; prefer `healAdoptionRate` — the combined rate divides
   * heal-only adoptions by all-phase injections and under-reports (#1656). */
  adoptionRate: number | null
  /** Honest heal-phase adoption rate, or null when never injected during
   * a heal. */
  healAdoptionRate: number | null
  /** Honest generator-phase adoption rate, or null when never injected
   * during generation (null until the generator auditor ships, #1657). */
  generatorAdoptionRate: number | null
  /** Per-phase heal counters (#1656) — the heal track record. Absent on
   * pre-#1656 rows until the boot backfill runs; read with `|| 0`. */
  injectedCountHealer?: number
  adoptedCountHealer?: number
  effectiveCountHealer?: number
  /** Per-phase generator counters (#1657) — the generation track record.
   * Absent until the generator adoption auditor records them; read with
   * `|| 0`. */
  injectedCountGenerator?: number
  adoptedCountGenerator?: number
  effectiveCountGenerator?: number
  deactivationReason?: string
  active: boolean
  createdAt: string
}

export const hintsAPI = {
  async getByVariant(variantId: string, includeInactive = false): Promise<Hint[]> {
    const qs = includeInactive ? '&all=true' : ''
    const response = await apiFetch(`${ws()}/hints?variantId=${variantId}${qs}`)
    if (!response.ok) throw new Error('Failed to fetch hints')
    const data = await response.json()
    return normalizeIds<Hint>(data)
  },

  async create(hint: { variantId: string; category: string; element?: string; selector?: string; pattern?: string; antipattern?: string }): Promise<{ id: string }> {
    const response = await apiFetch(`${ws()}/hints`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(hint),
    })
    if (!response.ok) throw new Error('Failed to create hint')
    return response.json()
  },

  async update(id: string, updates: { element?: string; selector?: string; pattern?: string; antipattern?: string }): Promise<void> {
    const response = await apiFetch(`${ws()}/hints/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
    if (!response.ok) throw new Error('Failed to update hint')
  },

  async deactivate(id: string): Promise<void> {
    const response = await apiFetch(`${ws()}/hints/${id}`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Failed to deactivate hint')
  },

  async reactivate(id: string): Promise<void> {
    const response = await apiFetch(`${ws()}/hints/${id}/reactivate`, { method: 'PATCH' })
    if (!response.ok) throw new Error('Failed to reactivate hint')
  },

  async deletePermanently(id: string): Promise<void> {
    const response = await apiFetch(`${ws()}/hints/${id}/permanent`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Failed to delete hint')
  },

  async reactivateAll(variantId: string): Promise<{ reactivated: number }> {
    const response = await apiFetch(`${ws()}/hints/reactivate-all`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variantId }),
    })
    if (!response.ok) throw new Error('Failed to reactivate hints')
    return response.json()
  },

  /** Hard-delete every hint for a variant — the "Delete Agent Memory"
   * affordance on the Hints page. Same shape as the other per-row
   * delete methods (deactivate / reactivate / deletePermanently) —
   * `Promise<void>`; the server returns `{ success, deleted }` but
   * the only call site doesn't consume it. */
  async deleteAllByVariant(variantId: string): Promise<void> {
    const response = await apiFetch(`${ws()}/hints?variantId=${variantId}`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Failed to delete agent memory')
  },
}

// ============================================
// Test Data API
// ============================================

// ── Unified Test Data Sets API (#1258 Slice 2) ────────────────────
//
// Workspace-scoped CRUD for the new TestDataSet primitive (catalog
// + matrix editor + CSV import + sensitive-cell reveal). Replaces
// the legacy testDataAPI surface as #1258 lands; until slice 5
// they coexist.

/** Column type values supported by the v1 matrix. Mirrors the
 * server's `TestDataSetColumnType`. */
export type TestDataSetColumnType = 'string' | 'number' | 'boolean' | 'date' | 'file'

export interface TestDataSetColumn {
  /** Stable column id (UUID), rename-safe. File cells are stored under
   * `row.fileValues[id]`, so the editor reads file cells by this, not by
   * `name` (#1413). Optional because columns created before the id field
   * existed may lack one until the server stamps it on the next read; a
   * file-bearing column always has one (the upload path stamps it). */
  id?: string
  name: string
  type: TestDataSetColumnType
  default?: string
  sensitive: boolean
  /** When true, this column gets a new, shape-preserving value each run
   * (#1677 Slice 1). Optional/additive — absent means the static
   * behavior. */
  freshPerRun?: boolean
  system: boolean
}

export interface TestDataSetRow {
  id: string
  values: Record<string, string>
  fileValues?: Record<string, FileRef>
  consumed: boolean
  /** #1800 — input-variation provenance. `variationKey` is the hidden,
   * stable class identity (never shown/edited); `label` is the editable
   * class name shown as a row annotation; `needsReview` drives the
   * "Please confirm" badge on a guessed row. All absent on plain rows. */
  variationKey?: string
  label?: string
  needsReview?: boolean
}

export interface TestDataSetSummary {
  _id: string
  name: string
  columns: TestDataSetColumn[]
  rowCount: number
  /** Count of DataBindings referencing this Set (post-merge
   * corrections on #1258 — the catalog's "Used by" column). */
  usedByCount: number
  /** User who most recently mutated the Set, resolved to a
   * display name server-side. Null on legacy Sets that predate
   * the lastModifiedBy field — the catalog falls back to the
   * raw timestamp in that case. */
  lastModifiedBy: { id: string; name: string } | null
  createdAt: string
  updatedAt: string
}

export interface TestDataSet {
  _id: string
  workspaceId: string
  name: string
  columns: TestDataSetColumn[]
  rows: TestDataSetRow[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface CsvImportSkip {
  row: number
  reason: 'type-coercion' | 'blank-key' | 'duplicate-key'
  column?: string
  detail: string
}

export interface CsvImportSummary {
  accepted: number
  skippedCount: number
  skipped: CsvImportSkip[]
  unknownHeaders: string[]
}

export const testDataSetsAPI = {
  /** List the workspace's Test Data Sets. Optional `applicationId` /
   * `variantId` narrow the catalog to one app/variant's Sets (#1413). */
  async list(opts: { applicationId?: string; variantId?: string } = {}): Promise<{ sets: TestDataSetSummary[] }> {
    const q = new URLSearchParams()
    if (opts.applicationId) q.set('applicationId', opts.applicationId)
    if (opts.variantId) q.set('variantId', opts.variantId)
    const qs = q.toString()
    const r = await apiFetch(`${ws()}/test-data-sets${qs ? `?${qs}` : ''}`)
    if (!r.ok) throw new Error(`Failed to list Test Data Sets (${r.status})`)
    return r.json()
  },

  async get(setId: string): Promise<{ set: TestDataSet }> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}`)
    if (!r.ok) throw new Error(`Failed to fetch Test Data Set (${r.status})`)
    return r.json()
  },

  /** Create a Set. `scope` stamps the owning app + variant so the new
   * Set shows up in that variant's scoped catalog view (#1413). The
   * catalog passes its current selection; omit for a workspace-level
   * orphan. */
  async create(
    name: string,
    columns: Array<Omit<TestDataSetColumn, 'system'>> = [],
    scope: { applicationId?: string; variantId?: string } = {},
  ): Promise<{ set: TestDataSet }> {
    const r = await apiFetch(`${ws()}/test-data-sets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, columns, applicationId: scope.applicationId, variantId: scope.variantId }),
    })
    if (!r.ok) {
      const detail = await r.text()
      throw new Error(`Failed to create Test Data Set: ${detail || r.statusText}`)
    }
    return r.json()
  },

  async rename(setId: string, name: string): Promise<void> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/name`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    })
    if (!r.ok) throw new Error('Failed to rename Test Data Set')
  },

  async delete(setId: string): Promise<{ orphanedBindings: number }> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}`, { method: 'DELETE' })
    if (!r.ok) throw new Error('Failed to delete Test Data Set')
    return r.json()
  },

  async addColumn(setId: string, input: { name: string; type: TestDataSetColumnType; default?: string; sensitive?: boolean; freshPerRun?: boolean }): Promise<void> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/columns`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!r.ok) {
      const detail = await r.text()
      throw new Error(`Failed to add column: ${detail || r.statusText}`)
    }
  },

  async patchColumn(setId: string, colName: string, patch: { newName?: string; newType?: TestDataSetColumnType; default?: string | null; freshPerRun?: boolean }): Promise<void> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/columns/${encodeURIComponent(colName)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!r.ok) {
      const detail = await r.text()
      throw new Error(`Failed to update column: ${detail || r.statusText}`)
    }
  },

  /** Preview one example of what a "new value each run" column would
   * produce for the given seed + type (#1677 Slice 1). Calls the same
   * server helper the runtime uses, so the sample matches real behavior;
   * each call returns a different sample. */
  async previewFreshSample(setId: string, input: { type: TestDataSetColumnType; seed: string }): Promise<string> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/columns/preview-fresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!r.ok) {
      const detail = await r.text()
      throw new Error(`Failed to preview sample: ${detail || r.statusText}`)
    }
    const data = (await r.json()) as { sample: string }
    return data.sample
  },

  async deleteColumn(setId: string, colName: string): Promise<void> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/columns/${encodeURIComponent(colName)}`, {
      method: 'DELETE',
    })
    if (!r.ok) throw new Error('Failed to delete column')
  },

  async addRow(setId: string, values: Record<string, string> = {}): Promise<{ rowId: string }> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/rows`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values }),
    })
    if (!r.ok) throw new Error('Failed to add row')
    return r.json()
  },

  async updateRow(setId: string, rowId: string, values: Record<string, string>): Promise<void> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/rows/${rowId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values }),
    })
    if (!r.ok) throw new Error('Failed to update row')
  },

  async deleteRow(setId: string, rowId: string): Promise<void> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/rows/${rowId}`, { method: 'DELETE' })
    if (!r.ok) throw new Error('Failed to delete row')
  },

  async setRowConsumed(setId: string, rowId: string, consumed: boolean): Promise<void> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/rows/${rowId}/consumed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ consumed }),
    })
    if (!r.ok) throw new Error('Failed to update $CONSUMED')
  },

  /** #1800 — update a row's input-variation metadata: rename its class
   * `label` and/or clear the `needsReview` "Please confirm" flag. The
   * hidden variationKey is never writable. */
  async updateRowVariation(
    setId: string,
    rowId: string,
    meta: { label?: string; needsReview?: boolean },
  ): Promise<void> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/rows/${rowId}/variation`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(meta),
    })
    if (!r.ok) throw new Error('Failed to update row')
  },

  async resetConsumed(setId: string): Promise<{ reset: number }> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/reset-consumed`, { method: 'POST' })
    if (!r.ok) throw new Error('Failed to reset $CONSUMED')
    return r.json()
  },

  async consumedSummary(setId: string): Promise<{ consumed: number; total: number }> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/consumed-summary`)
    if (!r.ok) throw new Error('Failed to read consumed summary')
    return r.json()
  },

  async importCsv(
    setId: string,
    input: { mode: 'append' | 'replace' | 'merge'; headers: string[]; rows: Array<Record<string, string>>; keyColumn?: string },
  ): Promise<{
    summary: CsvImportSummary
    write: { appended?: number; updated?: number; total: number }
  }> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/csv`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!r.ok) {
      const text = await r.text()
      throw new Error(text || `Failed to import CSV (${r.status})`)
    }
    return r.json()
  },

  exportCsvUrl(setId: string): string {
    // The export URL hits the same authenticated endpoint; the
    // browser sends cookies for the GET via a link click. Used as
    // an `<a href>` target so the download stream flows through
    // the browser instead of the SPA fetch buffer.
    return `${ws()}/test-data-sets/${setId}/csv`
  },

  async recordReveal(setId: string, rowId: string, columnName: string): Promise<void> {
    const r = await apiFetch(`${ws()}/test-data-sets/${setId}/audit/cell-reveal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rowId, columnName }),
    })
    if (!r.ok) {
      // Reveal-audit failures must not block the UI surface — log
      // and move on. The audit gap is a recovery concern for ops,
      // not a UX failure.
      console.warn('[testDataSets] failed to record reveal audit', await r.text())
    }
  },

  async uploadFile(setId: string, rowId: string, columnName: string, file: File): Promise<FileRef> {
    const form = new FormData()
    form.append('file', file)
    const r = await apiFetch(
      `${ws()}/test-data-sets/${setId}/rows/${rowId}/cells/${encodeURIComponent(columnName)}/file`,
      { method: 'POST', body: form },
    )
    if (!r.ok) {
      // Show the server's plain message (for example "This file is being
      // cleaned up. Upload it again in a minute.") rather than raw JSON.
      const text = await r.text()
      let readable = text
      try {
        const body = JSON.parse(text) as { message?: string; error?: string }
        readable = body.message || body.error || text
      } catch {
        // Not JSON: show the text as it came.
      }
      throw new Error(readable || `Failed to upload file cell (${r.status})`)
    }
    const data = await r.json()
    return data.file as FileRef
  },

  async clearFile(setId: string, rowId: string, columnName: string): Promise<void> {
    const r = await apiFetch(
      `${ws()}/test-data-sets/${setId}/rows/${rowId}/cells/${encodeURIComponent(columnName)}/file`,
      { method: 'DELETE' },
    )
    if (!r.ok) throw new Error('Failed to clear file cell')
  },
}

// ── DataBinding client (#1258 Slice 3) ────────────────────────

export type DataBindingConsumption = 'reusable' | 'single-use'

/** Severity of a regeneration-diff entry (#1262 slice 4). */
export type BindingDiffSeverity = 'blocking' | 'warning' | 'informational'
export type BindingDiffKind = 'rename' | 'add' | 'remove'

export interface BindingDiffEntry {
  field: string
  kind: BindingDiffKind
  severity: BindingDiffSeverity
  previousColumn?: string
  nextColumn?: string
  message: string
}

export interface BindingPendingDiff {
  scriptId: string
  computedAt: string
  previousFieldNames: string[]
  nextFieldNames: string[]
  entries: BindingDiffEntry[]
}

export interface DataBindingDto {
  _id: string
  workspaceId: string
  scriptId: string
  setId: string | null
  fieldMappings: Record<string, string>
  overrides: Record<string, boolean>
  consumption: DataBindingConsumption
  wasOrphaned: boolean
  archived: boolean
  /** Pending regeneration diff (#1262). `null` is the steady state.
   * Non-null means a regeneration shifted the script's field shape;
   * the pending-changes view inside Test Data renders these for
   * resolution. */
  pendingDiff: BindingPendingDiff | null
  createdAt: string
  updatedAt: string
}

export interface DataBindingForSet extends DataBindingDto {
  /** The bound script's display name. Enriched by the
   * `/by-set/:setId` route so the Bound Tests table renders
   * without a second fetch. */
  scriptName: string
}

/** Script field — input vs assertion classification. The mapper UI
 * renders each as a row with a kind-specific badge. */
export interface ScriptFieldDescriptor {
  name: string
  kind: 'input' | 'assertion'
}

/** A TestScript that has no DataBinding yet — returned by
 * `listUnboundScripts` to populate the "+ Bind a test" picker.
 * Minimal shape; the picker dialog displays the name and the
 * route resolves the rest on selection. */
export interface UnboundScriptSummary {
  _id: string
  name: string
  applicationId: string
  variantId: string
  updatedAt: string
}

export const dataBindingsAPI = {
  async getByScript(scriptId: string): Promise<DataBindingDto | null> {
    const r = await apiFetch(`${ws()}/data-bindings/by-script/${scriptId}`)
    if (r.status === 404) return null
    if (!r.ok) throw new Error(`Failed to read binding (${r.status})`)
    const data = await r.json()
    return data.binding
  },

  /** List TestScripts in the workspace that don't yet have a
   * DataBinding. Used by the catalog's "+ Bind a test" picker. */
  async listUnboundScripts(): Promise<{ scripts: UnboundScriptSummary[] }> {
    const r = await apiFetch(`${ws()}/data-bindings/unbound-scripts`)
    if (!r.ok) throw new Error(`Failed to list unbound scripts (${r.status})`)
    return r.json()
  },

  async getScriptFields(scriptId: string): Promise<{
    scriptId: string
    scriptName: string
    fields: ScriptFieldDescriptor[]
  }> {
    const r = await apiFetch(`${ws()}/data-bindings/by-script/${scriptId}/script-fields`)
    if (!r.ok) throw new Error(`Failed to read script fields (${r.status})`)
    return r.json()
  },

  async listForSet(setId: string): Promise<{ bindings: DataBindingForSet[] }> {
    const r = await apiFetch(`${ws()}/data-bindings/by-set/${setId}`)
    if (!r.ok) throw new Error(`Failed to list bindings for set (${r.status})`)
    return r.json()
  },

  async create(input: {
    scriptId: string
    setId: string
    fieldMappings?: Record<string, string>
    consumption?: DataBindingConsumption
  }): Promise<DataBindingDto> {
    const r = await apiFetch(`${ws()}/data-bindings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!r.ok) {
      const detail = await r.text()
      throw new Error(`Failed to create binding: ${detail || r.statusText}`)
    }
    const data = await r.json()
    return data.binding
  },

  async updateMapper(
    bindingId: string,
    input: { fieldMappings: Record<string, string>; overrides?: Record<string, boolean> },
  ): Promise<DataBindingDto> {
    const r = await apiFetch(`${ws()}/data-bindings/${bindingId}/mapper`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!r.ok) {
      const detail = await r.text()
      throw new Error(`Failed to update mapper: ${detail || r.statusText}`)
    }
    const data = await r.json()
    return data.binding
  },

  async updateConsumption(
    bindingId: string,
    consumption: DataBindingConsumption,
  ): Promise<DataBindingDto> {
    const r = await apiFetch(`${ws()}/data-bindings/${bindingId}/consumption`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ consumption }),
    })
    if (!r.ok) {
      const detail = await r.text()
      throw new Error(`Failed to update consumption: ${detail || r.statusText}`)
    }
    const data = await r.json()
    return data.binding
  },

  async delete(bindingId: string): Promise<void> {
    const r = await apiFetch(`${ws()}/data-bindings/${bindingId}`, { method: 'DELETE' })
    if (!r.ok) throw new Error(`Failed to delete binding (${r.status})`)
  },

  // ── Pending regeneration changes (#1262 slice 4) ─────────
  async getPending(bindingId: string): Promise<{
    bindingId: string
    scriptId: string
    setId: string | null
    pendingDiff: BindingPendingDiff | null
  }> {
    const r = await apiFetch(`${ws()}/data-bindings/${bindingId}/pending`)
    if (!r.ok) throw new Error(`Failed to read pending changes (${r.status})`)
    return r.json()
  },

  async applyPending(
    bindingId: string,
    input: { nextFieldMappings: Record<string, string> },
  ): Promise<DataBindingDto> {
    const r = await apiFetch(`${ws()}/data-bindings/${bindingId}/pending/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!r.ok) {
      const detail = await r.text()
      throw new Error(`Failed to apply pending changes: ${detail || r.statusText}`)
    }
    const data = await r.json()
    return data.binding
  },

  async discardPending(bindingId: string): Promise<void> {
    const r = await apiFetch(`${ws()}/data-bindings/${bindingId}/pending/discard`, {
      method: 'POST',
    })
    if (!r.ok) throw new Error(`Failed to discard pending changes (${r.status})`)
  },
}

/** The curated sign-in helper a credential's known sign-in addresses
 * match, computed server-side at read time (#1312). Reuses the same
 * matcher the run-start resolver uses, so the Credentials card preview
 * agrees with what a run actually does. `null` on a credential means no
 * helper matched (the card falls back to its other signals).
 * `confidence` is 'high' for a strong host match, 'low' for a weaker
 * signal — the card commits on high and merely suggests on low. */
export interface DetectedSignInMethod {
  libraryId: string
  name: string
  confidence: 'high' | 'low'
}

/** Wire shape of one credential as the test-data read returns it. Bearer
 * fields are redacted to `null` for roles without permission to see
 * them; `detectedSignInMethod` is the read-only sign-in preview (#1312).
 * Loosely typed (`steps: any[]`) because the panel normalizes the step
 * shape on load — this DTO names only the fields the panel reads
 * directly off the response. */
export interface CredentialReadDTO {
  id?: string
  role?: string
  label?: string
  method?: string
  steps?: any[]
  loginFixtureBinding?: { mode: 'library' | 'auto' | 'none'; libraryId?: string; libraryVersion?: number }
  detectedSignInMethod?: DetectedSignInMethod | null
  /** Server-resolved id of the sign-in helper that will ACTUALLY run for
   * this credential (#1884 follow-up): the explicitly chosen one, or the one
   * already installed for this role while the credential is on `auto`. The
   * editor marks that helper's required login details required — an `auto`
   * credential used to show them as optional and then fail the run. Null
   * when no helper applies. Read-only; never sent back on save. */
  effectiveFixtureId?: string | null
}

export const testDataAPI = {
  async get(variantId: string): Promise<any> {
    const response = await apiFetch(`${ws()}/test-data/${variantId}`)
    if (!response.ok) throw new Error('Failed to fetch test data')
    return response.json()
  },

  async setCredentials(variantId: string, credentials: any[]): Promise<void> {
    const response = await apiFetch(`${ws()}/test-data/${variantId}/credentials`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    })
    if (!response.ok) throw new Error('Failed to update credentials')
  },
}

// ── File-cell upload result types (#987 Slice 2) ────────────────────────
//
// Mirrors the persisted-shape FileRef from the backend. Kept local to
// the API surface so component code doesn't import server types
// directly (frontend / backend stay decoupled). When the server's
// `IFileRef` shape changes, this mirror updates in lockstep — the
// fields are stable per #987 design lock and unlikely to churn.

/** Persisted reference to one stored file. Mirrors the server's
 * `IFileRef`. The `detectedMime` is the magic-byte-sniffed MIME the
 * security stack actually gated on; `uploadedMime` is the
 * browser-supplied Content-Type, kept separately for operator
 * debugging of "why was this rejected" cases where the two differ. */
export interface FileRef {
  sha256: string
  size: number
  originalName: string
  detectedMime: string
  uploadedMime: string
  scanResult: 'clean'
  scanProvider: string
  /** ISO-8601 string when the file was uploaded. JSON-transported
   * dates land here as strings rather than `Date` instances. */
  uploadedAt: string
}

/** Structured rejection from one of the security-stack gates. The
 * `stage` identifies which gate fired so the UI can compose a
 * targeted message; `signatures` is populated only when the virus
 * scanner reported infected. */
export interface UploadFileCellRejection {
  kind: 'rejected'
  stage: 'size-cap' | 'extension-allowlist' | 'mime-allowlist' | 'virus-scan' | 'unknown'
  message: string
  signatures?: string[]
}

/** Discriminated result from `uploadFileCell`. The promise rejects
 * only for transport-level errors (no HTTP response). Every server
 * response — success, security-stack rejection, scanner-unavailable,
 * feature-disabled, addressed-row-vanished — resolves as a typed
 * variant so the caller branches on `kind`. */
export type UploadFileCellResult =
  | { kind: 'ok'; file: FileRef; dedupHit: boolean }
  | UploadFileCellRejection
  | { kind: 'scannerUnavailable'; message: string }
  | { kind: 'featureDisabled'; message: string }
  | { kind: 'notFound'; message: string }
  | { kind: 'aborted' }

// ============================================
// Scheduled Runs API
// ============================================

export const scheduledRunsAPI = {
  async getAll(): Promise<any[]> {
    const response = await apiFetch(`${ws()}/scheduled-runs`)
    if (!response.ok) throw new Error('Failed to fetch schedules')
    return response.json()
  },

  async getByVariant(variantId: string): Promise<any[]> {
    const response = await apiFetch(`${ws()}/scheduled-runs?variantId=${variantId}`)
    if (!response.ok) throw new Error('Failed to fetch schedules')
    return response.json()
  },

  async create(data: any): Promise<any> {
    const response = await apiFetch(`${ws()}/scheduled-runs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!response.ok) throw new Error('Failed to create schedule')
    return response.json()
  },

  async update(id: string, data: any): Promise<any> {
    const response = await apiFetch(`${ws()}/scheduled-runs/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!response.ok) throw new Error('Failed to update schedule')
    return response.json()
  },

  async delete(id: string): Promise<void> {
    const response = await apiFetch(`${ws()}/scheduled-runs/${id}`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Failed to delete schedule')
  },

  async togglePause(id: string): Promise<any> {
    const response = await apiFetch(`${ws()}/scheduled-runs/${id}/toggle`, { method: 'PATCH' })
    if (!response.ok) throw new Error('Failed to toggle schedule')
    return response.json()
  },
}

export const replayTemplatesAPI = {
  async getByVariant(variantId: string) {
    const response = await apiFetch(`${ws()}/replay-templates?variantId=${variantId}`)
    if (!response.ok) throw new Error('Failed to fetch replay templates')
    const data = await response.json()
    return normalizeIds(data)
  },

  // No `kind`: this endpoint's URL says which one, so requiring it would
  // be a field the server ignores.
  async create(template: Omit<CreateRunTemplateInput, 'kind'>) {
    const response = await apiFetch(`${ws()}/replay-templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(template),
    })
    if (!response.ok) throw new Error('Failed to create replay template')
    const data = await response.json()
    return normalizeId(data)
  },

  async update(id: string, template: UpdateRunTemplateInput) {
    const response = await apiFetch(`${ws()}/replay-templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(template),
    })
    if (!response.ok) throw new Error('Failed to update replay template')
    const data = await response.json()
    return normalizeId(data)
  },

  async delete(id: string): Promise<void> {
    const response = await apiFetch(`${ws()}/replay-templates/${id}`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Failed to delete replay template')
  },
}

/** Unified RunTemplate API for the three kinds: 'replay', 'generation',
 * 'resume'. Slice 0 (#1113) added the backend repository; Slice 1 (#1114)
 * wires the frontend to it. The `/run-templates` route exposes all three
 * kinds from a single endpoint; callers filter by `variantId` to get the
 * list for the TestRunner's Start page.
 *
 * The existing `replayTemplatesAPI` is still used for replay-specific CRUD
 * (create / update / delete replay templates via the `/replay-templates`
 * route). This new API is the UNIFIED READ for the Start page list. */
export const runTemplatesAPI = {
  /** List RunTemplates for a variant (all three kinds). Sorted by
   * updatedAt desc (most recently edited first) — matches the backend's
   * `listForVariant` sort.
   *
   * `pool` says which way of starting a run these settings are for
   * (#2175), and is REQUIRED. It began optional, and two surfaces that
   * should have narrowed did not — the Proposed tab's "Create tests",
   * which starts a run somebody is watching, and the pipeline snippet
   * builder, which writes a CI call an operator pastes into their
   * pipeline. Both silently offered every pool, which is the collision
   * the pools were introduced to remove. Requiring it turns the next
   * omission into a compile error instead of a screen that looks right.
   *
   * Nothing needs the unfiltered list today. If an oversight caller ever
   * does, give it its own method whose name says so rather than making
   * this argument optional again. */
  async listForVariant(variantId: string, pool: RunTemplatePool): Promise<unknown[]> {
    const response = await apiFetch(
      `${ws()}/run-templates?variantId=${variantId}&pool=${pool}`,
    )
    if (!response.ok) throw new Error('Failed to fetch run templates')
    const data = await response.json()
    return normalizeIds(data)
  },

  async create(template: CreateRunTemplateInput): Promise<unknown> {
    const response = await apiFetch(`${ws()}/run-templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(template),
    })
    if (!response.ok) throw new Error('Failed to create run template')
    const data = await response.json()
    return normalizeId(data)
  },

  async update(id: string, template: UpdateRunTemplateInput): Promise<unknown> {
    const response = await apiFetch(`${ws()}/run-templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(template),
    })
    if (!response.ok) throw new Error('Failed to update run template')
    const data = await response.json()
    return normalizeId(data)
  },

  async delete(id: string): Promise<void> {
    const response = await apiFetch(`${ws()}/run-templates/${id}`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Failed to delete run template')
  },
}

export interface SystemFixtureSummary {
  id: string
  name: string
  description?: string
  urlPatterns: string[]
  requiredFields: Array<'USERNAME' | 'PASSWORD' | 'TOTP_SECRET' | 'TOKEN' | 'LOGIN_URL'>
  /** Candidate sign-in paths OttoTester probes to find the address itself.
   * When present, the sign-in address is not something the operator has to
   * supply — asking for it would demand a path nobody can guess, and the
   * typed value would then outrank the correct one discovery finds. */
  authEntryPathHints?: string[]
  version: number
}

export const systemFixturesAPI = {
  async list(): Promise<SystemFixtureSummary[]> {
    const response = await apiFetch(`${API_BASE}/system-fixtures`)
    if (!response.ok) throw new Error('Failed to fetch system fixtures')
    return response.json()
  },
}

// ──────────────────────────────────────────────────────────────────────
// AdminSystemFixturesAPI — superadmin-only single-fixture detail (#914)
//
// The list view on the admin viewer reuses `systemFixturesAPI.list()`
// above: the public list already returns id, name, description,
// urlPatterns, requiredFields, version — the exact column set the
// admin page table needs. The admin-tier endpoint here exists ONLY
// for the detail view, which needs the heavy `specTemplate` field
// the public list correctly omits. Trust-boundary alignment with the
// server-side `routes/admin/systemFixtures.ts` file header.
// ──────────────────────────────────────────────────────────────────────

/** Full SystemFixture as the admin detail endpoint returns it. Superset
 * of `SystemFixtureSummary` with the spec template + lockout signals
 * + timestamps the admin viewer surfaces. */
export interface AdminSystemFixtureDetail extends SystemFixtureSummary {
  /** Optional per-IdP lockout indicators. Mirrors the server's
   * `ILockoutSignals` shape. */
  lockoutSignals?: {
    domPattern?: string
    statusCodes?: number[]
  }
  /** The full Playwright .spec.ts source for this fixture, including
   * the `{{ROLE}}` / `{{ROLE_UPPER}}` placeholders the copy-on-select
   * pass substitutes at variant time. */
  specTemplate: string
  createdAt: string
  updatedAt: string
}

export const adminSystemFixturesAPI = {
  async get(id: string): Promise<AdminSystemFixtureDetail> {
    const res = await apiFetch(`${API_BASE}/admin/system-fixtures/${encodeURIComponent(id)}`)
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string }
      throw new Error(err.error || `Failed to fetch system fixture (${res.status})`)
    }
    return (await res.json()) as AdminSystemFixtureDetail
  },
}

// ============================================
// Reporting API — variant- and run-keyed reads (#437)
// ============================================
//
// The dashboard's read path (#436 phase B). All five endpoints sit at
// `/api/runs/:variantId/...` or `/api/runs/:runId/...` — flat under
// `/api/runs`, NOT under `/api/workspaces/:wid/...`. The server resolves
// the workspace from the variant or run id itself (via the
// resolveVariantWorkspace / resolveRunWorkspace middleware in
// `apps/server/src/infrastructure/auth/middleware.ts`), then enforces
// membership and `testRuns.read` permission. Calling apiFetch directly
// against `${API_BASE}/runs/...` is correct here; do not prefix with
// `ws()`.

export interface GetTrendsOptions {
  /** Duration string (`30d` / `7d` / `24h`) or bare ms integer. */
  window?: string
  /** Max runs returned (default 50 server-side, cap 200). */
  limit?: number
  runType?: RunTypeFilter
  /** Optional suite filter (#710) — restrict to runs of one suite. */
  suite?: string
}

export interface GetFlakyOptions {
  /** Number of runs to examine (default 10, cap 100). */
  window?: number
  runType?: RunTypeFilter
  /** Optional suite filter (#710) — restrict to runs of one suite. */
  suite?: string
}

export interface GetTriageOptions {
  window?: string
  /** Max distinct runIds to examine (default 200, cap 500). */
  limit?: number
  runType?: RunTypeFilter
  /** When set, response includes only that bucket's entries. */
  bucket?: TriageBucketKey
  /** Per-bucket page size (default 100, cap 500). */
  bucketLimit?: number
  /** Opaque base64 cursor; only valid when `bucket` is set. */
  cursor?: string
  /** Optional suite filter (#710) — restrict to runs of one suite. */
  suite?: string
}

/** Options for `reportingAPI.getComparison` — a discriminated union on
 * `axis`, mirroring the server's `GET /api/runs/:variantId/comparison`
 * query contract (#747 Slice 2/3). Date fields are ISO 8601 strings. */
export type GetComparisonOptions =
  | { axis: 'release'; a: string; b: string; suiteId?: string }
  | {
      axis: 'date'
      aFrom: string
      aTo: string
      bFrom: string
      bTo: string
      suiteId?: string
    }

export const reportingAPI = {
  async getTrends(variantId: string, opts: GetTrendsOptions = {}): Promise<TrendsResponse> {
    const params = new URLSearchParams()
    if (opts.window !== undefined) params.set('window', opts.window)
    if (opts.limit !== undefined) params.set('limit', String(opts.limit))
    if (opts.runType !== undefined) params.set('runType', opts.runType)
    if (opts.suite !== undefined) params.set('suite', opts.suite)
    const qs = params.toString()
    const response = await apiFetch(`${API_BASE}/runs/${variantId}/trends${qs ? `?${qs}` : ''}`)
    if (!response.ok) throw new Error(`Failed to fetch trends (${response.status})`)
    return response.json()
  },

  async getFlaky(variantId: string, opts: GetFlakyOptions = {}): Promise<FlakyTestsResponse> {
    const params = new URLSearchParams()
    if (opts.window !== undefined) params.set('window', String(opts.window))
    if (opts.runType !== undefined) params.set('runType', opts.runType)
    if (opts.suite !== undefined) params.set('suite', opts.suite)
    const qs = params.toString()
    const response = await apiFetch(`${API_BASE}/runs/${variantId}/flaky${qs ? `?${qs}` : ''}`)
    if (!response.ok) throw new Error(`Failed to fetch flaky tests (${response.status})`)
    return response.json()
  },

  /** #1442 Slice 4 — Test Coverage over time. Returns one point per
   * finalized run (Authored / Passing) within the window, oldest-first. */
  async getCoverageTrend(
    variantId: string,
    opts: { window?: string } = {},
  ): Promise<CandidateCoverageTrendResponse> {
    const params = new URLSearchParams()
    if (opts.window !== undefined) params.set('window', opts.window)
    const qs = params.toString()
    const response = await apiFetch(`${API_BASE}/runs/${variantId}/coverage-trend${qs ? `?${qs}` : ''}`)
    if (!response.ok) throw new Error(`Failed to fetch coverage trend (${response.status})`)
    return response.json()
  },

  /** #1558 Slice 3 — requirements/defect coverage over time. One point per
   * finalized run within the window, oldest-first. `dimension` defaults to
   * `requirement`. */
  async getIssueCoverageTrend(
    variantId: string,
    opts: { window?: string; dimension?: IssueCoverageDimension } = {},
  ): Promise<IssueCoverageTrendResponse> {
    const params = new URLSearchParams()
    if (opts.window !== undefined) params.set('window', opts.window)
    if (opts.dimension !== undefined) params.set('dimension', opts.dimension)
    const qs = params.toString()
    const response = await apiFetch(`${API_BASE}/runs/${variantId}/issue-coverage-trend${qs ? `?${qs}` : ''}`)
    if (!response.ok) throw new Error(`Failed to fetch issue coverage trend (${response.status})`)
    return response.json()
  },

  async getTriage(variantId: string, opts: GetTriageOptions = {}): Promise<TriageResponse> {
    const params = new URLSearchParams()
    if (opts.window !== undefined) params.set('window', opts.window)
    if (opts.limit !== undefined) params.set('limit', String(opts.limit))
    if (opts.runType !== undefined) params.set('runType', opts.runType)
    if (opts.suite !== undefined) params.set('suite', opts.suite)
    if (opts.bucket !== undefined) params.set('bucket', opts.bucket)
    if (opts.bucketLimit !== undefined) params.set('bucketLimit', String(opts.bucketLimit))
    if (opts.cursor !== undefined) params.set('cursor', opts.cursor)
    const qs = params.toString()
    const response = await apiFetch(`${API_BASE}/runs/${variantId}/triage${qs ? `?${qs}` : ''}`)
    if (!response.ok) throw new Error(`Failed to fetch triage (${response.status})`)
    return response.json()
  },

  /** Release/date run comparison (#747). On a 400 — empty bucket, or a
   * malformed request — throws an `Error` carrying the server's
   * human-readable `message` (e.g. the empty-bucket explanation), so
   * the Compare page can surface it directly. */
  async getComparison(
    variantId: string,
    opts: GetComparisonOptions,
  ): Promise<ComparisonResponse> {
    const params = new URLSearchParams()
    params.set('axis', opts.axis)
    if (opts.axis === 'release') {
      params.set('a', opts.a)
      params.set('b', opts.b)
    } else {
      params.set('aFrom', opts.aFrom)
      params.set('aTo', opts.aTo)
      params.set('bFrom', opts.bFrom)
      params.set('bTo', opts.bTo)
    }
    if (opts.suiteId) params.set('suite', opts.suiteId)
    const response = await apiFetch(
      `${API_BASE}/runs/${variantId}/comparison?${params.toString()}`,
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(
        body?.message || body?.error || `Failed to fetch comparison (${response.status})`,
      )
    }
    return response.json()
  },

  /** Distinct `tags.release` values for a variant — feeds the Compare
   * screen's release picker. Returns just the (sorted) string list. */
  async getReleases(variantId: string): Promise<string[]> {
    const response = await apiFetch(`${API_BASE}/runs/${variantId}/releases`)
    if (!response.ok) throw new Error(`Failed to fetch releases (${response.status})`)
    const body = await response.json()
    return Array.isArray(body?.releases) ? body.releases : []
  },

  /** Returns `{ notApplicable: true, reason }` for discovery runs
   * (HTTP 400, code `'discovery-run'`) so the run-detail page can
   * render a friendly "regression delta doesn't apply to discovery
   * runs" panel without a try/catch round-trip. Other 4xx/5xx still
   * throw. */
  async getRegressionDelta(
    runId: string,
  ): Promise<RegressionDeltaResponse | RegressionDeltaNotApplicable> {
    const response = await apiFetch(`${API_BASE}/runs/${runId}/regression-delta`)
    if (response.status === 400) {
      const body = await response.json().catch(() => ({}))
      // The server emits `{ error, message, code, runType }` for the
      // discovery case. Anything else 400 (parse error, missing
      // param, etc.) is a real bug and should still surface.
      if (body?.code === 'discovery-run' || body?.runType === 'discovery') {
        return {
          notApplicable: true,
          reason: body.message ?? body.error ?? 'Regression delta is not applicable to discovery runs.',
          runType: body.runType,
        }
      }
      throw new Error(body?.message || body?.error || `Failed to fetch regression delta (400)`)
    }
    if (!response.ok) throw new Error(`Failed to fetch regression delta (${response.status})`)
    return response.json()
  },

  async getFailureSummary(runId: string): Promise<FailureSummaryResponse> {
    const response = await apiFetch(`${API_BASE}/runs/${runId}/failure-summary`)
    if (!response.ok) throw new Error(`Failed to fetch failure summary (${response.status})`)
    return response.json()
  },

  /** Per-test detail for a run, with `testCaseId` resolved via the
   * backend's TestScript join (#936 PR 2). Powers the run-detail
   * page's workflow card. */
  async getDetailedRun(runId: string): Promise<DetailedRunResponse> {
    const response = await apiFetch(`${API_BASE}/runs/${runId}/detail`)
    if (!response.ok) throw new Error(`Failed to fetch detailed run (${response.status})`)
    return response.json()
  },

  /** Per-test console/network event lists for a run (#1515 Slice 3).
   * The drill-down behind the run-report Issues count — fetched only
   * when the Issues card renders. Returns only tests that captured at
   * least one event. */
  async getRunIssues(runId: string): Promise<RunIssuesResponse> {
    const response = await apiFetch(`${API_BASE}/runs/${runId}/issues`)
    if (!response.ok) throw new Error(`Failed to fetch run issues (${response.status})`)
    return response.json()
  },
}

/** #936 PR 2 — per-test detail with `testCaseId` for workflow-card
 * attribution. Mirrors the server-side `DetailedRunTest` shape from
 * `application/testReporting/detailedRun.ts`. */
export interface DetailedRunTestStep {
  name: string
  category: string
  status: 'passed' | 'failed' | 'skipped'
  durationMs: number
  startedAt: string
  errorMessage?: string
  parentStepIndex?: number
}
export interface DetailedRunTestAttachment {
  name: string
  path: string
  contentType: string
}
/** Aggregate counts of run-health signals captured during this
 * test's execution (#1018 / slice B of meta #1008). Mirrors the
 * server-side `IssuesSummary` shape on `TestExecution`. */
export interface DetailedRunIssuesSummary {
  console: {
    errors: number
    warns: number
    pageErrors: number
  }
  network: {
    count: number
    severe: number
  }
}

export interface DetailedRunTest {
  testId: string
  testName: string
  testFile: string | null
  /** Resolved test-case id; absent for cascade-orphans (#936 PR 2). */
  testCaseId?: string
  status: 'passed' | 'failed' | 'skipped' | 'timedOut' | 'interrupted'
  durationMs: number
  retryCount: number
  errorType?: string
  errorMessage?: string
  errorStack?: string
  attachments: DetailedRunTestAttachment[]
  steps: DetailedRunTestStep[]
  /** Run-health signal counts (#1018 / slice B of meta #1008).
   * Absent on tests where no signal was captured — the row pill
   * renders nothing in that case. */
  issuesSummary?: DetailedRunIssuesSummary
}
export interface DetailedRunResponse {
  runId: string
  runType: 'replay' | 'discovery'
  tests: DetailedRunTest[]
  /** Run-level aggregate of every test's issuesSummary (#1018 /
   * slice B2 of meta #1008 / mockup 3). Powers the fifth tile on
   * the run-report header. Absent when no test in the run carries
   * an `issuesSummary` (legacy / disabled / no trace) — same
   * silence-on-absence rule as the row pill. */
  runIssuesSummary?: DetailedRunIssuesSummary
}

/** #1515 Slice 3 — the per-event detail behind the Issues count.
 * Mirror the server `ConsoleLogEntryPersisted` / `NetworkLogEntryPersisted`
 * shapes from `db/models/TestExecution.ts`. */
export interface ConsoleLogEntry {
  kind: 'pageError' | 'console-error' | 'console-warn'
  message: string
  errorName?: string
  source?: string
  /** Milliseconds from test start — the timeline's x-axis. */
  relativeMs: number
}
export interface NetworkLogEntry {
  method: string
  url: string
  status?: number
  durationMs?: number
  relativeMs: number
  pending: boolean
}
/** One test that recorded at least one event. `console` / `network`
 * are always present (as `[]` when that kind was empty); a test reaches
 * this list only when at least one is non-empty. Mirrors the server
 * `RunIssueTest`. */
export interface RunIssueTest {
  testId: string
  testName: string
  testFile: string | null
  testCaseId?: string
  status: 'passed' | 'failed' | 'skipped' | 'timedOut' | 'interrupted'
  issuesSummary?: DetailedRunIssuesSummary
  console: ConsoleLogEntry[]
  network: NetworkLogEntry[]
}
export interface RunIssuesResponse {
  runId: string
  tests: RunIssueTest[]
}

// ============================================
// Workspaces — flat (NOT workspace-scoped)
// ============================================
//
// The workspace itself can't be workspace-scoped. These endpoints
// resolve "which workspace am I in" (`getMe`) and manage workspaces.
// Pre-auth, getMe returns the migration workspace ("Demos and Tests").

export interface WorkspaceSummary {
  _id: string
  orgId: string
  name: string
  createdAt: string
  updatedAt: string
}

export const workspacesAPI = {
  /** Default workspace probe — first workspace in the user's first
   * org. Strict-auth post-Phase-5a: 401 if no session, 404 if the
   * caller has no orgs (route them to /onboarding instead). */
  async getMe(): Promise<WorkspaceSummary> {
    const response = await apiFetch(`${API_BASE}/workspaces/me`)
    if (!response.ok) throw new Error(`Failed to resolve active workspace (${response.status})`)
    return response.json()
  },

  async list(): Promise<WorkspaceSummary[]> {
    const response = await apiFetch(`${API_BASE}/workspaces`)
    if (!response.ok) throw new Error('Failed to list workspaces')
    return response.json()
  },

  /** Fetch a specific workspace by id. Used by AppContextProvider
   * when the URL :workspaceId changes (e.g., switching workspaces
   * via the header picker) to update the active workspace state. */
  async getById(workspaceId: string): Promise<WorkspaceSummary> {
    const response = await apiFetch(`${API_BASE}/workspaces/${workspaceId}`)
    if (!response.ok) throw new Error(`Failed to fetch workspace (${response.status})`)
    return response.json()
  },

  /** Create a workspace inside an org. Owner+admin only on the
   * server side; UI should hide the affordance for member/reviewer. */
  async createInOrg(orgId: string, name: string): Promise<WorkspaceSummary> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/workspaces`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed to create workspace (${response.status})` }))
      throw new Error(err.error || 'Failed to create workspace')
    }
    return response.json()
  },

  /** Rename a workspace. Owner+admin only on the server. */
  async update(workspaceId: string, patch: { name: string }): Promise<WorkspaceSummary> {
    const response = await apiFetch(`${API_BASE}/workspaces/${workspaceId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to update workspace')
    }
    return response.json()
  },

  /** Soft-delete a workspace. Sets `deletedAt`; recoverable for 30
   * days via the recovery page (Phase 5c-2). Owner+admin only. */
  async softDelete(workspaceId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/workspaces/${workspaceId}`, {
      method: 'DELETE',
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to delete workspace')
    }
  },
}

// ── Workspace member management (#526 PR 5a) ─────────────────────────

/** Subject of a workspace membership grant. PR 5a surface covers
 * `user` only; team subjects ride the same endpoint and land in
 * PR 5b alongside the Teams UI. */
export type WorkspaceMembershipSubjectType = 'user' | 'team'

/** Single row returned by GET /api/workspaces/:wid/members. Polymorphic
 * subject (user OR team) — PR 5a renders only user rows; PR 5b adds
 * team rows. */
export interface WorkspaceMembership {
  _id: string
  workspaceId: string
  subjectType: WorkspaceMembershipSubjectType
  subjectId: string
  role: 'admin' | 'tester' | 'reviewer'
  grantedBy: string
  grantedOrigin: 'direct' | 'workspace-creator-auto'
  createdAt: string
  updatedAt: string
}

/** Display info for one user, as attached by the routes that serve
 * id-only membership rows (#2094). Both fields are optional: the
 * server's lookup is fail-soft and a user row may legitimately have
 * no name. Render through `lib/userDisplay` rather than reading these
 * directly, so the name → email → id fallback stays in one place. */
export interface UserDisplayInfo {
  email?: string
  name?: string
}

/** userId → display info. Absent keys are expected, not an error:
 * a deleted account, or a lookup that degraded to `{}` wholesale. */
export type UserDisplayMap = Record<string, UserDisplayInfo>

/** Response of GET /api/workspaces/:wid/members (#2094). The rows
 * carry ids only; `users` names the user subjects and the grantors,
 * `teams` names the team subjects. */
export interface WorkspaceMembersResponse {
  memberships: WorkspaceMembership[]
  users: UserDisplayMap
  teams: Record<string, { name: string }>
}

export const workspaceMembersAPI = {
  /** List every membership on this workspace — direct user grants
   * plus team grants — with the display maps needed to render them.
   * Gated to org Owner/Admin or workspace Admin on the server side.
   *
   * Returns the whole `{ memberships, users, teams }` wrapper rather
   * than just the rows. Every caller gets the same shape from the one
   * client method; a second rows-only convenience method would be two
   * clients for one endpoint, and the callers would drift apart the
   * next time the response grows a field. */
  async list(workspaceId: string): Promise<WorkspaceMembersResponse> {
    const response = await apiFetch(`${API_BASE}/workspaces/${workspaceId}/members`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to list workspace members')
    }
    return response.json()
  },

  /** Grant a new membership. Subject must be an org member for
   * `subjectType: 'user'` (server validates). */
  async grant(
    workspaceId: string,
    payload: { subjectType: WorkspaceMembershipSubjectType; subjectId: string; role: 'admin' | 'tester' | 'reviewer' },
  ): Promise<WorkspaceMembership> {
    const response = await apiFetch(`${API_BASE}/workspaces/${workspaceId}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to grant workspace membership')
    }
    return response.json()
  },

  /** Change an existing membership's role. Server enforces the
   * at-least-one-Admin invariant; 409 on demoting the last admin. */
  async updateRole(
    workspaceId: string,
    subjectType: WorkspaceMembershipSubjectType,
    subjectId: string,
    role: 'admin' | 'tester' | 'reviewer',
  ): Promise<WorkspaceMembership> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${workspaceId}/members/${subjectType}/${encodeURIComponent(subjectId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to update membership role')
    }
    return response.json()
  },

  /** Revoke a membership. 409 if it would leave the workspace with
   * zero admins (same last-admin invariant as role change). */
  async revoke(
    workspaceId: string,
    subjectType: WorkspaceMembershipSubjectType,
    subjectId: string,
  ): Promise<void> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${workspaceId}/members/${subjectType}/${encodeURIComponent(subjectId)}`,
      { method: 'DELETE' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to revoke membership')
    }
  },
}

// ── Teams (#526 PR 5b) ────────────────────────────────────────────

/** A team — org-scoped grouping of users that can be granted into
 * workspaces. Per ADR 0010. */
export interface Team {
  _id: string
  orgId: string
  name: string
  description?: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

/** A team-membership row — the (team, user) link. */
export interface TeamMember {
  _id: string
  teamId: string
  userId: string
  addedBy: string
  addedAt: string
}

export const teamsAPI = {
  /** List teams in an org. Any org member can read; mutate gated to
   * Owner/Admin server-side. */
  async list(orgId: string): Promise<Team[]> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/teams`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to list teams')
    }
    return response.json()
  },

  /** Single team detail + member list. Returns the team, its members,
   * and the display names for those members in one round-trip (the
   * server wraps all three in `{ team, members, users }` — see
   * `routes/teams.ts`). `users` was added in #2094; member rows carry
   * only a `userId`. */
  async get(
    orgId: string,
    teamId: string,
  ): Promise<{ team: Team; members: TeamMember[]; users: UserDisplayMap }> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/teams/${teamId}`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to fetch team')
    }
    return response.json()
  },

  async create(orgId: string, payload: { name: string; description?: string }): Promise<Team> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/teams`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to create team')
    }
    return response.json()
  },

  async update(
    orgId: string,
    teamId: string,
    patch: { name?: string; description?: string },
  ): Promise<Team> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/teams/${teamId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to update team')
    }
    return response.json()
  },

  async delete(orgId: string, teamId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/teams/${teamId}`, {
      method: 'DELETE',
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to delete team')
    }
  },

  /** Convenience: the members list plus the names to render it with
   * (drops the team field). Returns `users` alongside `members` as of
   * #2094 — a member row is only a `userId`, so a caller that got the
   * rows without the map could not display anyone.
   *
   * References `teamsAPI.get` explicitly rather than `this.get` so
   * destructured invocation (`const { listMembers } = teamsAPI`)
   * still works. PR #541 Copilot review. */
  async listMembers(
    orgId: string,
    teamId: string,
  ): Promise<{ members: TeamMember[]; users: UserDisplayMap }> {
    const { members, users } = await teamsAPI.get(orgId, teamId)
    return { members, users }
  },

  async addMember(orgId: string, teamId: string, userId: string): Promise<TeamMember> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/teams/${teamId}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to add team member')
    }
    return response.json()
  },

  async removeMember(orgId: string, teamId: string, userId: string): Promise<void> {
    const response = await apiFetch(
      `${API_BASE}/orgs/${orgId}/teams/${teamId}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to remove team member')
    }
  },
}

// ============================================
// Identity & orgs (Phase 5a/b of ottotester#65)
// ============================================
//
// `/api/me` is the single source of truth for first-load routing —
// returns `{ user, orgs[], needsOnboarding }`. The frontend reads it
// once on boot via IdentityProvider and uses `needsOnboarding` to
// decide between the onboarding wizard and the workspace runner.
//
// `/api/orgs/*` wraps Better Auth's Organization plugin — see
// `apps/server/src/routes/orgs.ts` for the wrapping rationale.

export type OrgRole = 'owner' | 'admin' | 'billing' | 'member'

export interface OrgWithRole {
  _id: string
  name: string
  slug: string
  role: OrgRole
  createdAt: string
}

export interface IdentitySummary {
  user: {
    id: string
    email: string
    name?: string
    emailVerified?: boolean
    /** Persisted superadmin flag from the Better Auth user doc
     * (#429). Present-and-true means this user CAN escalate; the
     * actual escalation is the `x-superadmin-mode: on` opt-in
     * tracked client-side in `auth/superadminMode.ts`. Drives the
     * admin sidebar entry and the user-menu toggle visibility. */
    isSuperadmin?: boolean
    /** Reserved (#429). Currently unused on the frontend. */
    isInternalSupport?: boolean
    /** Preferred IANA timezone (e.g. `America/New_York`), set in
     * Account Settings → Profile. Read back here so the Profile form
     * re-renders the saved value instead of reverting to the browser
     * default. */
    timezone?: string
  }
  orgs: OrgWithRole[]
  /** Workspace-tier role per workspace the user can reach (#526 PR 5a).
   * Keyed on workspaceId; covers every direct WorkspaceMembership
   * grant plus implicit `admin` for every workspace in an org the
   * user owns (the Owner break-glass). Team-mediated grants land
   * in PR 5b. Optional so a server build pre-PR-5a returns an
   * undefined here and the consumer (`useCanIWorkspace`) treats
   * absence as "no permissions known" — the affordance hides until
   * the next /api/me refresh after the server upgrade. */
  workspaceRoles?: Record<string, 'admin' | 'tester' | 'reviewer'>
  needsOnboarding: boolean
  /** Which onboarding screen to show, on a deployment that bills its
   * customers (#2454). `fine` means nothing is in the way, and is what every
   * install that does not bill its customers always returns. Optional so a
   * server build from before this returns nothing and the gate treats the
   * absence as `fine` rather than blocking everybody. */
  billing?: 'fine' | 'none' | 'unfinished' | 'no-credit' | 'capped' | 'blocked' | 'unknown'
  /** True when this deployment bills its customers at all (the engine's
   * `gateApplies` predicate). Gates the Billing entry in organization
   * settings: shown only where there is billing to reach, absent on a
   * self-hosted engine. Optional so a server build from before this reads as
   * false and the entry stays hidden rather than appearing with nowhere to go. */
  billingEnabled?: boolean
  /** Absolute URL the billing gate's button sends a hosted customer to, to
   * start the sign-in handoff to the account service (#2481). Null or absent
   * when this install hands nobody over, and then the gate shows no action
   * button rather than one that leads nowhere. */
  billingHandoffUrl?: string | null
  /** How many days a deleted org / workspace / account is held before it's
   * permanently removed. 0 means no hold — deletes are permanent on the
   * spot. The delete-confirm dialogs read this to say "permanent" vs
   * "held N days" (#2510). Absent on a server build from before this;
   * treat absent as 0 (permanent). */
  recoveryWindowDays?: number
  /** True when the signed-in account is scheduled for deletion and still
   * within the 30-day hold (#1748). The app routes to the restore prompt
   * instead of the workspace; recovery is an explicit action, never a
   * side effect of logging in. */
  deletionPending?: boolean
  /** ms-epoch when the held account is permanently purged — drives the
   * "N days left" countdown on the restore screen (#1748). */
  recoverableUntil?: number
  /** Non-null when the current session was created via Better Auth's
   * admin impersonation endpoint. Carries the acting superadmin's
   * user id. Drives the amber impersonation banner in PageLayout
   * (#429 Wave 2) — the operator always knows whose identity they're
   * acting as. */
  impersonation?: { actorUserId: string } | null
}

export const identityAPI = {
  /** Returns null on 401 (no session) so callers can route to login
   * without a try/catch. Other failures still throw so the
   * IdentityProvider's error UI can surface them. */
  async me(): Promise<IdentitySummary | null> {
    const response = await apiFetch(`${API_BASE}/me`)
    if (response.status === 401) return null
    if (!response.ok) throw new Error(`Failed to resolve identity (${response.status})`)
    return response.json()
  },
}

/** Result of a delete attempt — discriminated so the Danger surface can
 * render the wrong-password and sole-owner-block states distinctly. */
export type AccountDeleteResult =
  | { ok: true }
  | { ok: false; reason: 'reauth'; message: string }
  | {
      ok: false
      reason: 'block'
      message: string
      orgs: Array<{ id: string; name: string; memberCount: number }>
    }
  | { ok: false; reason: 'error'; message: string }

export const accountAPI = {
  /** Schedule the signed-in account for deletion (30-day hold). Requires
   * the current password (inline re-auth on the server). On success the
   * session is revoked server-side; the caller redirects to login. (#1748) */
  async remove(password: string): Promise<AccountDeleteResult> {
    const response = await apiFetch(`${API_BASE}/account/delete`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ password }),
    })
    if (response.ok) return { ok: true }
    const body = (await response.json().catch(() => ({}))) as {
      error?: string
      orgs?: Array<{ id: string; name: string; memberCount: number }>
    }
    if (response.status === 401) {
      return { ok: false, reason: 'reauth', message: body.error ?? "That password didn't match." }
    }
    if (response.status === 409) {
      return {
        ok: false,
        reason: 'block',
        message: body.error ?? 'You solely own organizations that still have other members.',
        orgs: body.orgs ?? [],
      }
    }
    return { ok: false, reason: 'error', message: body.error ?? `Failed to delete account (${response.status})` }
  },
  /** Explicitly restore a pending-deletion account within the hold — a
   * deliberate action, never a side effect of logging in (#1748). */
  async recover(): Promise<void> {
    const response = await apiFetch(`${API_BASE}/account/recover`, { method: 'POST' })
    if (!response.ok) throw new Error(`Failed to restore account (${response.status})`)
  },
}

// ── Public settings ──

/** Facts about this installation that signed-out screens need. Each page
 * is present only when the installation set it; no pages means signup
 * asks the person to agree to nothing. */
export interface PublicSettings {
  termsUrl?: string
  privacyUrl?: string
  /** Whether this installation can send email. `sender` is who it comes
   * from, present only when email is on and a sender is set. */
  email: { enabled: boolean; sender?: { name: string; address: string } }
  /** Present only while the install has no accounts at all. `becomesAdmin`
   * says the first account becomes the admin; `supplierName` is the AI
   * company the installer set up, when the server can name it. */
  firstAccount?: { becomesAdmin: boolean; supplierName?: string }
  /** The Google, GitHub and Microsoft sign-ins this server turned on, in
   * the order the page shows them. Empty on an installed copy. Missing
   * only from a server older than this field. */
  signInProviders?: SignInProvider[]
}

/** The companies whose accounts a person can sign in with, when the server
 * has keys for them. */
export type SignInProvider = 'google' | 'github' | 'microsoft'

export const publicSettingsAPI = {
  /** Public and unauthenticated: signup reads it before anyone has an
   * account. Throws on failure so the caller can refuse to guess. */
  async get(): Promise<PublicSettings> {
    const response = await apiFetch(`${API_BASE}/public-settings`)
    if (!response.ok) throw new Error(`Failed to read public settings (${response.status})`)
    return response.json()
  },
}

// ── Sign-in lookup + onboarding (#1792 / #1434) ──

/** Ask whether an email address belongs to a customer who has connected
 * their own company sign-in (#1792). Public and unauthenticated by
 * necessity: it runs before anyone is signed in.
 *
 * Never throws. A failure here must not stop someone signing in with a
 * password, so any error resolves to "no company sign-in" and the
 * screen renders exactly as it does today. */
export async function lookupSso(email: string): Promise<SsoLookupResult> {
  try {
    const response = await apiFetch(`${API_BASE}/sso/lookup?email=${encodeURIComponent(email)}`)
    if (!response.ok) return { ssoEnabled: false }
    return await response.json()
  } catch {
    return { ssoEnabled: false }
  }
}

/** Result of `POST /api/onboarding/resolve` (#1434). `action` decides the
 * next step: join an inviting org, land in the provisioned workspace, or show
 * recovery when the personal org is soft-deleted. */
export type OnboardingResolveResult =
  | { action: 'invite' }
  // The provisioned personal org — the user lands in its workspace.
  | { action: 'workspace'; orgId: string }
  // The personal org is soft-deleted and within its recovery window;
  // `orgId` is that org so the restore screen can recover it in one click
  // without a second lookup, `recoverableUntil` (ms-epoch) drives the
  // "N days left" countdown, and `orgName` lets the screen name it (#2510).
  | { action: 'recover'; orgId: string; recoverableUntil: number; orgName: string }

/** Onboarding (#1434). `resolve` (post-verify, from the onboarding gate)
 * provisions the personal org and drops the user into their workspace. */
export const onboardingAPI = {
  async resolve(): Promise<OnboardingResolveResult> {
    const response = await apiFetch(`${API_BASE}/onboarding/resolve`, { method: 'POST' })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to finish signing in')
    }
    return response.json()
  },
}

/** One row of a per-user AI-spend breakdown (#1695 S8, #1721). `system`
 * is the automated-spend bucket (#1581); `former` is spend from a deleted
 * account. `costCents` is shown as dollars on the Org → Usage tab. */
export interface PerUserSpendRow {
  kind: 'user' | 'system' | 'former'
  label: string
  costCents: number
}

/** The per-user spend breakdown — rows plus the running total they
 * reconcile to (the period's attributed spend). */
export interface PerUserSpend {
  rows: PerUserSpendRow[]
  totalCents: number
}

// ── Notifications (bell-icon feed, #571 PR 3) ──────────────────────

export type NotificationSeverity = 'info' | 'warning' | 'critical'

export interface NotificationItem {
  _id: string
  userId: string
  eventType: string
  severity: NotificationSeverity
  title: string
  body: string
  deepLinkPath?: string
  read: boolean
  readAt?: string
  createdAt: string
  // Sparse FK columns — used for filter chips later; surfaced here
  // for completeness but the bell-icon dropdown only renders
  // title/body/deepLinkPath in v1.
  workspaceId?: string
  applicationId?: string
  variantId?: string
  runId?: string
  testCaseId?: string
  agentRunId?: string
}

export const notificationsAPI = {
  /** Newest-first feed pull, capped at `limit`. `before` is a cursor
   * (ISO date string) for backward pagination — pass the oldest
   * item's `createdAt` from the current page. */
  async list(opts: { limit?: number; before?: string } = {}): Promise<NotificationItem[]> {
    const params = new URLSearchParams()
    if (opts.limit) params.set('limit', String(opts.limit))
    if (opts.before) params.set('before', opts.before)
    const qs = params.toString() ? `?${params.toString()}` : ''
    const response = await apiFetch(`${API_BASE}/me/notifications${qs}`)
    if (!response.ok) throw new Error(`Failed to load notifications (${response.status})`)
    const data = await response.json()
    return data.items
  },

  async unreadCount(): Promise<number> {
    const response = await apiFetch(`${API_BASE}/me/notifications/unread-count`)
    if (!response.ok) throw new Error(`Failed to load unread count (${response.status})`)
    const data = await response.json()
    return data.count
  },

  async markRead(id: string): Promise<boolean> {
    const response = await apiFetch(`${API_BASE}/me/notifications/${id}/read`, { method: 'POST' })
    if (!response.ok) throw new Error(`Failed to mark read (${response.status})`)
    const data = await response.json()
    return data.updated
  },

  async markAllRead(): Promise<number> {
    const response = await apiFetch(`${API_BASE}/me/notifications/mark-all-read`, { method: 'POST' })
    if (!response.ok) throw new Error(`Failed to mark all read (${response.status})`)
    const data = await response.json()
    return data.updated
  },

  /** Dismiss (hard-delete) a single notification. The feed is
   * ephemeral — a dismissed entry is gone, not soft-deleted. Returns
   * whether a doc was actually removed. */
  async dismissNotification(id: string): Promise<boolean> {
    const response = await apiFetch(`${API_BASE}/me/notifications/${id}`, { method: 'DELETE' })
    if (!response.ok) throw new Error(`Failed to dismiss notification (${response.status})`)
    const data = await response.json()
    return data.deleted
  },

  /** Clear (hard-delete) every notification for the user — read and
   * unread alike. Returns the count actually deleted. */
  async clearAllNotifications(): Promise<number> {
    const response = await apiFetch(`${API_BASE}/me/notifications`, { method: 'DELETE' })
    if (!response.ok) throw new Error(`Failed to clear notifications (${response.status})`)
    const data = await response.json()
    return data.deleted
  },

  /** Read the user's role-scoped event list with current prefs.
   * Backend filters to events the user can ever be a recipient of
   * (#638), so the matrix UI never renders rows the user can't
   * receive. Returns an array (event order is stable: group, then
   * label). Labels + descriptions come from the backend metadata —
   * frontend no longer hardcodes them. */
  async getPreferences(): Promise<NotificationPreferencesResponse> {
    const response = await apiFetch(`${API_BASE}/me/notifications/preferences`)
    if (!response.ok) throw new Error(`Failed to load preferences (${response.status})`)
    const data = await response.json()
    return { events: data.events, roleContext: data.roleContext }
  },

  /** Save a (partial) matrix. Events present in the body are
   * upserted; events absent are left at their current value.
   * Out-of-scope event types in the body produce a 403 — the matrix
   * UI never renders them, but defense-in-depth guards against
   * stale builds / hand-crafted POSTs. Returns the post-write
   * scoped event list so the caller replaces in-memory state
   * without a follow-up GET. */
  async setPreferences(
    matrix: Partial<Record<NotificationEventType, NotificationTransportPrefs>>,
  ): Promise<NotificationPreferenceEvent[]> {
    const response = await apiFetch(`${API_BASE}/me/notifications/preferences`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ preferences: matrix }),
    })
    if (!response.ok) throw new Error(`Failed to save preferences (${response.status})`)
    const data = await response.json()
    return data.events
  },
}

export type NotificationEventType =
  | 'run.failed'
  | 'run.regressed'
  | 'run.completed'
  | 'schedule.dispatch_failed'
  | 'agent.budget_tripped'
  | 'model.health_failed'
  | 'model.unavailable'
  | 'release.available'
  | 'schedule.job_failed'
  | 'member.invited'
  | 'slack.integration_broken'

export interface NotificationTransportPrefs {
  inApp: boolean
  email: boolean
  /** Event-specific settings (string-keyed). Today only
   * `run.completed` populates it (with `pdfType: 'summary' |
   * 'detailed'`); other events leave it undefined. Open-typed
   * because Mongoose stores it as Mixed — future events can add
   * their own keys without a frontend schema bump. */
  settings?: Record<string, string>
}

export type NotificationEventGroup =
  | 'Runs'
  | 'Schedule'
  | 'Agents'
  | 'Membership'
  | 'Integrations'
  | 'Platform'

/** Recipient scope of an event, mirrored from the backend registry
 * (#1289 Slice 2). The settings UI reads it to badge each group with
 * the roles that see it and to fence the Global-Admin-only `Platform`
 * group (`kind === 'superadmin'`) apart. Kept structural (not a
 * pre-formatted string) so the plain-language wording lives in one
 * place on the frontend. */
export type NotificationScope =
  | { kind: 'superadmin' }
  | { kind: 'workspace'; roles: string[] }
  | { kind: 'org'; roles: string[] }
  | { kind: 'team' }
  | { kind: 'creatorAndTeam' }

/** One row in the Notifications settings matrix — the backend
 * filters this list to in-scope events for the requesting user. */
export interface NotificationPreferenceEvent {
  type: NotificationEventType
  label: string
  description: string
  group: NotificationEventGroup
  scope: NotificationScope
  prefs: NotificationTransportPrefs
}

/** One role the viewer holds on a named workspace or org. `role` is
 * the raw enum value; the UI maps it to plain-language copy. */
export interface NotificationRoleEntry {
  name: string
  role: string
}

/** The viewer's resolved roles, backing the Notifications screen's
 * role-context strip (#1289 Slice 2). Holds only roles actually held,
 * so the strip never invents a badge. */
export interface NotificationRoleContext {
  workspaces: NotificationRoleEntry[]
  orgs: NotificationRoleEntry[]
  isGlobalAdmin: boolean
}

/** The preferences GET payload: the scoped event rows plus the
 * viewer's role context for the strip. */
export interface NotificationPreferencesResponse {
  events: NotificationPreferenceEvent[]
  roleContext: NotificationRoleContext
}

export interface MemberWithUser {
  id: string
  organizationId: string
  userId: string
  role: OrgRole
  createdAt: string
  user: {
    id: string
    email: string
    name?: string
    image?: string
  }
}

// ── Invitations ────────────────────────────────────────────────────

export interface PendingInvitation {
  id: string
  email: string
  role: OrgRole
  status: 'pending' | 'accepted' | 'rejected' | 'canceled' | 'expired'
  expiresAt: string
  organizationId: string
  inviterId: string
}

export interface InvitationPreview {
  id: string
  email: string
  role: OrgRole
  organizationId: string
  organizationName?: string
  organizationSlug?: string
  inviterEmail?: string
  status: PendingInvitation['status']
  expiresAt: string
}

export const invitationsAPI = {
  /** List pending invitations for the signed-in user's email.
   * Powers the onboarding "you've been invited" surface. */
  async listMine(): Promise<{ invitations: InvitationPreview[] }> {
    const response = await apiFetch(`${API_BASE}/invitations/mine`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to load invitations')
    }
    return response.json()
  },

  /** Public preview — no session required. Lets the accept page
   * render org+role context before asking the user to sign in. */
  async preview(invitationId: string): Promise<InvitationPreview> {
    const response = await apiFetch(`${API_BASE}/invitations/${invitationId}`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Invitation not found')
    }
    return response.json()
  },

  /** Accept an invitation. Server enforces email match (403 if the
   * signed-in email differs from the invitation's email). */
  async accept(invitationId: string): Promise<unknown> {
    const response = await apiFetch(`${API_BASE}/invitations/${invitationId}/accept`, {
      method: 'POST',
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to accept invitation')
    }
    return response.json()
  },
}

// ── Recovery ───────────────────────────────────────────────────────

export interface RecoverableOrg {
  _id: string
  name: string
  slug: string
  deletedAt: string
  recoverableUntil: number
}

export interface RecoverableWorkspace {
  _id: string
  orgId: string
  name: string
  deletedAt: string
  recoverableUntil: number
}

/** Carries the HTTP status of a failed recovery so callers can tell the
 * two "can't recover" cases apart: 404 (already purged / never existed)
 * vs 410 (window expired, not yet purged). Matching on error text is
 * brittle, so the status rides the error object (#2510). */
export class RecoverError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
    this.name = 'RecoverError'
  }
}

export const recoveryAPI = {
  async list(): Promise<{ orgs: RecoverableOrg[]; workspaces: RecoverableWorkspace[] }> {
    const response = await apiFetch(`${API_BASE}/recover`)
    if (!response.ok) throw new Error('Failed to list recoverable items')
    return response.json()
  },

  async recoverOrg(orgId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/recover/orgs/${orgId}`, { method: 'POST' })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new RecoverError(response.status, err.error || 'Failed to recover org')
    }
  },

  async recoverWorkspace(workspaceId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/recover/workspaces/${workspaceId}`, { method: 'POST' })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to recover workspace')
    }
  },
}

export const orgsAPI = {
  async list(): Promise<OrgWithRole[]> {
    const response = await apiFetch(`${API_BASE}/orgs`)
    if (!response.ok) throw new Error('Failed to list orgs')
    return response.json()
  },

  async update(orgId: string, patch: { name: string }): Promise<OrgWithRole> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed to update org (${response.status})` }))
      throw new Error(err.error || 'Failed to update org')
    }
    return response.json()
  },

  async listMembers(orgId: string): Promise<{ members: MemberWithUser[] }> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/members`)
    if (!response.ok) throw new Error('Failed to list members')
    return response.json()
  },

  async changeMemberRole(orgId: string, memberId: string, role: OrgRole): Promise<unknown> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/members/${memberId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to change role')
    }
    return response.json()
  },

  async removeMember(orgId: string, memberId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/members/${memberId}`, {
      method: 'DELETE',
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to remove member')
    }
  },

  async leave(orgId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/leave`, { method: 'POST' })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to leave org')
    }
  },

  async createInvitation(
    orgId: string,
    payload: { email: string; role: OrgRole },
  ): Promise<PendingInvitation> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/invitations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to send invitation')
    }
    return response.json()
  },

  async listInvitations(orgId: string): Promise<PendingInvitation[]> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/invitations`)
    if (!response.ok) throw new Error('Failed to list invitations')
    const result = await response.json()
    // Better Auth returns either an array or { invitations: [] } across versions.
    return Array.isArray(result) ? result : (result.invitations ?? [])
  },

  async cancelInvitation(orgId: string, invitationId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/invitations/${invitationId}`, {
      method: 'DELETE',
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to cancel invitation')
    }
  },

  // Org creation lives in `adminOrgsAPI.create` — it's a superadmin-
  // only provisioning action. There is no self-serve
  // create-org affordance for regular users.

  /** Soft-delete (sets deletedAt; recovery within 30 days). Owner only. */
  async softDelete(orgId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Failed to delete org')
  },

  /** Read org settings (ottotester#211). Owner+admin only. The server
   * get-or-creates the doc, so callers always receive populated
   * defaults on first read. */
  async getSettings(orgId: string): Promise<OrgSettings> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/settings`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to read org settings')
    }
    return response.json()
  },

  /** Read which workspaces a first-time single-sign-on user can see,
   * plus the org's live workspaces to pick from (#1792). Owner+admin,
   * and only for an org whose plan includes single sign-on. */
  async getSsoSettings(orgId: string): Promise<OrgSsoSettings> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/settings/sso`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.message || err.error || 'Failed to read single sign-on settings')
    }
    return response.json()
  },

  /** Set which workspaces a first-time single-sign-on user can see
   * (#1792). An empty list is a valid choice: those people sign in
   * with no access until someone grants it. */
  async updateSsoSettings(
    orgId: string,
    defaultWorkspaceIds: string[],
  ): Promise<{ orgId: string; defaultWorkspaceIds: string[] }> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/settings/sso`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ defaultWorkspaceIds }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.message || err.error || 'Failed to update single sign-on settings')
    }
    return response.json()
  },

  /** Update the per-org agent run budgets (ottotester#211). Owner+admin
   * only. Server validates each value is a positive integer. */
  async updateRunBudgets(orgId: string, budgets: RunBudgets): Promise<OrgSettings> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/settings/run-budgets`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(budgets),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to update run budgets')
    }
    return response.json()
  },

  /** Read the org's plan-usage view (#139 phase 4, #766). Gated
   * server-side by `billing.read` — owner / admin / billing roles.
   * Read-only; returns usage against the org's tier limits plus the
   * period reset date. */
  async getUsage(orgId: string): Promise<OrgUsageView> {
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/usage`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to read usage')
    }
    return response.json()
  },
  // Org-facing audit log (#1119). Server-redacted rows; gated by
  // `audit.read` (owner/admin) on the route.
  async getAuditLog(
    orgId: string,
    opts: {
      limit?: number
      offset?: number
      category?: string
      since?: string
      until?: string
      actor?: string
    } = {},
  ): Promise<OrgAuditPage> {
    const params = new URLSearchParams()
    if (opts.limit != null) params.set('limit', String(opts.limit))
    if (opts.offset != null) params.set('offset', String(opts.offset))
    if (opts.category) params.set('category', opts.category)
    if (opts.since) params.set('since', opts.since)
    if (opts.until) params.set('until', opts.until)
    if (opts.actor) params.set('actor', opts.actor)
    const qs = params.toString() ? `?${params}` : ''
    const response = await apiFetch(`${API_BASE}/orgs/${orgId}/audit${qs}`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to read the audit log')
    }
    return response.json()
  },
}

/** One server-redacted row of the org audit log (#1119). Carries only
 * safe, derived fields — never the raw before/after/meta/targetId. */
export interface OrgAuditRow {
  id: string
  ts: string
  actor: string
  category: string
  action: string
  description: string
  result: 'success' | 'failure'
}

/** A page of the org audit log. No `total` — the collection is unbounded;
 * the client detects the end when `rows.length < limit`. */
export interface OrgAuditPage {
  rows: OrgAuditRow[]
  limit: number
  offset: number
}

/** Per-org agent iteration caps (ottotester#211). Each value is the
 * number of AgentRunner iterations the corresponding pipeline phase
 * gets per invocation; the planner additionally scales by the number
 * of test cases requested. */
export interface RunBudgets {
  iterationsPerTestCase: number
  iterationsPerScript: number
  iterationsPerHeal: number
}

/** What the org's Single sign-on tab needs in one read (#1792). The
 * workspace list is the org's live workspaces, so a workspace deleted
 * since the choice was made simply is not offered. */
export interface OrgSsoSettings {
  orgId: string
  workspaces: Array<{ _id: string; name: string }>
  /** Already filtered to workspaces that still exist. */
  defaultWorkspaceIds: string[]
}

/** Answer from the public sign-in lookup (#1792). `ssoEnabled` false is
 * the ordinary case: most addresses belong to no connected company. */
export interface SsoLookupResult {
  ssoEnabled: boolean
  organizationName?: string
  providerId?: string
}

export interface OrgSettings {
  orgId: string
  runBudgets: RunBudgets
  createdAt: string
  updatedAt: string
}

/** Same numbers the server's resolver falls back to. Surfaced as a
 * literal so the Settings UI can show defaults at render time
 * without a round-trip; the server is still the source of truth on
 * write. */
export const DEFAULT_RUN_BUDGETS: RunBudgets = {
  iterationsPerTestCase: 5,
  iterationsPerScript: 10,
  iterationsPerHeal: 10,
}

/** The ceilings a run is subject to (#2286). */
export interface RunLimits {
  /** Most tests a single run will plan, whatever is asked for. */
  maxTestCasesPerRun: number
}

/**
 * Ask the server what a run is limited to (#2286).
 *
 * There is deliberately no constant here any more. This number used to
 * be written into the bundle as a second copy of the server's, so
 * changing it meant editing both and shipping a build, and editing one
 * left the two disagreeing. The server owns it; the browser asks.
 *
 * No fallback either. A default of 5 in here would be the same second
 * copy under another name, and on a server set to something else the
 * screen would show 5 and then change. Callers get the number or
 * nothing, and show no limit until they have it — see `useRunLimits`.
 */
export const runLimitsAPI = {
  async get(): Promise<RunLimits> {
    const response = await apiFetch(`${ws()}/test-runs/limits`)
    if (!response.ok) throw new Error(`Failed to read run limits (${response.status})`)
    return response.json()
  },
}

// ============================================
// Plan usage visibility (#139 phase 4, #766)
// ============================================
//
// Read-only view of an org's plan usage against its limits.
// `orgsAPI.getUsage` is the org-facing single-org view (billing-role gated).

/** One limit dimension of the usage view — how much an org has used
 * and the cap it counts against. `limit: null` means the dimension is
 * unbounded (the `unlimited` tier) and should render as the word
 * "unlimited", never a number. */
export interface UsageDimension {
  used: number
  limit: number | null
}

/** The composed plan-usage view for one org. `cost` is in integer
 * cents and is surfaced to the customer as *estimated platform usage*,
 * not an invoice figure. `periodResetAt` is the ISO instant the cost +
 * run counters roll over (the script count does not reset). */
export interface OrgUsageView {
  orgId: string
  /** The org's current plan id (`Plan._id`), or `null` when on no plan.
   * This — not `tier` — is what the admin Billing "Plan" column reads (#2023). */
  planId: string | null
  /** Display name of the org's current plan, or `null` when it has no plan.
   * Rendered in the "Plan" column and preselected in the picker (#2023). */
  planName: string | null
  /** Whether the org is classified internal (runs unbounded, not billed).
   * Gates the internal-plan option in the picker (#2023). */
  internal: boolean
  /** @deprecated since #1037, superseded by `planId`. Still on the wire but
   * the UI no longer reads it for "Plan" (#2023). */
  tier: string
  scripts: UsageDimension
  cost: UsageDimension
  runs: UsageDimension
  /** Application-count dimension (#780). Live count, not a period
   * counter — apps accumulate across periods and do not reset. */
  applications: UsageDimension
  /** Workspace-count dimension (#1037 PR-A.2). Live count. */
  workspaces: UsageDimension
  /** Distinct user-account-count dimension (#1037 PR-A.2). */
  userAccounts: UsageDimension
  period: string
  periodResetAt: string
  /** Per-user breakdown of this period's AI spend (#1721) — who drove
   * the `cost` dimension. Rows reconcile to `cost.used`; a
   * "System / automated" row holds unattributed spend and "Former user"
   * holds deleted accounts. Empty on the superadmin cross-org table
   * (which shows totals only). */
  perUser: PerUserSpend
}

// ============================================
// BI / Analytics (#482, Slice C; relocated to admin-tier in #429
// Wave 3 Track E)
// ============================================
//
// Cross-org dashboard endpoints under `/api/admin/bi/*`. Backed by
// the Mongo BI store (runMetrics + runScore + modelPricing). Gated
// server-side by `requireSuperadmin` (`isSuperadmin` flag + opt-in
// `x-superadmin-mode: on` header). Frontend gates the
// `/admin/analytics` route via `<RequireSuperadmin>`; only
// superadmins reach these endpoints. The previous workspace-scoped
// mount at `/api/workspaces/:wid/bi/*` was removed when Analytics
// moved to `/admin/analytics`.

/** Common filter query the three list endpoints accept. All
 * fields optional; the backend defaults to the workspace-wide
 * view over the last 30 days, cap 100 runs. */
// ============================================
// Admin BI Pickers API (#516 v1)
// ============================================
//
// Feeds the Analytics FilterBar cascade. Cross-org / admin-tier
// only; mounted at `/api/admin/bi-pickers/*`.

export interface WorkspacePickerOption {
  id: string
  name: string
  orgId: string
}

export interface ApplicationPickerOption {
  id: string
  name: string
  workspaceId: string
}

export interface VariantPickerOption {
  id: string
  name: string
  applicationId: string
  baseUrl?: string
}

export interface TestCasePickerOption {
  id: string
  name: string
  variantId: string
}

/** One option in the Analytics "Run" picker (#1577). Mirrors the
 * server's `RunPickerRow` — outcome + type + time + id (the BI store
 * has no run name). */
export interface RunPickerOption {
  runId: string
  completedAt: string
  runType: 'discovery' | 'replay' | null
  testsPassed: number
  testsFailed: number
  testsSkipped: number
}

export const adminBiPickersAPI = {
  async listWorkspaces(orgId: string): Promise<WorkspacePickerOption[]> {
    const response = await apiFetch(`${API_BASE}/admin/bi-pickers/workspaces?orgId=${encodeURIComponent(orgId)}`)
    if (!response.ok) throw new Error('Failed to list workspaces')
    return response.json()
  },
  async listApplications(opts: { workspaceId?: string; orgId?: string }): Promise<ApplicationPickerOption[]> {
    const params = new URLSearchParams()
    if (opts.workspaceId) params.set('workspaceId', opts.workspaceId)
    else if (opts.orgId) params.set('orgId', opts.orgId)
    const response = await apiFetch(`${API_BASE}/admin/bi-pickers/applications?${params.toString()}`)
    if (!response.ok) throw new Error('Failed to list applications')
    return response.json()
  },
  async listVariants(applicationId: string): Promise<VariantPickerOption[]> {
    const response = await apiFetch(`${API_BASE}/admin/bi-pickers/variants?applicationId=${encodeURIComponent(applicationId)}`)
    if (!response.ok) throw new Error('Failed to list variants')
    return response.json()
  },
  /** Typeahead lookup. Empty `q` returns the first 50 alphabetically.
   * `pinnedId`, when supplied, ensures that one case is pinned to
   * the response even if it sorts past the 50-row alphabetical
   * window — so the FilterBar's "chosen label" lookup never falls
   * back to an 8-char id prefix for a past-50 pick. */
  async listTestCases(variantId: string, q = '', pinnedId?: string): Promise<TestCasePickerOption[]> {
    const params = new URLSearchParams({ variantId })
    if (q) params.set('q', q)
    if (pinnedId) params.set('id', pinnedId)
    const response = await apiFetch(`${API_BASE}/admin/bi-pickers/test-cases?${params.toString()}`)
    if (!response.ok) throw new Error('Failed to list test cases')
    return response.json()
  },
  /** Recent runs for the Analytics "Run" picker (#1577), scoped by the
   * active filter and newest-first. */
  async listRuns(opts: {
    orgId?: string
    workspaceId?: string
    applicationId?: string
    variantId?: string
    /** Custom range bounds (ISO date strings). When `from` is set it
     * wins over `sinceDays`, matching the chart routes. */
    from?: string
    to?: string
    sinceDays?: number
    limit?: number
  }): Promise<RunPickerOption[]> {
    const params = new URLSearchParams()
    if (opts.orgId) params.set('orgId', opts.orgId)
    if (opts.workspaceId) params.set('workspaceId', opts.workspaceId)
    if (opts.applicationId) params.set('applicationId', opts.applicationId)
    if (opts.variantId) params.set('variantId', opts.variantId)
    if (opts.from) params.set('from', opts.from)
    if (opts.to) params.set('to', opts.to)
    if (opts.sinceDays !== undefined) params.set('sinceDays', String(opts.sinceDays))
    if (opts.limit !== undefined) params.set('limit', String(opts.limit))
    const response = await apiFetch(`${API_BASE}/admin/bi-pickers/runs?${params.toString()}`)
    if (!response.ok) throw new Error('Failed to list runs')
    return response.json()
  },

  /** Distinct model specs across the scoped runs, for the Analytics
   * Model picker (#1650). Scoped by every active filter EXCEPT `modelSpec`
   * (intentionally not a param) so the dropdown never traps to its own
   * selection. */
  async listModels(opts: {
    orgId?: string
    workspaceId?: string
    applicationId?: string
    variantId?: string
    from?: string
    to?: string
    sinceDays?: number
    runType?: BiFilter['runType']
    agent?: string
  }): Promise<string[]> {
    const params = new URLSearchParams()
    if (opts.orgId) params.set('orgId', opts.orgId)
    if (opts.workspaceId) params.set('workspaceId', opts.workspaceId)
    if (opts.applicationId) params.set('applicationId', opts.applicationId)
    if (opts.variantId) params.set('variantId', opts.variantId)
    if (opts.from) params.set('from', opts.from)
    if (opts.to) params.set('to', opts.to)
    if (opts.sinceDays !== undefined) params.set('sinceDays', String(opts.sinceDays))
    if (opts.runType) params.set('runType', opts.runType)
    if (opts.agent) params.set('agent', opts.agent)
    const response = await apiFetch(`${API_BASE}/admin/bi-pickers/models?${params.toString()}`)
    if (!response.ok) throw new Error('Failed to list models')
    return response.json()
  },
}

export interface BiFilter {
  /** Cross-org admin filter (#516 v1). When set, the backend
   * resolves the org to its non-soft-deleted workspace ids and
   * scopes every chart query accordingly. */
  orgId?: string
  /** Single-workspace admin filter (#516 v1). Narrows the org
   * scope to one workspace. */
  workspaceId?: string
  applicationId?: string
  variantId?: string
  /** Test case scope. After #516 v1 every chart honors this except
   * Model Comparison (its scores key on testName, not testCaseId
   * — a follow-up join). */
  testCaseId?: string
  /** Window size in days — preset shortcut (7/30/90). Backend
   * enforces 1..365. Ignored when `from` is set (custom range
   * wins). */
  sinceDays?: number
  /** Custom-range lower bound (ISO 8601 date). When set,
   * `sinceDays` is ignored server-side. */
  from?: string
  /** Custom-range upper bound (ISO 8601 date). Optional — absence
   * means "now." */
  to?: string
  /** Run type (#1650). A single encoded value the backend decodes into
   * `{ runType, isResume }`: `discovery` (fresh) / `discovery_resume` /
   * `replay`. */
  runType?: 'discovery' | 'discovery_resume' | 'replay'
  /** "Runs where this agent fired" (#1650) — one of the five in-run
   * agents (planner/generator/healer/extractor/auditor). */
  agent?: string
  /** "Runs that used this model spec" (#1650). */
  modelSpec?: string
  /** Max runs to scan. Backend enforces 1..500. */
  limit?: number
}

export interface ToolCallsByAgentRow {
  agent: string
  tool: string
  count: number
}
export interface ToolCallsByAgentPayload {
  runCount: number
  rows: ToolCallsByAgentRow[]
}

export interface CostByAgentRunRow {
  runId: string
  completedAt: string
  byAgent: Record<string, number>
}
export interface CostByAgentPayload {
  runs: CostByAgentRunRow[]
}

export interface TokensByModelPoint {
  date: string
  tokens: number
}
export interface TokensByModelSeries {
  modelSpec: string
  points: TokensByModelPoint[]
}
export interface TokensByModelPayload {
  series: TokensByModelSeries[]
}

export interface RunDrillDownScoreEntry {
  testName: string
  dimension: string
  value: number
  comment?: string
}

export interface RunDrillDownPayload {
  runMetrics: {
    runId: string
    workspaceId: string
    applicationId?: string
    variantId?: string
    runType?: string
    startedAt: string
    completedAt: string
    pipelineWallMs: number
    playwrightRuntimeMs: number
    testCaseCount: number
    testScriptCount: number
    testsPassed: number
    testsFailed: number
    testsSkipped: number
    testsErrored: number
    byCategory: Record<string, number>
    byAgent: Record<string, {
      iters: number
      promptTokens: number
      completionTokens: number
      totalTokens: number
      cacheReadTokens: number
      cacheWriteTokens: number
      costCents: number
      /** Where `costCents` came from (#2346). THREE states, unlike the
       * model row's two: an agent can use several models in one run and
       * they need not agree, so `mixed` is the honest answer when part of
       * its spend was billed and part was worked out.
       *
       * Absent on rows written before #2346, which is not the same as
       * `estimated` — a screen shows nothing rather than guessing. */
      costSource?: 'reported' | 'estimated' | 'mixed'
      /** Wall time this agent ran. `0` means not measured, not instant —
       * runs recorded before #2006 timed only the planner and generator.
       * The table renders a falsy value as an em dash. */
      durationMs: number
      /** The model this agent used, when the rollup captured one (#2006).
       * The drill-down's model column falls back to this for agents with
       * no agent-execution row, such as embedding. */
      modelSpec?: string
      tools: Record<string, { callCount: number }>
    }>
    byTool: Record<string, { callCount: number }>
    byModel: Record<string, {
      promptTokens: number
      completionTokens: number
      totalTokens: number
      cacheReadTokens: number
      cacheWriteTokens: number
      /** Thinking tokens (#2249). A subset of `completionTokens`, never an
       * addition. The backend has written this since #2249; this mirror
       * never declared it, so it did not exist to the UI. */
      reasoningTokens?: number
      costCents: number
      /** Where `costCents` came from (#2334). Two states here: a model row
       * describes one model, which was either billed or worked out. */
      costSource?: 'reported' | 'estimated'
    }>
    totalCostCents: number
    totalTokens: number
    phoenixTraceId?: string
  }
  scoreSummary: {
    tqi: number
    perDimension: Partial<Record<'behaviorCorrectness' | 'assertionQuality' | 'flakeResistance' | 'locatorStrategy' | 'maintainability', number>>
    testCount: number
    schemaVersion?: number
  }
  scores: RunDrillDownScoreEntry[]
  phoenixTraceUrl: string | null
  hintsRetrieved?: RetrievedHintRow[]
  /** Per-run plan-quality radar (#1583); null when no plan audit. */
  planRadar: RunPlanRadar | null
  /** Per-run heal-quality radar (#1583); null when nothing was healed. */
  healRadar: RunHealRadar | null
  /** Which model each agent ran, keyed by agent name (#1583). Empty for
   * pre-#645 runs with no AgentExecution rows. */
  agentModels: Record<string, string>
}

/** Per-run PQI radar — mirrors the server's `RunPlanRadar` (#1583). */
export interface RunPlanRadar {
  objectiveCompletion: number
  oracleQuality: number
  stepExecutability: number
  pqi: number
}

/** Per-run HQI radar — mirrors the server's `RunHealRadar` (#1583).
 * #1608: four fixed dimensions, no N/A-able axis. */
export interface RunHealRadar {
  intentPreservation: number
  repairCorrectness: number
  oraclePreservation: number
  patchMinimality: number
  hqi: number
  healCount: number
}

/** One row of the per-run retrieved-hints drill-down. Mirrors the
 * shape returned by `GET /bi/runs/:runId`. The agent's actually-shown
 * hints are in `topkHints` for topk/dual modes (where `ragSkipped`
 * is true or `ragHints` is the parallel observation) and in `ragHints`
 * for rag-mode rows (where `topkHints` is empty). */
export interface RetrievedHintRow {
  id: string
  agentPhase: 'healer' | 'generator'
  queryText: string
  overlap: number
  ragSkipped: boolean
  ragFailed: boolean
  ragFailureReason?: string
  createdAt: string
  topkHints: RetrievedHintEntry[]
  ragHints: Array<RetrievedHintEntry & { score?: number; rank?: number }>
}

export interface RetrievedHintEntry {
  id: string
  category?: string
  element?: string
  pattern?: string
  antipattern?: string
  selector?: string
}

function biFilterQuery(filter?: BiFilter): string {
  if (!filter) return ''
  const params = new URLSearchParams()
  if (filter.orgId) params.set('orgId', filter.orgId)
  if (filter.workspaceId) params.set('workspaceId', filter.workspaceId)
  if (filter.applicationId) params.set('applicationId', filter.applicationId)
  if (filter.variantId) params.set('variantId', filter.variantId)
  if (filter.testCaseId) params.set('testCaseId', filter.testCaseId)
  // sinceDays only applies when the custom range is unset (matches
  // the server's parseFilter precedence — `from` wins over
  // sinceDays).
  if (filter.from) params.set('from', filter.from)
  if (filter.to) params.set('to', filter.to)
  if (!filter.from && filter.sinceDays !== undefined) {
    params.set('sinceDays', String(filter.sinceDays))
  }
  if (filter.limit !== undefined) params.set('limit', String(filter.limit))
  // #1650 run/agent/model WHERE-filters. EVERY run-grained chart, KPI,
  // and report endpoint serializes its filter through here — so these
  // three must be emitted here too, or the pickers update the URL but
  // the data fetch silently drops them (the bug this line fixes).
  // `runType` is the single ENCODED value (`discovery` |
  // `discovery_resume` | `replay`); the server's parseFilter decodes it.
  if (filter.runType) params.set('runType', filter.runType)
  if (filter.agent) params.set('agent', filter.agent)
  if (filter.modelSpec) params.set('modelSpec', filter.modelSpec)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

// Slice D themed bundles — one fetch per section.

export interface KpiSummaryPayload {
  runCount: number
  totalCostCents: number
  totalTokens: number
  meanTqi: number | null
  totalPlaywrightRuntimeMs: number
  totalPipelineWallMs: number
  windowStart: string | null
  windowEnd: string | null
}

export interface TqiTrendPoint {
  runId: string
  completedAt: string
  tqi: number
}

export interface TqiRadar {
  behaviorCorrectness: number
  assertionQuality: number
  flakeResistance: number
  locatorStrategy: number
  maintainability: number
}

export interface TqiDistributionBucket {
  bucket: number
  count: number
}

export interface AntiPatternRatePoint {
  date: string
  emptyAssertion: number
  urlRegexOr: number
  silentSkipGuard: number
  totalTests: number
}

export interface QualityChartsPayload {
  tqiTrend: TqiTrendPoint[]
  radar: TqiRadar | null
  distribution: TqiDistributionBucket[]
  antiPatternRate: AntiPatternRatePoint[]
}

// ── Plan quality charts (PQI, #635) ─────────────────────────────

export interface PqiTrendPoint {
  runId: string
  completedAt: string
  pqi: number
}

export interface PqiRadar {
  objectiveCompletion: number
  oracleQuality: number
  stepExecutability: number
}

export interface PqiDistributionBucket {
  bucket: number
  count: number
}

export interface PqiFlagRatePoint {
  date: string
  testWithNoExpects: number
  outcomeStepMissingExpect: number
  vacuousExpect: number
  duplicateTestNames: number
  missingFixtureForRole: number
  totalPlans: number
}

export interface PlanQualityChartsPayload {
  pqiTrend: PqiTrendPoint[]
  radar: PqiRadar | null
  distribution: PqiDistributionBucket[]
  flagRate: PqiFlagRatePoint[]
  totalPlans: number
}

// ── CQI charts (candidate quality, #2101 Slice 3b) ──────────────
// Hand-mirrored from `dashboardService.ts` — no codegen, so these move
// with the backend payload types.

export interface CqiTrendPoint {
  /** ISO 8601 UTC day — the trend is a DAILY MEAN of the after-refiner CQI. */
  completedAt: string
  cqi: number
}

export interface CqiDimensionMean {
  requirementsFidelity: number
  testability: number
}

export interface CqiDistributionBucket {
  bucket: number
  count: number
}

export interface CandidateQualityChartsPayload {
  cqiTrend: CqiTrendPoint[]
  dimensionMean: CqiDimensionMean | null
  distribution: CqiDistributionBucket[]
  totalCandidates: number
}

// ── Heal quality charts (HQI, #674) ─────────────────────────────

export interface HqiTrendPoint {
  runId: string
  testName: string
  completedAt: string
  hqi: number
}

/** #1608: four fixed dimensions — no N/A-able axis remains. */
export interface HqiRadar {
  intentPreservation: number
  repairCorrectness: number
  oraclePreservation: number
  patchMinimality: number
}

export interface HqiDistributionBucket {
  bucket: number
  count: number
}

export interface HqiDeltaFlagRatePoint {
  date: string
  introducedEmptyAssertion: number
  introducedUrlRegexOr: number
  introducedSilentSkipGuard: number
  testRemovedOrSkipped: number
  /** Derived flag — count of heals on this day whose re-run
   * failed (`!deltaFlags.healedTestPassed`). */
  healedTestReRunFailed: number
  totalHeals: number
}

export interface HealQualityChartsPayload {
  hqiTrend: HqiTrendPoint[]
  radar: HqiRadar | null
  distribution: HqiDistributionBucket[]
  flagRate: HqiDeltaFlagRatePoint[]
  totalHeals: number
}

export interface CacheHitRatioRow {
  agent: string
  ratio: number | null
  promptTokens: number
  cacheReadTokens: number
}

export interface WallClockRow {
  runId: string
  completedAt: string
  agentMs: number
  playwrightMs: number
}

export interface PassRateVsCostPoint {
  runId: string
  passRate: number
  costCents: number
  testCount: number
}

export interface EfficiencyChartsPayload {
  cacheHitRatio: CacheHitRatioRow[]
  wallClock: WallClockRow[]
  passRateVsCost: PassRateVsCostPoint[]
}

export interface IterationsByAgentRow {
  runId: string
  completedAt: string
  byAgent: Record<string, number>
}

export interface TopToolRow {
  tool: string
  count: number
}

export interface ScheduleHeatmapCell {
  dayOfWeek: number
  hour: number
  count: number
}

export interface ActivityChartsPayload {
  iterationsByAgent: IterationsByAgentRow[]
  topTools: TopToolRow[]
  scheduleHeatmap: ScheduleHeatmapCell[]
}

// Agent Memory effectiveness — joins hint retrieval rows against
// run-level outcomes (TQI, pass rate) to answer "is this hint
// pulling weight?". `baselineKind` flags the comparison as a
// heuristic (filter-matched runs that did NOT see the hint), not a
// controlled experiment — the UI surfaces a caption to make that
// honest.

export interface AgentMemoryHintRow {
  hintId: string
  summary: string
  category?: string
  shownCount: number
  shownRunCount: number
  meanTqiWhenShown: number | null
  meanTqiBaseline: number | null
  passRateWhenShown: number | null
  passRateBaseline: number | null
}

export interface AgentMemoryChartsPayload {
  retrievalCount: number
  retrievalRunCount: number
  hints: AgentMemoryHintRow[]
  baselineKind: 'heuristic' | 'none'
}

// ── Model comparison (#492; cross-agent #711 PR 2a) ─────────────

export type ModelComparisonAgent = 'candidate' | 'generator' | 'planner' | 'healer'

// Box-plot dimensions across the agents — the composite
// (cqi/tqi/pqi/hqi) plus each agent's sub-dimensions.
export type ModelComparisonDimension =
  // generator
  | 'tqi'
  | 'behaviorCorrectness'
  | 'assertionQuality'
  | 'flakeResistance'
  | 'locatorStrategy'
  | 'maintainability'
  // planner
  | 'pqi'
  | 'objectiveCompletion'
  | 'oracleQuality'
  | 'stepExecutability'
  // healer
  | 'hqi'
  | 'intentPreservation'
  | 'repairCorrectness'
  | 'oraclePreservation'
  | 'patchMinimality'
  // candidate (CQI, #2101 Slice 3c)
  | 'cqi'
  | 'requirementsFidelity'
  | 'testability'

export interface ModelDimensionDistribution {
  model: string
  dimension: ModelComparisonDimension
  count: number
  min: number
  q1: number
  median: number
  q3: number
  max: number
  mean: number
}

export interface ModelLeaderboardRow {
  model: string
  /** Which proposal pass produced this row (#2101 Slice 3c) — set only for
   * the `candidate` agent (proposer / refiner), whose scatter colors by pass.
   * Undefined for the run-based agents. */
  pass?: 'proposer' | 'refiner'
  runCount: number
  scoreCount: number
  meanQuality: number
  costCentsPerTest: number | null
  tokensPerRun: number | null
  durationMsPerRun: number | null
  itersPerRun: number | null
  qualityPerDollar: number | null
}

export interface ModelTrendPoint {
  date: string
  value: number
}

export interface ModelTrendSeries {
  model: string
  points: ModelTrendPoint[]
}

export interface ModelComparisonPayload {
  agent: ModelComparisonAgent
  dimensions: ModelComparisonDimension[]
  distributions: ModelDimensionDistribution[]
  leaderboard: ModelLeaderboardRow[]
  qualityTrends: ModelTrendSeries[]
  costTrends: ModelTrendSeries[]
  scoreCount: number
  unattributedScoreCount: number
}

// ── Quality detail reports (#672) ──────────────────────────────────
// Row-level Plan/Test quality reports. Mirrors the backend types in
// `application/bi/qualityReports.ts`.

export type ScoreBand = 'excellent' | 'good' | 'fair' | 'poor'

export interface ReportScope {
  orgId: string | null
  orgName: string | null
  workspaceId: string
  workspaceName: string | null
  applicationId: string | null
  applicationName: string | null
  variantId: string | null
  variantName: string | null
}

export interface ArtifactTokenUsage {
  promptTokens: number
  completionTokens: number
  totalTokens: number
}

export interface RowEfficiency {
  costCents: number | null
  qiPerDollar: number | null
  qiPer1MTokens: number | null
}

export interface TestQualityReportRow {
  runId: string
  testName: string
  auditedAt: string
  scope: ReportScope
  model: string | null
  tokens: ArtifactTokenUsage | null
  /** Wall-clock ms spent producing this artifact (#1998). Null on rows
   * predating duration capture. Hand-mirrored like the rest of this file —
   * there is no codegen, so this has to move with the backend row type. */
  durationMs: number | null
  tqi: number
  band: ScoreBand
  behaviorCorrectness: number
  assertionQuality: number
  flakeResistance: number
  locatorStrategy: number
  maintainability: number
  antiPatterns: { emptyAssertion: boolean; urlRegexOr: boolean; silentSkipGuard: boolean }
  evidence: {
    actionPerformed: string
    strongestAssertion: string
    behaviorEvidence: string
    flakeEvidence: string
    locatorEvidence: string
    maintainabilityEvidence: string
  }
  efficiency: RowEfficiency
}

export interface PlanQualityReportRow {
  runId: string
  auditedAt: string
  scope: ReportScope
  model: string | null
  tokens: ArtifactTokenUsage | null
  /** Wall-clock ms spent producing this artifact (#1998). Null on rows
   * predating duration capture. Hand-mirrored like the rest of this file —
   * there is no codegen, so this has to move with the backend row type. */
  durationMs: number | null
  pqi: number
  band: ScoreBand
  objectiveCompletion: number
  testName: string | null
  oracleQuality: number | null
  stepExecutability: number | null
  flags: {
    testWithNoExpects: number
    outcomeStepMissingExpect: number
    vacuousExpect: number
    duplicateTestNames: number
    missingFixtureForRole: number
  }
  planEvidence: { requestSummary: string; objectiveEvidence: string }
  testEvidence: { representativeStep: string; strongestExpect: string } | null
  efficiency: RowEfficiency
}

export interface HealQualityReportRow {
  runId: string
  testName: string
  auditedAt: string
  scope: ReportScope
  model: string | null
  tokens: ArtifactTokenUsage | null
  /** Wall-clock ms spent producing this artifact (#1998). Null on rows
   * predating duration capture. Hand-mirrored like the rest of this file —
   * there is no codegen, so this has to move with the backend row type. */
  durationMs: number | null
  hqi: number
  band: ScoreBand
  intentPreservation: number
  repairCorrectness: number
  oraclePreservation: number
  patchMinimality: number
  deltaFlags: {
    introducedEmptyAssertion: boolean
    introducedUrlRegexOr: boolean
    introducedSilentSkipGuard: boolean
    testRemovedOrSkipped: boolean
    assertionCountDelta: number
    healedTestPassed: boolean
  }
  evidence: {
    failureContext: string
    repairEvidence: string
    intentEvidence: string
    oracleEvidence: string
    patchEvidence: string
  }
  efficiency: RowEfficiency
}

/** One graded pass of a candidate (#2101 Slice 3b). Hand-mirrored from
 * `qualityReports.ts`. Null on the report row for a pass that wasn't
 * scored (a refiner-dropped candidate has no refiner block). */
export interface CandidatePassBlock {
  /** The Auditor replied but its scores could not be read: the block keeps
   * its evidence, and its CQI, band and scores are null. */
  notGraded: boolean
  cqi: number | null
  band: ScoreBand | null
  requirementsFidelity: number | null
  testability: number | null
  producedByModel: string | null
  candidateTestId: string | null
  evidence: { requestSummary: string; fidelityEvidence: string; testabilityEvidence: string }
}

export interface CandidateQualityReportRow {
  proposalId: string
  candidateOrdinal: number
  candidateName: string
  intent: string
  auditedAt: string
  scope: ReportScope
  /** Headline facet fields — the after-refiner pass. `model` is null for a
   * dropped candidate, so a Model filter excludes it. `band` is null when
   * the pass it comes from is not graded, so no band filter matches it. */
  model: string | null
  band: ScoreBand | null
  proposer: CandidatePassBlock | null
  refiner: CandidatePassBlock | null
}

/** One row of the per-test Cost report (#2146). Hand-mirrored from
 * `qualityReports.ts` — no codegen, so this moves with the backend row.
 * Cents are fractional (shared stages are divided across the candidates /
 * tests that shared them); a null stage renders as a dash. */
export interface TestCostRow {
  testCaseId: string
  testName: string
  scope: ReportScope
  proposalCents: number | null
  planCents: number | null
  generationCents: number | null
  healCents: number | null
  totalCents: number | null
  proposalCandidateCount: number | null
  planTestCount: number | null
  healCount: number
}

export interface QualityReport<TRow> {
  rows: TRow[]
  truncated: boolean
  rowCap: number
  generatedAt: string
}

export type CsvMode = 'scores' | 'full'

// ── Hint-efficacy report (#694 slice 6) ────────────────────────────
// Per-hint efficacy table + corpus rollup. Mirrors the backend types
// in `application/bi/efficacyReport.ts`.

/** Where a hint sits in the 3-way corpus rollup. */
export type EfficacyBucket = 'effective' | 'deadWeight' | 'unrated'

export interface HintEfficacyRow {
  hintId: string
  scope: ReportScope
  category: string
  summary: string
  injectedCount: number
  adoptedCount: number
  effectiveCount: number
  failureCount: number
  /** Smoothed efficacy rate in (0, 1) — the Beta posterior mean, NOT
   * the raw effective/adopted ratio. */
  efficacyRate: number
  /** Combined adopted / injected, or null when never injected. Prefer
   * `healAdoptionRate` — the combined rate under-reports (#1656). */
  adoptionRate: number | null
  /** Per-phase heal counters (#1656). */
  injectedCountHealer: number
  adoptedCountHealer: number
  effectiveCountHealer: number
  /** Honest heal-phase adoption rate, or null when never heal-injected. */
  healAdoptionRate: number | null
  /** Honest generator-phase adoption rate, or null until the generator
   * auditor ships (#1657). */
  generatorAdoptionRate: number | null
  bucket: EfficacyBucket
  active: boolean
  createdAt: string
}

export interface EfficacyCorpusRollup {
  corpusSize: number
  effectiveCount: number
  deadWeightCount: number
  unratedCount: number
  totalAdoptions: number
  thresholds: {
    /** Minimum `adoptedCount` for a hint to be "rated". */
    ratedMinAdoptions: number
    /** Neutral prior mean — the effective / dead-weight cut point. */
    priorMean: number
  }
}

export interface HintEfficacyReport {
  rows: HintEfficacyRow[]
  rollup: EfficacyCorpusRollup
  truncated: boolean
  rowCap: number
  generatedAt: string
}

/** Base path for BI endpoints. Single-mount today (`/api/admin/bi`,
 * admin-tier per #429 Wave 3 Track E). A future per-customer view
 * would add a workspace-scoped mount and switch this base by the
 * active `AnalyticsScope`. Kept as a constant so the swap is
 * one-line when that happens. */
const BI_BASE = `${API_BASE}/admin/bi`

export const biAPI = {
  async toolCallsByAgent(filter?: BiFilter): Promise<ToolCallsByAgentPayload> {
    const response = await apiFetch(`${BI_BASE}/charts/tool-calls-by-agent${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch tool-calls chart')
    return response.json()
  },

  async costByAgent(filter?: BiFilter): Promise<CostByAgentPayload> {
    const response = await apiFetch(`${BI_BASE}/charts/cost-by-agent${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch cost-by-agent chart')
    return response.json()
  },

  async tokensByModel(filter?: BiFilter): Promise<TokensByModelPayload> {
    const response = await apiFetch(`${BI_BASE}/charts/tokens-by-model${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch tokens-by-model chart')
    return response.json()
  },

  async runDrillDown(runId: string): Promise<RunDrillDownPayload | null> {
    const response = await apiFetch(`${BI_BASE}/runs/${encodeURIComponent(runId)}`)
    if (response.status === 404) return null
    if (!response.ok) throw new Error('Failed to fetch run drill-down')
    return response.json()
  },

  async kpiSummary(filter?: BiFilter): Promise<KpiSummaryPayload> {
    const response = await apiFetch(`${BI_BASE}/charts/kpi-summary${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch KPI summary')
    return response.json()
  },

  async quality(filter?: BiFilter): Promise<QualityChartsPayload> {
    const response = await apiFetch(`${BI_BASE}/charts/quality${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch quality charts')
    return response.json()
  },

  async planQuality(filter?: BiFilter): Promise<PlanQualityChartsPayload> {
    const response = await apiFetch(`${BI_BASE}/charts/plan-quality${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch plan quality charts')
    return response.json()
  },

  async healQuality(filter?: BiFilter): Promise<HealQualityChartsPayload> {
    const response = await apiFetch(`${BI_BASE}/charts/heal-quality${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch heal quality charts')
    return response.json()
  },

  async candidateQuality(filter?: BiFilter): Promise<CandidateQualityChartsPayload> {
    const response = await apiFetch(`${BI_BASE}/charts/candidate-quality${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch candidate quality charts')
    return response.json()
  },

  async efficiency(filter?: BiFilter): Promise<EfficiencyChartsPayload> {
    const response = await apiFetch(`${BI_BASE}/charts/efficiency${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch efficiency charts')
    return response.json()
  },

  async activity(filter?: BiFilter): Promise<ActivityChartsPayload> {
    const response = await apiFetch(`${BI_BASE}/charts/activity${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch activity charts')
    return response.json()
  },

  async agentMemory(filter?: BiFilter): Promise<AgentMemoryChartsPayload> {
    const response = await apiFetch(`${BI_BASE}/charts/agent-memory${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch Agent Memory charts')
    return response.json()
  },

  async modelComparison(
    filter?: BiFilter,
    agent?: ModelComparisonAgent,
  ): Promise<ModelComparisonPayload> {
    const base = `${BI_BASE}/charts/model-comparison${biFilterQuery(filter)}`
    const url = agent ? `${base}${base.includes('?') ? '&' : '?'}agent=${agent}` : base
    const response = await apiFetch(url)
    if (!response.ok) throw new Error('Failed to fetch model comparison charts')
    return response.json()
  },

  // Quality detail reports (#672).
  async testQualityReport(filter?: BiFilter): Promise<QualityReport<TestQualityReportRow>> {
    const response = await apiFetch(`${BI_BASE}/reports/test-quality${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch test quality report')
    return response.json()
  },

  async planQualityReport(filter?: BiFilter): Promise<QualityReport<PlanQualityReportRow>> {
    const response = await apiFetch(`${BI_BASE}/reports/plan-quality${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch plan quality report')
    return response.json()
  },

  async healQualityReport(filter?: BiFilter): Promise<QualityReport<HealQualityReportRow>> {
    const response = await apiFetch(`${BI_BASE}/reports/heal-quality${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch heal quality report')
    return response.json()
  },

  async candidateQualityReport(filter?: BiFilter): Promise<QualityReport<CandidateQualityReportRow>> {
    const response = await apiFetch(`${BI_BASE}/reports/candidate-quality${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch candidate quality report')
    return response.json()
  },

  // Test cost report (#2146) — the Cost tab's "By test" lens.
  async testCostReport(filter?: BiFilter): Promise<QualityReport<TestCostRow>> {
    const response = await apiFetch(`${BI_BASE}/reports/test-cost${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch test cost report')
    return response.json()
  },

  // Hint-efficacy report (#694 slice 6).
  async hintEfficacyReport(filter?: BiFilter): Promise<HintEfficacyReport> {
    const response = await apiFetch(`${BI_BASE}/reports/hint-efficacy${biFilterQuery(filter)}`)
    if (!response.ok) throw new Error('Failed to fetch hint efficacy report')
    return response.json()
  },

  /** Fetch a report as a CSV blob. One method for all three reports —
   * `report` selects the endpoint, `mode` the column set. The
   * optional `facets` carry the table's current Model / Score-band
   * selections; the server re-applies them so the download matches
   * what the operator currently sees. */
  async reportCsv(
    report: 'test-quality' | 'plan-quality' | 'heal-quality' | 'candidate-quality' | 'test-cost',
    mode: CsvMode,
    filter?: BiFilter,
    facets?: { model?: string; band?: string },
  ): Promise<Blob> {
    const qs = biFilterQuery(filter)
    const params = new URLSearchParams({ format: 'csv', mode })
    if (facets?.model) params.set('model', facets.model)
    if (facets?.band) params.set('band', facets.band)
    const sep = qs ? '&' : '?'
    const response = await apiFetch(`${BI_BASE}/reports/${report}${qs}${sep}${params}`)
    if (!response.ok) throw new Error('Failed to export CSV')
    return response.blob()
  },
}

// ──────────────────────────────────────────────────────────────────────
// AppSettings — admin-tier runtime config (#429 Wave 3 Track D / #410).
// ──────────────────────────────────────────────────────────────────────

/** Per-agent provider+model+temperature triple. All fields optional —
 * the admin form treats each empty/cleared field as "unset, fall
 * through to env/default". */
export interface AgentModelOverride {
  provider?: string
  model?: string
  /** Sampling temperature in [0, 2]. Optional — falls through to
   * `<AGENT>_TEMPERATURE` env then `LLM_TEMPERATURE` when unset. */
  temperature?: number
}

/** Per-agent model defaults. Every agent the Models admin page
 * surfaces is listed here. Backend keeps two legacy env-var
 * names — `EXTRACTOR_*` for `analyzer`, `CANDIDATE_PROPOSER_*` for
 * `proposer` — so existing deploys don't silently break on
 * rename. The `proposer` is the first non-agent-loop entry: it's a
 * one-shot `generateObject` extractor (lives at
 * `application/candidateProposer/` on the server), but it
 * participates in the same model-resolution cascade as the
 * multi-turn agent loops above. */
export type AdminAgentName =
  | 'planner'
  | 'generator'
  | 'healer'
  | 'analyzer'
  | 'auditor'
  | 'scanner'
  | 'proposer'
  | 'refiner'
  | 'dedupJudge'

export interface AgentModelDefaults {
  planner?: AgentModelOverride
  generator?: AgentModelOverride
  healer?: AgentModelOverride
  analyzer?: AgentModelOverride
  auditor?: AgentModelOverride
  scanner?: AgentModelOverride
  proposer?: AgentModelOverride
  refiner?: AgentModelOverride
  dedupJudge?: AgentModelOverride
}

export interface DefaultRunBudgetsConfig {
  iterationsPerTestCase?: number
  iterationsPerScript?: number
  iterationsPerHeal?: number
}

/** Agent context + retry caps (#1567). Each field optional — absent
 * means "no doc-tier override; fall through to env / built-in." */
export interface AgentRuntimeCapsConfig {
  snapshotWindow?: number
  contextSoftTrimChars?: number
  generatorRetryBudget?: number
  /** Seconds the agent waits for one model call (30–600). */
  llmCallSeconds?: number
}

/** Test-similarity threshold (#1626). The one cosine that drives both the
 * run-time dedup and the candidate-picker coverage badge. */
export interface TestSimilarityConfig {
  duplicateThreshold?: number
  /** #1786 — when true (default), a borderline near-duplicate escalates to
   * the LLM dedup judge instead of auto-suppressing on the score alone.
   * `false` reverts to the pre-#1786 score-only behavior. */
  judgeEnabled?: boolean
}

export interface EmailTemplateOverrides {
  subjectPrefix?: string
  senderName?: string
}

/** Planner RAG-augmentation knobs (Slice 3B of #860). Each field
 * optional — absent means "no doc-tier override; fall through to
 * env / built-in." */
export interface PlannerRagConfig {
  augmentationEnabled?: boolean
  similarityThreshold?: number
  hardKCap?: number
  backgroundTotalCapChars?: number
}

/** Input-variations knobs (#1800). One knob today: the per-field cap on how
 * many valid input classes the planner derives. Absent => fall through to
 * the `MAX_INPUT_CLASSES_PER_FIELD` env var, then the built-in 6. */
export interface InputVariationsConfig {
  maxClassesPerField?: number
}

/** "Sites links can reach" on the Links OttoTester can read card. */
export type InternalAddressesSetting = 'blocked' | 'allowed'

/** Which sites reference-doc links can reach, as saved on Runtime Config
 * → "Links OttoTester can read". Mirrors the server's
 * `IReferenceDocLinkSettings`. */
export interface ReferenceDocLinkSettings {
  internalAddresses?: InternalAddressesSetting
  alwaysAllow?: string[]
  neverRead?: string[]
}

/** GET /api/admin/appSettings — current doc + the cascade-resolved
 * snapshot the form binds for provenance ("currently from env"). */
export interface AppSettingsDoc {
  _id: string
  agentModels?: AgentModelDefaults
  defaultRunBudgets?: DefaultRunBudgetsConfig
  agentRuntimeCaps?: AgentRuntimeCapsConfig
  testSimilarity?: TestSimilarityConfig
  emailTemplates?: EmailTemplateOverrides
  plannerRag?: PlannerRagConfig
  inputVariations?: InputVariationsConfig
  referenceDocLinks?: ReferenceDocLinkSettings
  createdAt: string
  updatedAt: string
}

/** Source tiers for the full agent-model resolution chain (#429 +
 * Models-UI widening). Used by `ResolvedAgentEntry.model.source` to
 * surface which layer of the cascade produced the actually-running
 * model. */
export type ResolvedAgentModelChainSource =
  | 'per-run'
  | 'app-settings'
  | 'per-agent-env'
  | 'default-env'
  | 'legacy-env'
  | 'hardcoded'

/** Source tiers for the temperature chain. `'unset'` means every tier
 * was empty — the AI SDK uses the provider's own default. */
export type ResolvedAgentTemperatureChainSource =
  | 'per-run'
  | 'app-settings'
  | 'per-agent-env'
  | 'default-env'
  | 'unset'

/** Per-agent entry in the resolved block. Top-level `spec`+`source`
 * describe the AppSettings-doc tier ONLY (so existing UI keeps working);
 * `model` walks the full resolver chain so the operator sees the
 * actually-running model even when the doc tier is unset; `temperature`
 * does the same for sampling temperature. */
export interface ResolvedAgentEntry {
  /** AppSettings-doc tier value. `null` when the doc tier is unset
   * (cascade falls through to env). */
  spec: string | null
  source: 'doc' | 'unset'
  /** Full resolver-chain result for the model. `spec` always resolves
   * (chain bottoms at the hardcoded floor); `source` names whichever
   * tier produced it. */
  model: {
    spec: string
    source: ResolvedAgentModelChainSource
    /** Set when this install cannot use the resolved model. Carried here
     * because a retired model has no catalog row to say so. */
    unavailableReason?: ModelUnavailableReason
  }
  /** Full resolver-chain result for temperature. `value` is `null`
   * when every tier was empty (AI SDK uses provider default). */
  temperature: {
    value: number | null
    source: ResolvedAgentTemperatureChainSource
  }
}

/** @deprecated Kept for back-compat with callers that imported the
 * pre-widening name. Prefer {@link ResolvedAgentEntry}. */
export type ResolvedAgentModelDefault = Pick<ResolvedAgentEntry, 'spec' | 'source'>

export interface ResolvedRunBudgetField {
  value: number | null
  source: 'doc' | 'unset'
}

export interface ResolvedAppSettings {
  /** Resolved model + temperature per agent.
   *
   * PARTIAL on purpose, though a healthy server sends every agent. During a
   * deployment the browser can hold a newer bundle than the backend it is
   * talking to, so an agent this bundle knows about is simply absent from the
   * response. Declaring it required made that a lie the compiler helped tell:
   * the Models page dereferenced every entry and blanked itself on the one it
   * did not get (#2137). A partial record makes the skew contract explicit and
   * turns the next occurrence into a type error rather than a blank page. */
  agentModels: Partial<Record<AdminAgentName, ResolvedAgentEntry>>
  defaultRunBudgets: {
    iterationsPerTestCase: ResolvedRunBudgetField
    iterationsPerScript: ResolvedRunBudgetField
    iterationsPerHeal: ResolvedRunBudgetField
  }
  agentRuntimeCaps: {
    snapshotWindow: ResolvedRunBudgetField
    contextSoftTrimChars: ResolvedRunBudgetField
    generatorRetryBudget: ResolvedRunBudgetField
    /** The time limit in effect and its tier, not just the saved value, so
     * the page can show an env value pinned by deploy settings. Optional
     * because a frontend can outlive the backend serving it. */
    llmCallSeconds?: { value: number; source: 'doc' | 'env' | 'builtIn' }
  }
  testSimilarity: {
    duplicateThreshold: ResolvedRunBudgetField
  }
  emailTemplates: {
    subjectPrefix: { value: string | null; source: 'doc' | 'unset' }
    senderName: { value: string | null; source: 'doc' | 'unset' }
  }
  /** Planner RAG knobs (Slice 3B of #860). Each value is the
   * cascade-resolved result the next planner kickoff would
   * observe; `defaults` carries the built-in fallback values so the
   * UI can hint "default: 0.55" next to the threshold input
   * without hardcoding the numbers. The doc-tier value is in
   * `AppSettingsDoc.plannerRag.*`; bind form inputs to that and
   * fall back to defaults visually when unset. */
  plannerRag: {
    augmentationEnabled: boolean
    similarityThreshold: number
    hardKCap: number
    backgroundTotalCapChars: number
    defaults: {
      augmentationEnabled: boolean
      similarityThreshold: number
      hardKCap: number
      backgroundTotalCapChars: number
    }
  }
  /** Input-variations per-field class cap (#1800). Always resolves to a
   * concrete value (cascade bottoms out at the built-in 6), so `source` names
   * the tier (doc / env / builtIn) — there is no `unset` state. Mirrors the
   * server's `resolveInputClassesCap()` shape. */
  inputVariations: { value: number; source: 'doc' | 'env' | 'builtIn' }
  /** Which sites reference-doc links can reach. Optional because a frontend
   * can outlive the backend serving it; absent, the card isn't shown. */
  referenceDocLinks?: {
    internalAddresses: { value: InternalAddressesSetting; source: 'doc' | 'env' | 'builtIn' }
    /** The choice without the saved card — what Reset to default settings
     * fills in. */
    defaultInternalAddresses: InternalAddressesSetting
    alwaysAllow: string[]
    neverRead: string[]
  }
}

/** One agent's declaration, as served on the app settings response.
 *
 * `required` drives the run-time gate (tool calling only). `bar` is the
 * advisory standard behind the "Fits the <Agent>" chip and the assign-time
 * warning. Optional because a frontend can outlive the backend that serves it;
 * absent, the picker behaves as it did before the bar existed. */
export interface AgentCapabilityDeclaration {
  required: readonly string[]
  bar?: AgentModelBar
}

/** What a model should have to do one agent's job well. Never a block. */
export interface AgentModelBar {
  /** Smallest context window, in tokens. */
  minContextTokens: number
  needs: readonly string[]
}

export interface AppSettingsResponse extends AppSettingsDoc {
  resolved: ResolvedAppSettings
  /** What each agent needs a model to be able to DO (#1928), served straight
   * from the server's own requirement map.
   *
   * A response-root field rather than a member of `resolved` on purpose:
   * `resolved` holds the OUTCOME of the doc→env→builtIn cascade for settings an
   * operator can change, whereas this is a fixed property of the code — no
   * cascade, no tiers, nothing to resolve.
   *
   * Optional because a frontend can outlive the backend that serves it. When
   * absent the picker degrades to unfiltered, which is exactly how it behaved
   * before this slice. */
  agentCapabilityRequirements?: Record<string, AgentCapabilityDeclaration>
  /** True when a country-of-origin policy is in force (#2107). Excluded models
   * are then absent from the catalog, so the model picker stops offering an
   * off-catalog model it would otherwise keep visible.
   *
   * Optional for the same reason as the field above: a frontend can outlive
   * the backend serving it, and absent means "no policy", which is how every
   * deployment behaves by default. */
  originPolicyActive?: boolean
  /** The new-release check's state for the Global Admin version panel.
   * Optional because a frontend can outlive the backend serving it; when
   * absent the panel shows no release line. */
  release?: ReleaseStatus
}

/** What the version panel's release line shows. Mirrors the server's
 * `ReleaseLineState`; the server decides it, so the panel never compares
 * versions itself. */
export type ReleaseLineState =
  | 'newer'
  | 'current'
  | 'never-checked'
  | 'failed'
  | 'off'
  | 'not-set-up'
  | 'dev'

export interface ReleaseStatus {
  state: ReleaseLineState
  /** The running release, or null for a build that is not one. */
  running: string | null
  /** The newest release the last good check found. */
  latest: { version: string; publishedAt: string | null } | null
  /** When the check last ran, and when it last reached GitHub (ISO). */
  checkedAt: string | null
  succeededAt: string | null
  /** Why the last check did not reach GitHub, in plain words. */
  error: string | null
  /** The scheduled job the Check for updates button runs. */
  jobId: string
}

/** Partial update payload for PUT — only the field groups present in
 * the body are written; the rest are left untouched. Within a group,
 * the value fully replaces the prior value (so to clear all agent
 * models, send `agentModels: {}`). */
export interface AppSettingsUpdate {
  agentModels?: AgentModelDefaults
  defaultRunBudgets?: DefaultRunBudgetsConfig
  agentRuntimeCaps?: AgentRuntimeCapsConfig
  testSimilarity?: TestSimilarityConfig
  emailTemplates?: EmailTemplateOverrides
  plannerRag?: PlannerRagConfig
  inputVariations?: InputVariationsConfig
  /** The whole card. `internalAddresses: null` clears the saved choice so
   * the install's own setting applies again. */
  referenceDocLinks?: {
    internalAddresses: InternalAddressesSetting | null
    alwaysAllow: string[]
    neverRead: string[]
  }
}

export const adminAppSettingsAPI = {
  async get(): Promise<AppSettingsResponse> {
    const response = await apiFetch(`${API_BASE}/admin/appSettings`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to read app settings')
    }
    return response.json()
  },

  async update(update: AppSettingsUpdate): Promise<AppSettingsDoc> {
    const response = await apiFetch(`${API_BASE}/admin/appSettings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to update app settings')
    }
    return response.json()
  },
}

// ──────────────────────────────────────────────────────────────────────
// Run-artifact retention — admin-tier (#662 slice 5). Backed by
// `/api/admin/run-artifacts/retention` which sits behind
// requireSession + requireSuperadmin.
// ──────────────────────────────────────────────────────────────────────

export interface AdminRunArtifactsBucketStats {
  objects: number
  totalBytes: number
  oldestObjectAt?: string
  largestObjectBytes?: number
  refreshedAt: string
}

export type AdminRunArtifactsLifecycle =
  | {
      configured: boolean
      appliedMaxAgeDays?: number
      nonStandardExpiration?: boolean
      enabled: boolean
    }
  | { skipped: 'disk-store' }
  | { error: string }

export interface AdminRunArtifactsRetention {
  maxAgeDays: number
  source: 'doc' | 'default'
  softCapDays: number
  /** Where run files are kept: this server's disk, or S3. The page
   * shows a different panel for each. */
  store: 'disk' | 's3'
  /** The folder run files are kept in (disk only), or null on S3. */
  diskFolder: string | null
  bucket: string | null
  lifecycle: AdminRunArtifactsLifecycle
  stats: AdminRunArtifactsBucketStats
  range: { min: number; max: number }
  /** #1518 — trace-retention window (days). The trace-cleanup job
   * deletes the heavy trace files past this, keeping each run's report.
   * Distinct from, and usually shorter than, `maxAgeDays`. */
  traceRetentionDays: number
  /** Whether `traceRetentionDays` is pinned on the doc or the default. */
  traceSource: 'doc' | 'default'
  /** Inclusive validation range for the trace-retention input. */
  traceRange: { min: number; max: number }
  /** Present only on PUT 207 (partial success) — the S3 lifecycle
   * sync failed after the DB write committed. UI surfaces a "Retry
   * sync" affordance against this. */
  lifecycleError?: string
}

export const adminRunArtifactsAPI = {
  async getRetention(): Promise<AdminRunArtifactsRetention> {
    const response = await apiFetch(
      `${API_BASE}/admin/run-artifacts/retention`,
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({
        error: `Failed (${response.status})`,
      }))
      throw new Error(err.error || 'Failed to read retention')
    }
    return response.json()
  },

  async setRetention(
    update: { maxAgeDays?: number; traceRetentionDays?: number },
  ): Promise<AdminRunArtifactsRetention> {
    const response = await apiFetch(
      `${API_BASE}/admin/run-artifacts/retention`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(update),
      },
    )
    // 207 = DB committed but S3 lifecycle sync failed; the body still
    // carries the new retention shape PLUS a `lifecycleError` string.
    // Treat as a successful return so the UI can render both the new
    // state and the retry affordance.
    if (response.status !== 200 && response.status !== 207) {
      const err = await response.json().catch(() => ({
        error: `Failed (${response.status})`,
      }))
      throw new Error(err.error || 'Failed to update retention')
    }
    return response.json()
  },
}

// ──────────────────────────────────────────────────────────────────────
// Org provisioning — admin-tier (privileged tenant
// creation, not a self-serve affordance). Backed by
// `/api/admin/orgs` which sits behind requireSession + requireSuperadmin.
// ──────────────────────────────────────────────────────────────────────

/** One row of the admin orgs directory. `ownerEmail` is the email of
 * the first owner-role member (lowest createdAt) — multiple owners
 * exist but the directory view shows one; full membership lives on
 * the per-org settings surface. `workspaceCount` excludes
 * soft-deleted workspaces. */
export interface AdminOrgRow {
  id: string
  name: string
  slug: string
  createdAt: string
  ownerUserId?: string
  ownerEmail?: string
  workspaceCount: number
}

export interface AdminOrgCreateBody {
  name: string
  ownerEmail: string
}

/** Better-auth's `createOrganization` response shape (the bits we
 * actually care about on the create response — id + name + slug). */
export interface AdminOrgCreateResponse {
  id?: string
  name?: string
  slug?: string
  [k: string]: unknown
}

/** Per-org CI tunables (#554 PR 2). Both fields are nullable in the
 * wire shape — `null` means "unset / falls through to env default".
 * Future #560 adds plan-defaults as a layer between these and env. */
export interface AdminOrgCiSettings {
  orgId: string
  maxConcurrentRuns: number | null
  artifactRetentionDays: number | null
  envDefaults: {
    artifactRetentionDays: number
  }
}

export const adminOrgsAPI = {
  async list(): Promise<AdminOrgRow[]> {
    const response = await apiFetch(`${API_BASE}/admin/orgs`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to list orgs')
    }
    const body = (await response.json()) as { orgs: AdminOrgRow[] }
    return body.orgs
  },

  async create(body: AdminOrgCreateBody): Promise<AdminOrgCreateResponse> {
    const response = await apiFetch(`${API_BASE}/admin/orgs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to create org')
    }
    return (await response.json()) as AdminOrgCreateResponse
  },

  async getCiSettings(orgId: string): Promise<AdminOrgCiSettings> {
    const response = await apiFetch(`${API_BASE}/admin/orgs/${orgId}/ci-settings`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to fetch CI settings')
    }
    return (await response.json()) as AdminOrgCiSettings
  },

  async updateCiSettings(
    orgId: string,
    patch: { maxConcurrentRuns?: number | null; artifactRetentionDays?: number | null },
  ): Promise<AdminOrgCiSettings> {
    const response = await apiFetch(`${API_BASE}/admin/orgs/${orgId}/ci-settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to update CI settings')
    }
    return (await response.json()) as AdminOrgCiSettings
  },

  /** Classify an org as "OttoTester staff" (runs unbounded, not billed) or
   * clear the flag (#2023). A superadmin classification change, separate from
   * plan assignment. The flag is the single lever for staff orgs (#2039);
   * setting it also clears any plan server-side (a staff org has no plan). */
  async setInternal(orgId: string, internal: boolean): Promise<{ orgId: string; internal: boolean }> {
    const response = await apiFetch(`${API_BASE}/admin/orgs/${orgId}/internal`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ internal }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to update internal classification')
    }
    return (await response.json()) as { orgId: string; internal: boolean }
  },

  /** How many existing worktrees would be swept if retention dropped
   * to `days`. Used by the modal to gate destructive decreases behind
   * an inline confirm. */
  async getRetentionImpact(orgId: string, days: number): Promise<{ days: number; affected: number }> {
    const response = await apiFetch(`${API_BASE}/admin/orgs/${orgId}/retention-impact?days=${days}`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to fetch retention impact')
    }
    return (await response.json()) as { days: number; affected: number }
  },
}

// ──────────────────────────────────────────────────────────────────────
// Superadmin grants — admin-tier (#1022 Slice 1).
// ──────────────────────────────────────────────────────────────────────

/** One row from `GET /api/admin/superadmin-grants`. `email` is null
 * when the grantee's User row was hard-deleted (pathological state
 * the operator should clean up). `grantedByEmail` is null EITHER for
 * env-bootstrap grants (the sentinel `system:bootstrap` grantor) OR
 * for grants whose grantor User row was hard-deleted — the UI
 * disambiguates by checking `grantedBy === 'system:bootstrap'`. */
export interface AdminSuperadminGrantRow {
  id: string
  userId: string
  email: string | null
  grantedBy: string
  grantedByEmail: string | null
  grantedAt: string
}

export const adminSuperadminGrantsAPI = {
  async list(): Promise<AdminSuperadminGrantRow[]> {
    const response = await apiFetch(`${API_BASE}/admin/superadmin-grants`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || "Couldn't load the Global Admins")
    }
    const body = (await response.json()) as { grants: AdminSuperadminGrantRow[] }
    return body.grants
  },

  async create(email: string): Promise<AdminSuperadminGrantRow> {
    const response = await apiFetch(`${API_BASE}/admin/superadmin-grants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || "Couldn't make this person a Global Admin")
    }
    const body = (await response.json()) as { grant: AdminSuperadminGrantRow }
    return body.grant
  },

  async revoke(userId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/admin/superadmin-grants/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || "Couldn't remove Global Admin access")
    }
  },
}

// ──────────────────────────────────────────────────────────────────────
// Operator tokens (#1573) — capability-scoped credentials a superadmin
// mints for headless automation. Superadmin-gated; the plaintext is
// returned exactly once on create.
// ──────────────────────────────────────────────────────────────────────

/** A listed operator token — safe display fields only (never the key). */
export interface AdminOperatorTokenRow {
  id: string
  name: string | null
  start: string | null
  createdAt: string | null
  expiresAt: string | null
}

/** The create response — carries the one-time plaintext `key`. */
export interface AdminOperatorTokenCreated {
  id: string
  name: string
  /** Plaintext token value — shown ONCE; the client surfaces it and drops it. */
  key: string
  createdAt: string | null
  expiresAt: string | null
}

export const adminOperatorTokensAPI = {
  async list(): Promise<AdminOperatorTokenRow[]> {
    const response = await apiFetch(`${API_BASE}/admin/operator-tokens`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || "Couldn't load automation tokens")
    }
    const body = (await response.json()) as { tokens: AdminOperatorTokenRow[] }
    return body.tokens
  },

  async create(input: { name: string; expiresInDays?: number }): Promise<AdminOperatorTokenCreated> {
    const response = await apiFetch(`${API_BASE}/admin/operator-tokens`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || "Couldn't create the automation token")
    }
    const body = (await response.json()) as { token: AdminOperatorTokenCreated }
    return body.token
  },

  async revoke(id: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/admin/operator-tokens/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || "Couldn't revoke the automation token")
    }
  },
}

// ──────────────────────────────────────────────────────────────────────
// Admin-tier cross-org invitations directory (#1024 Slice 3 of #1021).
// ──────────────────────────────────────────────────────────────────────

/** The five values `status` can take on an admin invitation row.
 * Mirrors the server's `AdminInvitationStatus` — keep in sync. The
 * four native Better-Auth statuses (`pending`, `accepted`, `rejected`,
 * `canceled`) plus the synthetic `expired` (a pending row past
 * `expiresAt`). Filtering by `status=pending` excludes expired rows
 * server-side, matching the user-visible semantic. */
export const ADMIN_INVITATION_STATUSES = [
  'pending',
  'accepted',
  'rejected',
  'canceled',
  'expired',
] as const
export type AdminInvitationStatus = (typeof ADMIN_INVITATION_STATUSES)[number]

/** One row from `GET /api/admin/invitations`. `inviterUserId` /
 * `inviterEmail` are null when the inviter's User row was hard-deleted
 * (or when the row pre-dates Better Auth populating `inviterId`).
 * `organizationName` is null when the org row itself is missing
 * (pathological — invitation survived an org hard-delete). */
export interface AdminInvitationRow {
  id: string
  inviteeEmail: string
  inviterUserId: string | null
  inviterEmail: string | null
  organizationId: string
  organizationName: string | null
  status: AdminInvitationStatus
  invitedAt: string
  expiresAt: string | null
}

/** Filters passed to `adminInvitationsAPI.list`. Each field is
 * independently optional; the API ANDs whatever is supplied. */
export interface AdminInvitationsFilter {
  status?: AdminInvitationStatus
  orgId?: string
  inviteeSearch?: string
  inviterSearch?: string
}

export const adminInvitationsAPI = {
  async list(
    filter: AdminInvitationsFilter = {},
  ): Promise<{ invitations: AdminInvitationRow[]; total: number }> {
    const params = new URLSearchParams()
    if (filter.status) params.set('status', filter.status)
    if (filter.orgId) params.set('orgId', filter.orgId)
    if (filter.inviteeSearch) params.set('inviteeSearch', filter.inviteeSearch)
    if (filter.inviterSearch) params.set('inviterSearch', filter.inviterSearch)
    const qs = params.toString()
    const response = await apiFetch(
      `${API_BASE}/admin/invitations${qs ? `?${qs}` : ''}`,
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to list invitations')
    }
    return (await response.json()) as { invitations: AdminInvitationRow[]; total: number }
  },
}

// ──────────────────────────────────────────────────────────────────────
// Users — admin-tier global directory + detail (#1023 Slice 2).
// ──────────────────────────────────────────────────────────────────────

/** One row from `GET /api/admin/users`. Mirrors `AdminUserListRow`
 * on the server. `primaryOrg` is null for orphan users; the
 * alphabetically-first org otherwise. `providers` carries the
 * Better Auth `accounts.providerId` values (`credential` for
 * email+password, `google`/`github`/... for OAuth). */
export interface AdminUserListRow {
  id: string
  email: string
  name?: string
  emailVerified?: boolean
  createdAt?: string
  lastSignInAt: string | null
  providers: string[]
  isOrphan: boolean
  primaryOrg: { id: string; name: string } | null
  orgMembershipCount: number
}

/** Filter primitives accepted by the list endpoint. v1 UI surfaces a
 * SUBSET; the full set is URL-deep-linkable from external dashboards
 * or pasted directly. */
export interface AdminUserListFilter {
  search?: string
  orgId?: string
  workspaceId?: string
  role?: string
  signedInBefore?: string
  signedInAfter?: string
  provider?: string
  orphan?: boolean
  /** Server-side primitive for the "Never signed in" / "Has signed
   * in" presets. `false` → users with zero sessions; `true` →
   * users with at least one. Added in PR #1036 Copilot review to
   * replace a client-side post-filter that mis-paginated. */
  signedIn?: boolean
}

export interface AdminUserListResponse {
  rows: AdminUserListRow[]
  limit: number
  offset: number
}

export interface AdminUserOrgMembership {
  orgId: string
  orgName: string
  role: string
}

export interface AdminUserWorkspaceMembership {
  workspaceId: string
  workspaceName: string
  orgId: string
  orgName: string
  role: string
  grantedOrigin: 'direct' | 'team'
}

export interface AdminUserDetail {
  id: string
  email: string
  name?: string
  emailVerified?: boolean
  createdAt?: string
  lastSignInAt: string | null
  providers: string[]
  suspended: boolean
  isSuperadmin: boolean
  orgs: AdminUserOrgMembership[]
  workspaces: AdminUserWorkspaceMembership[]
}

export const adminUsersAPI = {
  async list(
    filter: AdminUserListFilter,
    pagination: { limit?: number; offset?: number } = {},
  ): Promise<AdminUserListResponse> {
    const qs = new URLSearchParams()
    if (filter.search !== undefined) qs.set('search', filter.search)
    if (filter.orgId !== undefined) qs.set('orgId', filter.orgId)
    if (filter.workspaceId !== undefined) qs.set('workspaceId', filter.workspaceId)
    if (filter.role !== undefined) qs.set('role', filter.role)
    if (filter.signedInBefore !== undefined)
      qs.set('signedInBefore', filter.signedInBefore)
    if (filter.signedInAfter !== undefined)
      qs.set('signedInAfter', filter.signedInAfter)
    if (filter.provider !== undefined) qs.set('provider', filter.provider)
    if (filter.orphan !== undefined) qs.set('orphan', String(filter.orphan))
    if (filter.signedIn !== undefined) qs.set('signedIn', String(filter.signedIn))
    if (pagination.limit !== undefined) qs.set('limit', String(pagination.limit))
    if (pagination.offset !== undefined) qs.set('offset', String(pagination.offset))

    const url = `${API_BASE}/admin/users${qs.toString() ? '?' + qs.toString() : ''}`
    const response = await apiFetch(url)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to list users')
    }
    return (await response.json()) as AdminUserListResponse
  },

  async detail(userId: string): Promise<AdminUserDetail> {
    const response = await apiFetch(
      `${API_BASE}/admin/users/${encodeURIComponent(userId)}`,
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to load user detail')
    }
    const body = (await response.json()) as { user: AdminUserDetail }
    return body.user
  },

  /** Hard-delete an orphan user (zero org memberships) — #1542. The
   * server is the authority on orphan status; this throws with the
   * server's message on 403 (self) / 404 (gone) / 409 (not an orphan,
   * or is a superadmin). */
  async delete(userId: string): Promise<void> {
    const response = await apiFetch(
      `${API_BASE}/admin/users/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to delete user')
    }
  },
}

// ──────────────────────────────────────────────────────────────────────
// Entitlements — per-org read for the frontend's feature-gate hooks
// (#1037 PR-A.4).
// ──────────────────────────────────────────────────────────────────────

/** What `GET /api/orgs/:orgId/entitlements` returns. Mirrors the
 * server-side `EffectiveEntitlements` shape, except `features` is a
 * JSON-serializable array (the hook re-builds a Set on receipt). */
export interface OrgEntitlementsResponse {
  limits: {
    maxTestScripts: number | null
    maxMonthlyAgentCostCents: number | null
    maxMonthlyRuns: number | null
    maxApplications: number | null
    maxWorkspaces: number | null
    maxUserAccounts: number | null
  }
  features: string[]
}

export const orgEntitlementsAPI = {
  /** Fetch the org's effective entitlements. Read by every org
   * member; surfaces what's locked (the lock-state hook reads
   * `features` set membership) and what limits apply. */
  async getByOrgId(orgId: string): Promise<OrgEntitlementsResponse> {
    const response = await apiFetch(
      `${API_BASE}/orgs/${encodeURIComponent(orgId)}/entitlements`,
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to read entitlements')
    }
    return (await response.json()) as OrgEntitlementsResponse
  },
}

// ──────────────────────────────────────────────────────────────────────
// Slack notifications — OAuth installs + per-channel destinations
// ──────────────────────────────────────────────────────────────────────

export interface SlackInstallSummary {
  _id: string
  workspaceId: string
  slackTeamId: string
  slackTeamName: string
  slackTeamDomain?: string
  botUserId: string
  scopes: string[]
  installedBy: string
  lastError?: string
  lastErrorAt?: string
  createdAt: string
  updatedAt: string
}

export interface SlackIntegrationSummary {
  _id: string
  workspaceId: string
  slackInstallId: string
  channelId: string
  channelName: string
  channelIsPrivate: boolean
  lastDeliveryAt?: string
  lastDeliveryStatus?: 'success' | 'broken'
  lastError?: string
  addedBy: string
  createdAt: string
  updatedAt: string
}

/** Codebase-canonical run-type discriminator. Same enum values
 * stamped on every `TestExecution.runType` in Mongo and accepted by
 * the Slack routing-rule POST endpoint. Schema values are
 * `'discovery'` and `'replay'` — the customer-facing display strings
 * are "Generation" and "Replay" respectively. Use
 * `runTypeDisplayLabel()` to translate at the UI seam. */
export type RunType = 'discovery' | 'replay'

/** Translate a schema `RunType` enum value into the customer-facing
 * display string. Centralized here so the picker, matrix, and chip
 * tooltip stay in lockstep when copy changes. */
export function runTypeDisplayLabel(rt: RunType): string {
  return rt === 'discovery' ? 'Generation' : 'Replay'
}

/** Wire shape of a `SlackChannelRoutingRule` row — one per
 * (channel, app, variant) the customer added. Each row carries a
 * `runTypes` array (one or both of `'discovery' | 'replay'`) naming
 * which run types this row covers. The dispatcher matches the row
 * on (app, variant) and consults `runTypes` to decide whether the
 * incoming event's run type is covered; the UI exposes the array
 * as two inline checkboxes (Generation / Replay) on each matrix row.
 * The non-empty invariant is enforced at the wire boundary too —
 * an empty array round-trips as a server-side 400. */
export interface SlackChannelRoutingRule {
  _id: string
  workspaceId: string
  integrationId: string
  applicationId: string
  variantId: string
  runTypes: RunType[]
  events: Record<string, boolean>
  createdAt: string
  updatedAt: string
}

export interface SlackChannelOption {
  id: string
  name: string
  is_private: boolean
  is_member: boolean
  num_members?: number
}

/** Wire shape of a Slack-eligible event row returned by
 * `GET /slack/events`. The backend filters by recipient scope so the
 * frontend renders whatever it gets — no parallel predicate here. */
export interface SlackEligibleEvent {
  type: string
  label: string
  description: string
  group: string
  defaultOn: boolean
}

/** Error class for the channel-list + test-message paths so callers
 * can render Slack's own remediation string verbatim. The backend
 * passes Slack's `data.error` through unchanged on the `slackError`
 * field of the response body. */
export class SlackError extends Error {
  slackError?: string
  constructor(message: string, slackError?: string) {
    super(message)
    this.name = 'SlackError'
    this.slackError = slackError
  }
}

export const slackAPI = {
  /** Mint a Slack authorize URL. The frontend redirects the browser
   * client-side after receiving the URL so a loading spinner can
   * render between click and navigation (per the design's
   * "loading spinner during OAuth initiate" affordance). The plan
   * gate fires here — error carries `code` + `upgradeHint` so the
   * caller can render an upgrade prompt distinct from a generic
   * failure. */
  async initiateOAuth(workspaceId: string): Promise<{ url: string }> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/oauth/initiate`,
      { method: 'POST' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      const error = new Error(
        err.message || err.error || 'Failed to initiate Slack OAuth',
      ) as Error & { code?: string; upgradeHint?: string }
      error.code = err.error
      error.upgradeHint = err.upgradeHint
      throw error
    }
    return (await response.json()) as { url: string }
  },

  async listInstalls(workspaceId: string): Promise<SlackInstallSummary[]> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/installs`,
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to list Slack installs')
    }
    return (await response.json()) as SlackInstallSummary[]
  },

  async disconnectInstall(workspaceId: string, installId: string): Promise<void> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/installs/${encodeURIComponent(installId)}`,
      { method: 'DELETE' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to disconnect Slack workspace')
    }
  },

  async listChannels(
    workspaceId: string,
    installId: string,
  ): Promise<SlackChannelOption[]> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/installs/${encodeURIComponent(installId)}/channels`,
    )
    if (!response.ok) {
      const err = (await response.json().catch(() => ({}))) as {
        error?: string
        slackError?: string
        message?: string
      }
      throw new SlackError(
        err.message || err.error || 'Failed to list Slack channels',
        err.slackError,
      )
    }
    const body = (await response.json()) as { channels: SlackChannelOption[] }
    return body.channels
  },

  async listIntegrations(workspaceId: string): Promise<SlackIntegrationSummary[]> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/integrations`,
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to list Slack integrations')
    }
    return (await response.json()) as SlackIntegrationSummary[]
  },

  async addIntegration(
    workspaceId: string,
    body: {
      installId: string
      channelId: string
      channelName: string
      channelIsPrivate: boolean
    },
  ): Promise<SlackIntegrationSummary> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/integrations`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      const error = new Error(
        err.message || err.error || 'Failed to add Slack channel',
      ) as Error & { code?: string; upgradeHint?: string }
      error.code = err.error
      error.upgradeHint = err.upgradeHint
      throw error
    }
    return (await response.json()) as SlackIntegrationSummary
  },

  async removeIntegration(
    workspaceId: string,
    integrationId: string,
  ): Promise<void> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/integrations/${encodeURIComponent(integrationId)}`,
      { method: 'DELETE' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to remove Slack channel')
    }
  },

  async sendTestMessage(
    workspaceId: string,
    integrationId: string,
  ): Promise<void> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/integrations/${encodeURIComponent(integrationId)}/test-message`,
      { method: 'POST' },
    )
    if (!response.ok) {
      const err = (await response.json().catch(() => ({}))) as {
        error?: string
        slackError?: string
        message?: string
      }
      throw new SlackError(
        err.message || err.error || 'Failed to send test message',
        err.slackError,
      )
    }
  },

  /** Fetch the Slack-eligible event catalog (Step 05 editor source).
   * Backend applies the scope-based eligibility filter; the response
   * is the authoritative list the editor should render. */
  async listEligibleEvents(
    workspaceId: string,
  ): Promise<SlackEligibleEvent[]> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/events`,
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to list Slack-eligible events')
    }
    const body = (await response.json()) as { events: SlackEligibleEvent[] }
    return body.events
  },

  /** List the routing rule rows on a channel. Each row binds the
   * channel to exactly one (application, variant) pair plus the
   * per-event toggles for that pair. */
  async listRoutingRules(
    workspaceId: string,
    integrationId: string,
  ): Promise<SlackChannelRoutingRule[]> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/integrations/${encodeURIComponent(integrationId)}/routing-rules`,
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to list routing rules')
    }
    const body = (await response.json()) as { rules: SlackChannelRoutingRule[] }
    return body.rules
  },

  /** Bulk-add routing rule rows to a channel from the picker save.
   * Each pair is one routing row to create, carrying the run types
   * the customer ticked for that pair. Returns the freshly-created
   * rows plus any pairs the picker ticked that were already present
   * on the channel (defense-in-depth against a stale picker). Each
   * new row carries the three default-on event types; the customer
   * refines per row from the matrix editor.
   *
   * Wire shape: `{ pairs: [{ applicationId, variantId, runTypes }] }`.
   * `runTypes` is a non-empty array of `'discovery' | 'replay'`. The
   * picker enforces non-empty client-side (Add disabled when no
   * checkbox is ticked for any pair); the backend re-enforces the
   * same invariant at the route layer (400 on `[]`). */
  async addRoutingRows(
    workspaceId: string,
    integrationId: string,
    pairs: ReadonlyArray<{
      applicationId: string
      variantId: string
      runTypes: RunType[]
    }>,
  ): Promise<{
    created: SlackChannelRoutingRule[]
    skipped: Array<{
      applicationId: string
      variantId: string
    }>
  }> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/integrations/${encodeURIComponent(integrationId)}/routing-rules`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pairs }),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      const error = new Error(
        err.message || err.error || 'Failed to add routing rules',
      ) as Error & { code?: string; upgradeHint?: string }
      error.code = err.error
      error.upgradeHint = err.upgradeHint
      throw error
    }
    return (await response.json()) as {
      created: SlackChannelRoutingRule[]
      skipped: Array<{
        applicationId: string
        variantId: string
      }>
    }
  },

  /** Replace a single routing row's events Map. Same one-intent-per-
   * save contract the legacy subscriptions PUT had — the matrix
   * editor commits the row's full event toggle state on Save. */
  async updateRoutingRow(
    workspaceId: string,
    integrationId: string,
    ruleId: string,
    events: Record<string, boolean>,
  ): Promise<void> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/integrations/${encodeURIComponent(integrationId)}/routing-rules/${encodeURIComponent(ruleId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      const error = new Error(
        err.message || err.error || 'Failed to update routing rule',
      ) as Error & { code?: string; upgradeHint?: string }
      error.code = err.error
      error.upgradeHint = err.upgradeHint
      throw error
    }
  },

  /** Replace a single routing row's `runTypes` array. Called when the
   * customer toggles a Generation / Replay checkbox on the matrix
   * row. The backend rejects an empty array with a 400 — the picker
   * UI prevents this by disabling save when no checkboxes are ticked,
   * but the seam is still enforced over the wire. */
  async updateRoutingRowRunTypes(
    workspaceId: string,
    integrationId: string,
    ruleId: string,
    runTypes: RunType[],
  ): Promise<void> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/integrations/${encodeURIComponent(integrationId)}/routing-rules/${encodeURIComponent(ruleId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ runTypes }),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      const error = new Error(
        err.message || err.error || 'Failed to update routing rule run types',
      ) as Error & { code?: string; upgradeHint?: string }
      error.code = err.error
      error.upgradeHint = err.upgradeHint
      throw error
    }
  },

  /** Remove a single routing rule row (the ✕ button on the matrix
   * row). */
  async removeRoutingRow(
    workspaceId: string,
    integrationId: string,
    ruleId: string,
  ): Promise<void> {
    const response = await apiFetch(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/slack/integrations/${encodeURIComponent(integrationId)}/routing-rules/${encodeURIComponent(ruleId)}`,
      { method: 'DELETE' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to remove routing rule')
    }
  },
}

// ──────────────────────────────────────────────────────────────────────
// Prompts — admin-tier runtime-prompt management (#574).
// ──────────────────────────────────────────────────────────────────────

/** Mirrors the server's `PromptType` union (Prompt.ts). One per real
 * LLM agent, plus `shared` for a rules chunk more than one agent
 * composes (#1901). See Prompt.ts for the rationale on dropped values
 * (`executor`, `evaluator`). Keep this in sync; adding a
 * new type means extending BOTH the server union and the schema enum
 * AND this list. */
export type AdminPromptType =
  | 'planner' | 'generator' | 'healer' | 'analyzer' | 'auditor' | 'scanner' | 'proposer' | 'refiner' | 'dedupJudge' | 'shared'

// Hand-mirrors the backend `PROMPT_TYPES` enum. Keep in lockstep: a type the
// backend seeds but this list omits is filtered out of the Prompts page and
// becomes invisible (that is exactly what hid `refiner.system` until #2148's
// follow-up added 'refiner' here).
export const ADMIN_PROMPT_TYPES: readonly AdminPromptType[] = [
  'planner', 'generator', 'healer', 'analyzer', 'auditor', 'scanner', 'proposer', 'refiner', 'dedupJudge', 'shared',
]

export interface AdminPromptComment {
  id: string
  authorUserId: string
  text: string
  createdAt: string
}

/** Full prompt shape — returned by GET `/:id` (paired with versions
 * in `AdminPromptDetail`) and by mutation endpoints that return the
 * updated row. List page does NOT use this — the list endpoint
 * returns the lighter `AdminPromptListRow` (no `comments` payload,
 * adds `commentCount` + `activeVersion`). */
export interface AdminPromptRow {
  _id: string
  name: string
  type: AdminPromptType
  description?: string
  activeVersionId?: string
  nextVersion: number
  tags: string[]
  comments: AdminPromptComment[]
  createdAt: string
  updatedAt: string
}

/** What GET `/api/admin/prompts` (the list endpoint) returns per
 * row. Drops the full `comments[]` to keep the payload bounded —
 * the list page only needs a count. Adds `activeVersion` (the
 * version number, not just the id) because deriving it client-side
 * from `nextVersion - 1` would be wrong after a rollback: nextVersion
 * keeps growing even when the operator activates an older version. */
export interface AdminPromptListRow {
  _id: string
  name: string
  type: AdminPromptType
  description?: string
  activeVersionId?: string
  activeVersion: number | null
  nextVersion: number
  tags: string[]
  commentCount: number
  createdAt: string
  updatedAt: string
}

export interface AdminPromptVersion {
  _id: string
  promptId: string
  version: number
  body: string
  authorUserId: string
  commitMessage: string
  activatedAt: string | null
  expiredAt: string | null
  createdAt: string
  updatedAt: string
}

export interface AdminPromptDetail {
  prompt: AdminPromptRow
  versions: AdminPromptVersion[]
}

export const adminPromptsAPI = {
  async list(opts: { type?: AdminPromptType } = {}): Promise<AdminPromptListRow[]> {
    const qs = opts.type ? `?type=${encodeURIComponent(opts.type)}` : ''
    const res = await apiFetch(`${API_BASE}/admin/prompts${qs}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string }
      throw new Error(err.error || `Failed to list prompts (${res.status})`)
    }
    const body = (await res.json()) as { prompts: AdminPromptListRow[] }
    return body.prompts
  },

  async get(id: string): Promise<AdminPromptDetail> {
    const res = await apiFetch(`${API_BASE}/admin/prompts/${id}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string }
      throw new Error(err.error || `Failed to fetch prompt (${res.status})`)
    }
    return (await res.json()) as AdminPromptDetail
  },

  async getVersion(promptId: string, versionId: string): Promise<AdminPromptVersion> {
    const res = await apiFetch(`${API_BASE}/admin/prompts/${promptId}/versions/${versionId}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string }
      throw new Error(err.error || `Failed to fetch version (${res.status})`)
    }
    return (await res.json()) as AdminPromptVersion
  },

  async create(input: {
    name: string
    type: AdminPromptType
    description?: string
    tags?: string[]
    initialBody: string
    commitMessage: string
  }): Promise<{ prompt: AdminPromptRow; version: AdminPromptVersion }> {
    const res = await apiFetch(`${API_BASE}/admin/prompts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string }
      throw new Error(err.error || `Failed to create prompt (${res.status})`)
    }
    return (await res.json()) as { prompt: AdminPromptRow; version: AdminPromptVersion }
  },

  async createVersion(id: string, input: { body: string; commitMessage: string }): Promise<AdminPromptVersion> {
    const res = await apiFetch(`${API_BASE}/admin/prompts/${id}/versions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string }
      throw new Error(err.error || `Failed to save version (${res.status})`)
    }
    return (await res.json()) as AdminPromptVersion
  },

  async activate(id: string, versionId: string): Promise<{ prompt: AdminPromptRow; version: AdminPromptVersion }> {
    const res = await apiFetch(`${API_BASE}/admin/prompts/${id}/activate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ versionId }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string }
      throw new Error(err.error || `Failed to activate version (${res.status})`)
    }
    return (await res.json()) as { prompt: AdminPromptRow; version: AdminPromptVersion }
  },

  async updateTags(id: string, tags: string[]): Promise<AdminPromptRow> {
    const res = await apiFetch(`${API_BASE}/admin/prompts/${id}/tags`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tags }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string }
      throw new Error(err.error || `Failed to update tags (${res.status})`)
    }
    return (await res.json()) as AdminPromptRow
  },

  async addComment(id: string, text: string): Promise<AdminPromptComment> {
    const res = await apiFetch(`${API_BASE}/admin/prompts/${id}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string }
      throw new Error(err.error || `Failed to add comment (${res.status})`)
    }
    return (await res.json()) as AdminPromptComment
  },

  async removeComment(id: string, commentId: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/admin/prompts/${id}/comments/${commentId}`, {
      method: 'DELETE',
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string }
      throw new Error(err.error || `Failed to remove comment (${res.status})`)
    }
  },
}

// ──────────────────────────────────────────────────────────────────────
// ModelPricing — admin-tier rate-card edits (#429 Wave 3 Track F /
// frontend half of #517).
// ──────────────────────────────────────────────────────────────────────

/** One row of `modelPricing` as the admin GET surface returns it. The
 * #517 provenance fields (`_unpriced`, `_source`, `discoveredAt`,
 * `discoveredSource`) flow through as-is so the table can badge each row. */
export interface ModelPricingRow {
  _id: string
  namePattern: string
  provider: string | null
  inputCentsPer1M: number
  outputCentsPer1M: number
  cacheReadCentsPer1M?: number
  cacheWriteCentsPer1M?: number
  effectiveFrom: string
  _unpriced?: boolean
  /** When a refresh last saw this model upstream (#2337). Moves whether or
   * not the price changed, which is the point: `updatedAt` beside it says
   * when the number last MOVED, and a rate that has held steady for ten
   * weeks looks abandoned when that is the only date on the row. */
  lastCheckedAt?: string
  /** Which sync (or operator) last wrote this row. `'openrouter'` was missing
   * from this union until #1928 even though the server writes it and it is the
   * MAJORITY of rows — 407 of 663 locally. Nothing crashed, because the badge
   * renders the raw string, but any exhaustive switch on `_source` was silently
   * wrong for most of the catalog and TypeScript could not see it: the value
   * crosses the wire as JSON. */
  _source?: 'litellm' | 'openrouter' | 'ottocloud' | 'override' | 'discovery'
  discoveredAt?: string
  discoveredSource?: string
  /** #1362 — proposed rates from the most recent LiteLLM refresh that
   * tripped the >10x swing guard. Present together when one is set;
   * absent on a row that has no swing under review. The admin pricing
   * page renders rows with `pendingProposedAt` set in red and exposes
   * Approve / Reject buttons that call the matching POST endpoints. */
  pendingInputCentsPer1M?: number
  pendingOutputCentsPer1M?: number
  pendingCacheReadCentsPer1M?: number
  pendingCacheWriteCentsPer1M?: number
  pendingProposedAt?: string
  /** #1842 — whether this model accepts image input (multimodal). The
   * backend catalog + LiteLLM/OpenRouter sync populate it (`supports_vision`
   * / `input_modalities`); the admin pricing editor can override it. Drives
   * the "vision enabled" badge on the assignment picker (#1844 Slice 1) —
   * informational only, never gates selection. Absent on rows synced before
   * the field existed. */
  imageInput?: boolean
  /** #1926 capabilities. **Tri-state** — `undefined` means the catalog is
   * silent, which is NOT the same as unsupported, so the UI must render three
   * states rather than two. `toolCalling` is the already-resolved effective
   * value (an operator override beats the catalog); `toolCallingUpstream` is
   * the raw catalog value, exposed ONLY so the UI can tell "this model is
   * fine" from "an operator overrode a no". Never decide on `*Upstream`. */
  toolCalling?: boolean
  toolCallingUpstream?: boolean
  /** Whether the model reasons at all (LiteLLM `supports_reasoning`). Distinct
   * from `reasoningEffortReducible` below, and the gate on it: a model that
   * does not reason still reports "effort cannot be reduced", so reading
   * reducibility alone would label ordinary models as always-on reasoners. */
  reasoning?: boolean
  /** Whether reasoning effort can be turned DOWN (#1926). Advisory: a model
   * that reasons without this can spend a whole capped output budget thinking
   * and never reach its tool call. Never gates. */
  reasoningEffortReducible?: boolean
  /** Context window in tokens. Populated for `openrouter:*` rows since #1926;
   * LiteLLM namespaces already carried it. */
  maxInputTokens?: number
  /** ISO date for when the upstream catalog first listed this model (#1985).
   *
   * Shown to operators as "Added" rather than "Released": OpenRouter's value
   * is when THEY listed it, which trails a vendor launch for any model added
   * later. Absent on `litellm` rows — LiteLLM publishes no such date — and
   * absent renders as nothing, never a guess. */
  listedAt?: string
  /** #1844 Slice 1.5 — the editorial collections this model belongs to
   * (`otto-curated`, `frontier`). A server-side overlay from the
   * code-owned curated list, not a stored field; drives the picker's
   * curated/frontier facet chips. Absent/[] when the model is in no
   * editorial collection. */
  collections?: string[]
  /** #1844 Slice 2 — the model's relative cost category (Low/Medium/High/
   * Premium), a rank percentile over the priced models by a static
   * input+3×output blend: thirds, with Premium the priciest tenth (#2011). `free` is a model at
   * $0 for input and output, set aside before that ranking (#2878). A server-side overlay (not stored); absent on an unpriced model
   * (rendered "—"). This is the ONLY cost signal on the selection surface —
   * no dollars there. */
  costCategory?: 'free' | 'low' | 'medium' | 'high' | 'premium'
  /** #2015 — who built the model and where they are based, derived from the
   * name pattern by the server. Distinct from the Provider column, which is
   * the serving path: `openrouter:qwen/…` has provider `openrouter` and maker
   * `qwen`, based in China. `country: 'unknown'` when the maker is not one we
   * have classified. */
  origin?: { maker: string | null; makerLabel: string | null; country: string }
  /** Whether this install can pick the model: its supplier is set up, its
   * key was not rejected, and its latest listing named it. Absent from a
   * backend older than this field, which means available. */
  available?: boolean
  /** Why not, when `available` is false. `unsupported`: priced under a
   * prefix the engine has no supplier for. */
  unavailableReason?: ModelUnavailableReason
  createdAt: string
  updatedAt: string
}

/** Why this install cannot use a model. Mirrors the server's `UnavailableReason`. */
export type ModelUnavailableReason = 'noKey' | 'keyRejected' | 'notListed' | 'unsupported' | 'noPrice'

/** One model supplier, as the Models screen's "Where models come from" card
 * shows it. Mirrors the server's `SupplierStatus`. */
export interface ModelSupplierStatus {
  supplier: string
  name: string
  state: 'ready' | 'keyRejected' | 'noKey' | 'notListedYet' | 'failed'
  listedCount: number
  listedAt?: string
  /** The setting that holds this supplier's key. */
  keySetting: string
  /** The scheduled job that lists this supplier's models, for List now. */
  listingJobId?: string
}

/** One row of the pricing-coverage audit — one distinct model that
 * appears in `runMetrics.byModel` across the corpus OR is currently
 * pinned per-agent on the Models admin page, with its pricing status.
 * Drives the Pricing-page coverage banner + the "in use only" filter
 * on the rate-card table, and the "Price assigned" column on the
 * Agent Models page. A configured-but-never-run model surfaces here
 * with zero metrics and `lastSeenAt: undefined` (#796). */
export interface PricingCoverageRow {
  modelSpec: string
  provider: string
  model: string
  totalTokens: number
  runCount: number
  /** Undefined when the model is configured per-agent but has not yet
   * been exercised by a finalized run. */
  lastSeenAt?: string
  isPriced: boolean
}

export interface PricingCoverage {
  models: PricingCoverageRow[]
  /** Count of `models[]` entries with `isPriced === false`. The single
   * headline number the coverage banner renders. */
  unpricedInUseCount: number
}

/** A model the catalog no longer offers (#2014). Not pickable, but an agent
 * may still be assigned one, so the screens need to name what happened. */
export interface RetiredModelSummary {
  namePattern: string
  reason: 'delisted' | 'noFixedPrice'
  retiredAt: string
}

export interface ModelCatalogResponse {
  rows: ModelPricingRow[]
  retired: RetiredModelSummary[]
  /** True when a country-of-origin policy is in force (#2107). Excluded models
   * are then absent from this catalog, so the screen states that instead of
   * offering a filter for them. Optional — absent means no policy. */
  originPolicyActive?: boolean
  /** Every supplier and whether it is usable. Absent from an older backend. */
  suppliers?: ModelSupplierStatus[]
}

export const adminPricingAPI = {
  /** Live catalog rows, plus the retired models as identity and reason only
   * (#2014). Both in one call because every screen that lets an operator pick
   * a model also has to explain one they picked before it retired. */
  async list(): Promise<ModelCatalogResponse> {
    const response = await apiFetch(`${API_BASE}/admin/pricing`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to read model pricing')
    }
    const body = (await response.json()) as ModelCatalogResponse
    // `?? []` so a backend predating #2014 still renders — the labels simply
    // fall back to the generic wording rather than the page failing.
    return { rows: body.rows, retired: body.retired ?? [], suppliers: body.suppliers ?? [] }
  },

  async coverage(): Promise<PricingCoverage> {
    const response = await apiFetch(`${API_BASE}/admin/pricing/coverage`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to read pricing coverage')
    }
    return (await response.json()) as PricingCoverage
  },

  /** Record an operator's correction of what a model can DO (#1928).
   *
   * The only write on this screen that changes a model's own facts. There
   * used to be a rate edit beside it; #2337 removed it, because a price
   * somebody typed is a price nothing re-checks. A capability correction is
   * different: it does not stop the row refreshing, so upstream keeps
   * updating everything else about the model.
   *
   * `imageInput` joined `toolCalling` in #2337 — the removed rate edit
   * carried the vision flag as a passenger, and this is where it landed.
   *
   * `null` clears the correction and hands the field back to upstream. */
  async setCapabilityOverride(
    id: string,
    capability: 'toolCalling' | 'imageInput',
    value: boolean | null,
  ): Promise<ModelPricingRow> {
    const response = await apiFetch(
      `${API_BASE}/admin/pricing/${encodeURIComponent(id)}/capability`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capability, value }),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to update model capability')
    }
    const payload = (await response.json()) as { row: ModelPricingRow }
    return payload.row
  },

  /** Approve the row's pending swing — writes pending* values to live
   * fields, clears pending. (#1362) */
  async approveSwing(id: string): Promise<ModelPricingRow> {
    const response = await apiFetch(
      `${API_BASE}/admin/pricing/${encodeURIComponent(id)}/approve-swing`,
      { method: 'POST' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to approve pending price change')
    }
    const payload = (await response.json()) as { row: ModelPricingRow }
    return payload.row
  },

  /** Reject the row's pending swing — clears pending fields, leaves
   * live rates untouched. (#1362) */
  async rejectSwing(id: string): Promise<ModelPricingRow> {
    const response = await apiFetch(
      `${API_BASE}/admin/pricing/${encodeURIComponent(id)}/reject-swing`,
      { method: 'POST' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to reject pending price change')
    }
    const payload = (await response.json()) as { row: ModelPricingRow }
    return payload.row
  },
}

// ──────────────────────────────────────────────────────────────────────
// Audit log — admin-tier reader (#429 Wave 3 Track G).
// ──────────────────────────────────────────────────────────────────────

/** One row of `auditEvents` as the admin GET surface returns it. Shape
 * matches the server's `AuditEvent` (POJO), with Date fields
 * serialized to ISO strings over the wire. `_id` arrives as a hex
 * string (Mongoose `.lean()` returns ObjectId, but JSON.stringify
 * surfaces its `.toString()`). */
export interface AuditEventRow {
  _id: string
  ts: string
  actorUserId: string
  actorOrgId?: string
  action: string
  targetType: string
  targetId: string
  before?: Record<string, unknown>
  after?: Record<string, unknown>
  meta?: Record<string, unknown>
  result: 'success' | 'failure'
  failureReason?: string
  createdAt: string
  updatedAt: string
}

/** Filter + pagination knobs accepted by `GET /api/admin/audit`. Empty
 * fields are dropped from the URL by the API client; the server
 * treats empty strings as absent regardless (defensive against a UI
 * that builds the URL by concatenation). */
export interface AuditFilter {
  /** Prefix match (server anchors `^prefix` at the start; uses the
   * `{actorUserId: 1, ts: -1}` index). Minimum 5 chars at the
   * server. Mutually exclusive with `actorEmail`. */
  actorUserId?: string
  /** Server resolves email → actorUserId(s) via the users
   * collection, then queries audit. The audit row never stores
   * email — resolution is read-side only. Mutually exclusive
   * with `actorUserId`. */
  actorEmail?: string
  actorOrgId?: string
  action?: string
  targetType?: string
  targetId?: string
  /** ISO 8601 inclusive lower bound on `ts`. */
  since?: string
  /** ISO 8601 exclusive upper bound on `ts`. */
  until?: string
  limit?: number
  offset?: number
  sortDir?: 'asc' | 'desc'
}

/** Display-only enrichment per row. The audit STORE never has
 * email/name on the row — these come from a batch-join to the
 * users collection at read time. AU-3 minimization: audit row
 * stays id-only on disk. */
export interface AuditActor {
  email?: string
  name?: string
}

/** Display-only context chain for one audit event. The audit STORE
 * only carries a single `(targetType, targetId)` pair — the chain
 * (org → workspace → app → variant) is resolved server-side at read
 * time by walking the domain repos. Every layer is optional: a
 * `variant_test_data` event resolves all four; an `org` event
 * resolves only org; an event with no resolvable target falls back
 * to org via `actorOrgId`. **UI rendering:** the Org column also
 * falls back to the bare `actorOrgId` carried on the row when the
 * chain resolver returned no `org` (resolver miss, DB blip,
 * system-level target type with no chain). The workspace / app /
 * variant columns render `—` when absent — the row doesn't carry
 * bare ids for those layers. PR #534 Copilot review. */
export interface AuditContext {
  org?: { id: string; name: string }
  workspace?: { id: string; name: string }
  app?: { id: string; name: string }
  variant?: { id: string; name: string }
}

export interface AuditListResponse {
  events: AuditEventRow[]
  /** Keyed by actorUserId. Missing keys = user not resolvable
   * (deleted account / legacy row); the UI falls back to bare id. */
  actors: Record<string, AuditActor>
  /** Keyed by audit-event _id. Missing keys = context resolution
   * failed (transient DB blip — fail-soft contract on the server);
   * the UI falls back to bare-id rendering of org/workspace/app/
   * variant columns in that case. */
  contexts: Record<string, AuditContext>
  limit: number
  offset: number
}

export const adminAuditAPI = {
  async list(filter: AuditFilter = {}): Promise<AuditListResponse> {
    const qs = new URLSearchParams()
    if (filter.actorUserId) qs.set('actorUserId', filter.actorUserId)
    if (filter.actorEmail) qs.set('actorEmail', filter.actorEmail)
    if (filter.actorOrgId) qs.set('actorOrgId', filter.actorOrgId)
    if (filter.action) qs.set('action', filter.action)
    if (filter.targetType) qs.set('targetType', filter.targetType)
    if (filter.targetId) qs.set('targetId', filter.targetId)
    if (filter.since) qs.set('since', filter.since)
    if (filter.until) qs.set('until', filter.until)
    if (filter.limit !== undefined) qs.set('limit', String(filter.limit))
    if (filter.offset !== undefined) qs.set('offset', String(filter.offset))
    if (filter.sortDir) qs.set('sortDir', filter.sortDir)
    const q = qs.toString()
    const url = q ? `${API_BASE}/admin/audit?${q}` : `${API_BASE}/admin/audit`
    const response = await apiFetch(url)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to read audit events')
    }
    return (await response.json()) as AuditListResponse
  },

  /** Count audit events matching the filter — drives the reader UI's
   * "N new events" badge. Same filter shape as `list`, sans
   * pagination knobs (limit/offset/sortDir are ignored server-side).
   * Caller is expected to poll with `since=lastTopRowTs`; bounding
   * by a `since` predicate keeps the server query index-served and
   * cheap as the collection grows. */
  async countNew(filter: AuditFilter = {}): Promise<{ count: number }> {
    const qs = new URLSearchParams()
    if (filter.actorUserId) qs.set('actorUserId', filter.actorUserId)
    if (filter.actorEmail) qs.set('actorEmail', filter.actorEmail)
    if (filter.actorOrgId) qs.set('actorOrgId', filter.actorOrgId)
    if (filter.action) qs.set('action', filter.action)
    if (filter.targetType) qs.set('targetType', filter.targetType)
    if (filter.targetId) qs.set('targetId', filter.targetId)
    if (filter.since) qs.set('since', filter.since)
    if (filter.until) qs.set('until', filter.until)
    const q = qs.toString()
    const url = q ? `${API_BASE}/admin/audit/count?${q}` : `${API_BASE}/admin/audit/count`
    const response = await apiFetch(url)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error(err.error || 'Failed to count audit events')
    }
    return (await response.json()) as { count: number }
  },
}

// ── RAG inspector (#601) ────────────────────────────────────────────

export type RagAgentKind = 'generator' | 'healer'

export interface RagHitDisplay {
  hintId: string
  rank: number
  scoreLabel: 'confirms' | 'cosine' | 'textScore' | 'rrf'
  scoreValue: number
  category?: string
  pattern?: string
  antipattern?: string
  element?: string
  selector?: string
  sourceRunId?: string
  denseContribution?: { score?: number; rank?: number }
  lexicalContribution?: { score?: number; rank?: number }
}

export interface RagInspectorResponse {
  variant: {
    id: string
    name: string
    applicationId: string
    applicationName?: string
    workspaceId?: string
    workspaceName?: string
  }
  expandedQuery: string
  results: {
    topk: RagHitDisplay[]
    dense: RagHitDisplay[]
    lexical: RagHitDisplay[]
    hybrid: RagHitDisplay[]
  }
}

export interface RagSampleQuery {
  id: string
  variantId: string
  agentPhase: RagAgentKind
  queryText: string
  runId: string
  createdAt: string
  ragHitCount: number
  topkHitCount: number
}

export interface RagConfigResponse {
  /** Active `HINT_RETRIEVAL_MODE` — what this install's runs use today. */
  mode: 'topk' | 'rag' | 'dual'
  /** Active `HINT_RETRIEVER` — which retriever this install uses when
   * RAG runs. Note: `mode='topk'` means RAG never runs at all, so
   * this value is "configured but inert" in that case. */
  retriever: 'dense' | 'lexical' | 'hybrid' | 'pgvector'
}

export const adminRagAPI = {
  /** Resolve the live `HINT_RETRIEVAL_MODE` + `HINT_RETRIEVER` so the
   * inspector page can show a status chip instead of hardcoded copy
   * that rots when env values rotate (#607). `cache: 'no-store'` so
   * the browser never serves a stale cached response — the whole
   * point of this endpoint is to defeat staleness, and the server
   * sets `Cache-Control: no-store` on its side too (belt + suspenders). */
  async getConfig(): Promise<RagConfigResponse> {
    const res = await apiFetch(`${API_BASE}/admin/rag/config`, { cache: 'no-store' })
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string }
      throw new Error(err.error || `Failed to load RAG config (${res.status})`)
    }
    return (await res.json()) as RagConfigResponse
  },

  async query(input: {
    variantId: string
    agentKind: RagAgentKind
    queryText: string
    k?: number
  }): Promise<RagInspectorResponse> {
    const res = await apiFetch(`${API_BASE}/admin/rag/query`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string }
      throw new Error(err.error || `Failed to run inspector query (${res.status})`)
    }
    return (await res.json()) as RagInspectorResponse
  },

  async sampleQueries(opts: {
    variantId?: string
    agentPhase?: RagAgentKind
    limit?: number
  } = {}): Promise<RagSampleQuery[]> {
    const params = new URLSearchParams()
    if (opts.variantId) params.set('variantId', opts.variantId)
    if (opts.agentPhase) params.set('agentPhase', opts.agentPhase)
    if (opts.limit !== undefined) params.set('limit', String(opts.limit))
    const qs = params.toString()
    const res = await apiFetch(`${API_BASE}/admin/rag/sample-queries${qs ? '?' + qs : ''}`)
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string }
      throw new Error(err.error || `Failed to load sample queries (${res.status})`)
    }
    const body = (await res.json()) as { samples: RagSampleQuery[] }
    return body.samples
  },
}

// ──────────────────────────────────────────────────────────────────────
// triggerRulesAPI — TriggerRule CRUD (#1134, Slice 1 of meta #1132)
//
// Provider-agnostic: the `provider` field on the body selects the
// sub-schema. Today only 'github' is accepted by the backend. When
// GitLab support lands the same endpoints accept provider: 'gitlab'.
//
// Three list views:
//   listForRepo       → repo-centric (inside GitHubIntegrationBlock; ADR 0018 Q3)
//   listForTemplate   → single-template-centric (in ReplayTemplateDialog; ADR 0018 Q3)
//   listForTemplates  → multi-template fan-out (variant Automation page, #1155);
//                       takes an array of templateIds because the backend
//                       has no ?variantId= filter today.
// ──────────────────────────────────────────────────────────────────────

import type { TriggerRule, TriggerRuleProvider } from '@/types'

export type { TriggerRule, TriggerRuleProvider }

export interface CreateTriggerRuleInput {
  provider: TriggerRuleProvider
  integrationId: string
  repoIdentifier: string
  templateId: string
  /** GitHub event type — locked to `'deployment_status'` post-#1173.
   * The seam is preserved so a future second event type can land as
   * an additive union edit; today only the single literal is valid. */
  eventType: 'deployment_status'
  /** Optional deployment-environment filter (#1173, meta #1172).
   * Empty / absent = match any environment. */
  environment?: string
}

export interface UpdateTriggerRuleInput {
  repoIdentifier?: string
  templateId?: string
  /** Per-row patch — `null` / `''` clears the filter to match any. */
  environment?: string | null
  eventType?: 'deployment_status'
}

export const triggerRulesAPI = {
  /**
   * Repo-centric view: list rules for a specific (provider, repoIdentifier).
   * Used by TriggerRulesForRepo inside the GitHub provider block.
   */
  async listForRepo(
    provider: TriggerRuleProvider,
    repoIdentifier: string,
  ): Promise<TriggerRule[]> {
    const params = new URLSearchParams({ provider, repoIdentifier })
    const response = await apiFetch(`${ws()}/trigger-rules?${params}`)
    if (!response.ok) throw new Error('Failed to fetch trigger rules for repo')
    return response.json()
  },

  /**
   * Template-centric view: list rules pointing at a given templateId.
   * Used by the Triggers section in ReplayTemplateDialog.
   */
  async listForTemplate(templateId: string): Promise<TriggerRule[]> {
    const response = await apiFetch(`${ws()}/trigger-rules?templateId=${encodeURIComponent(templateId)}`)
    if (!response.ok) throw new Error('Failed to fetch trigger rules for template')
    return response.json()
  },

  /**
   * Template-set view: list rules pointing at any of the given templates.
   *
   * The helper name takes its noun from what the caller passes in
   * (template IDs), NOT from what the caller represents conceptually
   * (the variant). Earlier this was named `listForVariant(templateIds)`
   * to mirror `runTemplatesAPI.listForVariant(variantId)`, but the
   * argument-type mismatch was a footgun — Copilot caught it on #1159.
   * Variant-centric callers (the Automation page, the dashboard summary
   * card) build their template-id list from `runTemplatesAPI.listForVariant`
   * first, then hand it here.
   *
   * Implemented client-side as fan-out across the templates — the
   * backend's `/trigger-rules` endpoint takes `?templateId=`, not
   * `?variantId=`. A variant typically owns a handful of templates, so
   * the request count is small. If the fan-out cost ever becomes
   * relevant the backend can grow a `?variantId=` filter and this helper
   * collapses to a single call (and the rename can be revisited).
   *
   * Duplicate-safe: a rule can point at only one template, so a
   * Set-based de-dup isn't needed.
   */
  async listForTemplates(templateIds: string[]): Promise<TriggerRule[]> {
    if (templateIds.length === 0) return []
    const rulesPerTemplate = await Promise.all(
      templateIds.map((id) => this.listForTemplate(id)),
    )
    return rulesPerTemplate.flat()
  },

  async create(input: CreateTriggerRuleInput): Promise<TriggerRule> {
    const response = await apiFetch(`${ws()}/trigger-rules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error((err as { error?: string }).error || 'Failed to create trigger rule')
    }
    return response.json()
  },

  async update(id: string, patch: UpdateTriggerRuleInput): Promise<TriggerRule> {
    const response = await apiFetch(`${ws()}/trigger-rules/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error((err as { error?: string }).error || 'Failed to update trigger rule')
    }
    return response.json()
  },

  async delete(id: string): Promise<void> {
    const response = await apiFetch(`${ws()}/trigger-rules/${id}`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Failed to delete trigger rule')
  },
}

// ── Scheduled Jobs (Global Admin) ─────────────────────────────────────
//
// Admin-tier catalog of code-registered recurring jobs (#1278 / #1280).
// The backend collection is admin-tier (not workspace-scoped); all
// endpoints sit under `/api/admin/scheduled-jobs` and require the
// superadmin gate. Adding a new job is a code change + deploy — the
// admin UI never POSTs to create rows. The catalog appears via the
// boot seeder; operators may pause / resume / disable / retime
// (subject to the row's edit policy) / run now / delete (only when a
// code definition has been removed).

export type ScheduledJobStatus = 'active' | 'paused' | 'disabled'
export type ScheduledJobEditPolicy = 'schedule-editable' | 'code-owned-schedule'
export type ScheduledJobFailureAlert = 'silent' | 'email' | 'slack'
export type ScheduledJobLastRunStatus = 'succeeded' | 'failed' | 'timed-out'
export type ScheduledJobRunStatus = 'running' | 'succeeded' | 'failed' | 'timed-out'
export type ScheduledJobRunTrigger = 'scheduled' | 'run-now' | 'boot-claim-release'

export interface ScheduledJobRow {
  jobId: string
  cron: string
  timezone: string
  status: ScheduledJobStatus
  onFailure: ScheduledJobFailureAlert
  maxDurationSec: number
  displayName: string
  description: string
  definitionHash: string
  editPolicy: ScheduledJobEditPolicy
  registered: boolean
  lastRunAt: string | null
  lastRunStatus: ScheduledJobLastRunStatus | null
  nextRunAt: string | null
  runInProgress: boolean
  /** Why the job does not apply to this install, or null when it does.
   * Optional because an older server does not send it. */
  off?: ScheduledJobOff | null
}

/** Why a job does not apply here. Mirrors the server's `JobOff`. */
export interface ScheduledJobOff {
  /** The sentence beside the Off pill. */
  reason: string
  /** What turns it on, for the job's own page. */
  turnsOnWhen?: string
}

export interface ScheduledJobRunRow {
  runId: string
  jobId: string
  trigger: ScheduledJobRunTrigger
  startedAt: string
  completedAt: string | null
  status: ScheduledJobRunStatus
  logExcerpt: string | null
  logRef: string | null
  errorSummary: string | null
  metrics: Record<string, unknown> | null
  rowsTouched: number | null
}

export interface ScheduledJobDetailResponse {
  job: ScheduledJobRow
  recentRuns: ScheduledJobRunRow[]
}

export interface ScheduledJobPatchInput {
  cron?: string
  timezone?: string
  status?: ScheduledJobStatus
  onFailure?: ScheduledJobFailureAlert
}

export const adminScheduledJobsAPI = {
  /** List every catalog row. Returns rows ordered alphabetically by
   * display name. */
  async list(): Promise<{ jobs: ScheduledJobRow[] }> {
    const response = await apiFetch(`${API_BASE}/admin/scheduled-jobs`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error((err as { error?: string }).error || 'Failed to load scheduled jobs')
    }
    return (await response.json()) as { jobs: ScheduledJobRow[] }
  },

  async get(jobId: string): Promise<ScheduledJobDetailResponse> {
    const response = await apiFetch(`${API_BASE}/admin/scheduled-jobs/${encodeURIComponent(jobId)}`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error((err as { error?: string }).error || 'Failed to load scheduled job')
    }
    return (await response.json()) as ScheduledJobDetailResponse
  },

  /** PATCH a row. Server returns the updated row. 403 fires when the
   * row's edit policy is code-owned and the patch attempts to retime;
   * 400 fires for an invalid cron or timezone before any write. */
  async patch(jobId: string, patch: ScheduledJobPatchInput): Promise<ScheduledJobRow> {
    const response = await apiFetch(
      `${API_BASE}/admin/scheduled-jobs/${encodeURIComponent(jobId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error((err as { error?: string }).error || 'Failed to update scheduled job')
    }
    const body = (await response.json()) as { job: ScheduledJobRow }
    return body.job
  },

  /** Trigger an immediate run via the same claim path as the runner's
   * scheduled tick. 409 fires when a run is already in progress. Does
   * NOT recompute next-run time. Returns the new run id. */
  async runNow(jobId: string): Promise<{ runId: string }> {
    const response = await apiFetch(
      `${API_BASE}/admin/scheduled-jobs/${encodeURIComponent(jobId)}/run-now`,
      { method: 'POST' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error((err as { error?: string }).error || 'Failed to start a run')
    }
    return (await response.json()) as { runId: string }
  },

  /** Delete a catalog row. Only succeeds when the row is no longer
   * registered in code; registered rows return 409 ("pause or disable
   * instead"). */
  async delete(jobId: string): Promise<void> {
    const response = await apiFetch(
      `${API_BASE}/admin/scheduled-jobs/${encodeURIComponent(jobId)}`,
      { method: 'DELETE' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error((err as { error?: string }).error || 'Failed to delete scheduled job')
    }
  },
}

// ──────────────────────────────────────────────────────────────────────
// Model verification — admin-tier (#1281). Backed by
// `/api/admin/model-verification/*` which sits behind requireSession +
// requireSuperadmin. Powers the model health on the Admin → Models →
// Assignment page (per-agent status + the embedding row + per-row
// "Check now"), folded in alongside the assignment controls (#1281
// follow-up; was a separate Catalog page).
// ──────────────────────────────────────────────────────────────────────

export type ModelVerificationState =
  | 'working'
  | 'notWorking'
  | 'checking'
  | 'notChecked'

export type ModelVerificationErrorClass =
  | 'auth'
  | 'network'
  | 'modelNotFound'
  | 'quota'
  | 'dimMismatch'
  | 'unknown'

export type ModelVerificationTargetKind = 'agent' | 'embedding'

export interface ModelVerificationStatusRow {
  state: ModelVerificationState
  errorClass: ModelVerificationErrorClass | null
  errorMessage: string | null
  fixMessage: string | null
  latencyMs: number | null
  observedDimensions: number | null
  lastCheckedAt: string | null
  lastSuccessAt: string | null
  /** Provider + model the cached state was recorded against (#1364).
   * The page compares these to the target's current `provider` + `model`
   * — when they differ the operator has changed the selection since
   * the check and the pill downgrades to "Not checked yet." */
  checkedProvider: string | null
  checkedModelId: string | null
}

export interface ModelTargetRow {
  targetId: string
  targetKind: ModelVerificationTargetKind
  displayName: string
  provider: string
  model: string
  modelSpec: string
  apiKeyEnvChain: readonly string[]
  /** Masked preview of the resolved API key (first/last few chars), or
   * `null` when no key in the chain is set. The full secret never
   * reaches the client. */
  apiKeyPreview: string | null
  /** The env var the preview was read from (first set entry in the
   * chain), or `null`. Shown next to the preview. */
  apiKeyEnvUsed: string | null
  baseUrlEnvChain: readonly string[]
  modelEnvChain: readonly string[]
  expectedDimensions?: number
  /** Embedding-only. True when the local bundled model serves this: it needs
   * no key, so the row shows "served locally" and a missing key does not
   * downgrade its verified state. */
  servedLocally?: boolean
  status: ModelVerificationStatusRow | null
}

/** What checking a model before saving it found (#2890), mirroring the
 * server's `CandidateCheck` plus the spec checked. Only a network failure
 * or the check's time limit is `unreachable`; the page saves on `works`
 * and `unreachable`, never on `fails`. */
export type ModelCandidateCheck = { model: string } & (
  | { outcome: 'works' }
  | { outcome: 'unreachable'; errorMessage: string }
  | { outcome: 'fails'; errorClass: ModelVerificationErrorClass; errorMessage: string; fixMessage: string }
)

export interface ModelVerificationListResponse {
  targets: ModelTargetRow[]
  /** True when any row's status state is `notWorking`. Drives the
   * Models sub-nav red-dot badge. */
  anyNotWorking: boolean
}

/** OttoCloud's answer for one agent (#2891), mirroring the server's
 * `AgentRecommendation`. `seeded` is a pick from OttoCloud's lab-tested
 * list; `community` comes from installs that share scores and carries how
 * many runs back it. */
export type AgentRecommendation =
  | { status: 'recommended'; model: string; basis: 'seeded' | 'community'; runs: number }
  /** OttoCloud answered with nothing usable for this agent. */
  | { status: 'none' }
  /** The request for this agent's supplier failed. */
  | { status: 'unavailable' }

/** GET /api/admin/model-recommendations, asked when an admin presses
 * "Show recommendations". */
export type ModelRecommendationsResponse =
  /** OttoCloud is not set up on this install; nothing was asked. */
  | { kind: 'notSetUp' }
  /** The organization has no OttoCloud account linked. */
  | { kind: 'noAccount' }
  /** OttoCloud did not answer. */
  | { kind: 'unreachable' }
  | { kind: 'answered'; recommendations: Partial<Record<string, AgentRecommendation>> }

export const adminModelRecommendationsAPI = {
  /** `orgId` names the organization to ask for on our hosted install,
   * where each has its own OttoCloud account; other installs ignore it. */
  async get(orgId?: string): Promise<ModelRecommendationsResponse> {
    const query = orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''
    const response = await apiFetch(`${API_BASE}/admin/model-recommendations${query}`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error((err as { error?: string }).error || 'Failed to load recommended models')
    }
    return response.json()
  },
}

export const adminModelVerificationAPI = {
  async list(): Promise<ModelVerificationListResponse> {
    const response = await apiFetch(`${API_BASE}/admin/model-verification`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error((err as { error?: string }).error || 'Failed to load model targets')
    }
    return response.json()
  },

  async verify(targetId: string): Promise<{ target: ModelTargetRow }> {
    const response = await apiFetch(
      `${API_BASE}/admin/model-verification/${encodeURIComponent(targetId)}/verify`,
      { method: 'POST' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error((err as { error?: string }).error || 'Failed to verify model')
    }
    return response.json()
  },

  /** Check a model for an agent BEFORE it is saved (#2890). `model` is a
   * `supplier:model` spec, or null for the default the agent would fall
   * back to without one. Records nothing on the server. */
  async check(agent: AdminAgentName, model: string | null): Promise<ModelCandidateCheck> {
    const response = await apiFetch(`${API_BASE}/admin/model-verification/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agent, model }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error((err as { error?: string }).error || 'Failed to check the model')
    }
    return response.json()
  },

  /** Kick the scheduled "verify all" job off-cadence. Hits the slice-1
   * run-now path so both the catalog banner and a future re-tick from
   * the Scheduled Jobs UI share the exact same code. Returns 202 with
   * a run id on success; the caller polls `list()` to see fresh
   * statuses. */
  async runAllNow(): Promise<void> {
    const response = await apiFetch(
      `${API_BASE}/admin/scheduled-jobs/verifyAllModels/run-now`,
      { method: 'POST' },
    )
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `Failed (${response.status})` }))
      throw new Error((err as { error?: string }).error || 'Failed to start the check')
    }
  },
}
